import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useTheme } from '../../contexts/ThemeContext';
import RichTextEditor from '../../components/base/RichTextEditor';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';

interface Client {
  id: string;
  name: string;
  first_name?: string;
  last_name?: string;
  email: string;
  phone: string;
  state: string;
  purchase_price: string;
  broker: string;
  last_contact: string;
  client_state: string;
  date_created: string;
  user_id: string;
  loan_type: string;
  lead_source: string;
  lender_name: string;
  loan_amount?: string;
  client_state_updated?: string;
  realtor_id?: string;
  follow_up_date?: string;
  notes?: string;
  closing_date?: string;
  interest_rate?: string;
  lender_loan_number?: string;
  arive_loan_number?: string;
  broker_comp?: string;
  comp?: string;
}

interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  body: string;
}

interface TextTemplate {
  id: string;
  name: string;
  body: string;
}

interface Realtor {
  id: string;
  name: string;
  email: string;
  phone: string;
  state: string;
}

interface ClientStateOption {
  state_name: string;
  display_order: number;
}

interface CompRate {
  id: string;
  broker: string;
  comp_percentage: number;
  personal_comp_percentage: number;
  full_broker_name: string;
  email_address?: string;
}

interface LoanType {
  id: string;
  loan_type: string;
  display_order: number;
  intake_mapping: string;
}

interface QuickFilterConfig {
  id: string;
  filter_name: string;
  client_states: string[];
  display_order?: number;
}

interface SentTemplate {
  id: string;
  template_id: string;
  template_name: string;
  template_type: 'email' | 'text';
  sent_at: string;
}

interface ClientNote {
  id: string;
  note: string;
  created_at: string;
  updated_at: string;
}

interface DiscoveryCall {
  id: string;
  client_id: string;
  current_housing: string;
  purchase_timeline: string;
  preapproval_status: string;
  budget_down_payment: string;
  credit_score_range: string;
  employment_status: string;
  additional_notes: string;
  call_date: string;
  created_at: string;
  mortgagees?: MortgageeData[];
  shopping_reason?: string;
  assets?: string;
  liabilities?: string;
}

interface MortgageeData {
  name: string;
  employments: {
    employment_status: string;
    current_income: string;
    employer_job_description: string;
  }[];
  credit_score_range: string;
}

interface DiscoveryCallField {
  id: string;
  field_name: string;
  field_label: string;
  field_type: 'text' | 'select' | 'textarea' | 'date';
  dropdown_options: string[] | null;
  display_order: number;
  is_required: boolean;
}

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

// Add interface for user profile data
interface UserProfileData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  nmls: string;
}

const getBrokerByState = (state: string): string => {
  const stateUpper = state.toUpperCase();
  
  // TM: Texas, California
  if (['TEXAS', 'CALIFORNIA', 'TX', 'CA'].includes(stateUpper)) {
    return 'TM';
  }
  
  // NS: All other states
  return 'NS';
};

const getBrokerEmail = (broker: string, compRates: CompRate[]): string => {
  const rate = compRates.find(r => r.broker === broker);
  if (rate && rate.email_address) {
    return rate.email_address;
  }
  // Fallback to default emails if not found in comp_rates
  if (broker === 'NS') {
    return 'heena@nextstep-lending.com';
  }
  if (broker === 'TM') {
    return 'heena@trademarkfinancing.com';
  }
  return 'heena@example.com';
};

const getBrokerFullName = (broker: string, compRates: CompRate[]): string => {
  const rate = compRates.find(r => r.broker === broker);
  return rate ? rate.full_broker_name : broker;
};

const parsePurchasePrice = (priceString: string): number => {
  const cleaned = priceString.replace(/[$,]/g, '');
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
};

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const replaceTemplateVariables = (text: string, client: Client, compRates: CompRate[], userProfile?: UserProfileData): string => {
  const firstName = client.first_name || client.name.split(' ')[0] || '';
  const lastName = client.last_name || client.name.split(' ').slice(1).join(' ') || '';
  
  // Calculate expiration date (90 days from today)
  const today = new Date();
  const expirationDate = new Date(today);
  expirationDate.setDate(expirationDate.getDate() + 90);
  const expirationDateFormatted = expirationDate.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  // Use logged-in user's profile for broker variables if available
  const brokerFullName = userProfile
    ? `${userProfile.firstName} ${userProfile.lastName}`.trim()
    : getBrokerFullName(client.broker, compRates);
  const brokerEmail = userProfile?.email || getBrokerEmail(client.broker, compRates);
  const brokerFirstName = userProfile?.firstName || '';
  const brokerLastName = userProfile?.lastName || '';
  const brokerPhone = userProfile ? formatPhoneNumber(userProfile.phone) : '';
  const brokerNMLS = userProfile?.nmls || '';
  
  return text
    .replace(/\{clientName\}/g, client.name)
    .replace(/\{firstName\}/g, firstName)
    .replace(/\{lastName\}/g, lastName)
    .replace(/\{state\}/g, client.state)
    .replace(/\{purchasePrice\}/g, client.purchase_price)
    .replace(/\{loanAmount\}/g, client.loan_amount || '')
    .replace(/\{brokerName\}/g, brokerFullName)
    .replace(/\{brokerEmail\}/g, brokerEmail)
    .replace(/\{brokerFirstName\}/g, brokerFirstName)
    .replace(/\{brokerLastName\}/g, brokerLastName)
    .replace(/\{brokerPhone\}/g, brokerPhone)
    .replace(/\{brokerNMLS\}/g, brokerNMLS)
    .replace(/\{expirationDate\}/g, expirationDateFormatted);
};

const formatPhoneNumber = (phone: string): string => {
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)})-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  return phone;
};

const formatPurchasePrice = (price: string): string => {
  const cleaned = price.replace(/[$,]/g, '');
  const parsed = parseFloat(cleaned);
  if (isNaN(parsed)) return price;
  return `$${parsed.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
};

const getStateAbbreviation = (state: string): string => {
  const stateMap: Record<string, string> = {
    'ALABAMA': 'AL', 'ALASKA': 'AK', 'ARIZONA': 'AZ', 'ARKANSAS': 'AR', 'CALIFORNIA': 'CA',
    'COLORADO': 'CO', 'CONNECTICUT': 'CT', 'DELAWARE': 'DE', 'FLORIDA': 'FL', 'GEORGIA': 'GA',
    'HAWAII': 'HI', 'IDAHO': 'ID', 'ILLINOIS': 'IL', 'INDIANA': 'IN', 'IOWA': 'IA',
    'KANSAS': 'KS', 'KENTUCKY': 'KY', 'LOUISIANA': 'LA', 'MAINE': 'ME', 'MARYLAND': 'MD',
    'MASSACHUSETTS': 'MA', 'MICHIGAN': 'MI', 'MINNESOTA': 'MN', 'MISSISSIPPI': 'MS', 'MISSOURI': 'MO',
    'MONTANA': 'MT', 'NEBRASKA': 'NE', 'NEVADA': 'NV', 'NEW HAMPSHIRE': 'NH', 'NEW JERSEY': 'NJ',
    'NEW MEXICO': 'NM', 'NEW YORK': 'NY', 'NORTH CAROLINA': 'NC', 'NORTH DAKOTA': 'ND', 'OHIO': 'OH',
    'OKLAHOMA': 'OK', 'OREGON': 'OR', 'PENNSYLVANIA': 'PA', 'RHODE ISLAND': 'RI', 'SOUTH CAROLINA': 'SC',
    'SOUTH DAKOTA': 'SD', 'TENNESSEE': 'TN', 'TEXAS': 'TX', 'UTAH': 'UT', 'VERMONT': 'VT',
    'VIRGINIA': 'VA', 'WASHINGTON': 'WA', 'WEST VIRGINIA': 'WV', 'WISCONSIN': 'WI', 'WYOMING': 'WY'
  };
  
  const upperState = state.toUpperCase().trim();
  if (upperState.length === 2) return upperState;
  return stateMap[upperState] || state;
};

const formatDateTime = (dateString: string): string => {
  if (!dateString || dateString === '-') return '-';
  
  try {
    // Check if it's already in the MM/DD/YYYY, HH:MM AM/PM format
    if (dateString.includes(',') && dateString.includes('/')) {
      return dateString;
    }
    
    // Parse ISO string and convert to local time
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    const hoursStr = String(hours).padStart(2, '0');
    
    return `${month}/${day}/${year}, ${hoursStr}:${minutes} ${ampm}`;
  } catch (error) {
    return dateString;
  }
};

const formatDate = (dateString: string): string => {
  if (!dateString || dateString === '-') return '-';
  
  try {
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    
    return `${month}/${day}/${year}`;
  } catch (error) {
    return dateString;
  }
};

const formatDateTimeMultiLine = (dateString: string): { date: string; time: string } => {
  if (!dateString || dateString === '-' || dateString === 'No Contact Yet' || dateString === 'None' || dateString === 'Never') return { date: 'None', time: '' };
  
  try {
    // Parse ISO string and convert to local time
    const date = new Date(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return { date: 'None', time: '' };
    }
    
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    const hoursStr = String(hours).padStart(2, '0');
    
    return {
      date: `${month}/${day}/${year}`,
      time: `${hoursStr}:${minutes} ${ampm}`
    };
  } catch (error) {
    return { date: 'None', time: '' };
  }
};

const getCurrentTimestamp = (): string => {
  const now = new Date();
  
  // Return ISO string which will be properly converted by formatDateTime
  return now.toISOString();
};

const isFollowUpDue = (followUpDate: string): boolean => {
  if (!followUpDate) return false;
  
  const now = new Date();
  
  // Parse the follow-up date manually to avoid timezone issues
  if (followUpDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
    // Split the date string and create date using local timezone
    const [year, month, day] = followUpDate.split('-').map(Number);
    const followUp = new Date(year, month - 1, day); // month is 0-indexed
    followUp.setHours(6, 0, 0, 0); // Set to 6:00 AM on the follow-up date
    
    // Only show alert if current time is past 6:00 AM on the follow-up date
    return now.getTime() >= followUp.getTime();
  }
  
  // Fallback for other date formats
  const followUp = new Date(followUpDate);
  followUp.setHours(6, 0, 0, 0);
  
  return now.getTime() >= followUp.getTime();
};

const generatePreApprovalPDF = async (client: Client, compRates: CompRate[], userProfileData?: UserProfileData) => {
  // Validation checks
  const validationErrors: string[] = [];
  
  // Check if purchase price is not "-"
  if (!client.purchase_price || client.purchase_price === '-' || client.purchase_price.trim() === '') {
    validationErrors.push('Purchase Price is missing or invalid');
  }
  
  // Check if loan amount is not "-"
  if (!client.loan_amount || client.loan_amount === '-' || client.loan_amount.trim() === '') {
    validationErrors.push('Loan Amount is missing or invalid');
  }
  
  // Check if loan type is not "-"
  if (!client.loan_type || client.loan_type === '-' || client.loan_type.trim() === '') {
    validationErrors.push('Loan Type is missing or invalid');
  }
  
  // Check if broker abbreviation is "NS"
  if (client.broker !== 'NS') {
    validationErrors.push('The Broker must be Next Step Lending to generate a pre-approval');
  }
  
  // Check if purchase price is higher than loan amount (only if both are valid)
  if (client.purchase_price && client.purchase_price !== '-' && 
      client.loan_amount && client.loan_amount !== '-') {
    const purchasePrice = parsePurchasePrice(client.purchase_price);
    const loanAmount = parsePurchasePrice(client.loan_amount);
    
    if (loanAmount >= purchasePrice) {
      validationErrors.push('Loan Amount must be less than Purchase Price');
    }
  }
  
  // If there are validation errors, show them to the user and stop
  if (validationErrors.length > 0) {
    const errorMessage = 'Cannot generate pre-approval PDF. Please fix the following issues:\n\n' + 
                        validationErrors.map((error, index) => `${index + 1}. ${error}`).join('\n');
    alert(errorMessage);
    return;
  }

  // Load the pre-approval template from the database
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return;

  const { data: templateData, error } = await supabase
    .from('pre_approval_templates')
    .select('template_content')
    .eq('user_id', session.user.id)
    .single();

  if (error && error.code !== 'PGRST116') {
    console.error('Error loading pre-approval template:', error);
    alert('Failed to load pre-approval template');
    return;
  }

  // Use the template from database or default template
  let templateContent = templateData?.template_content || `Dear {clientName},

I am pleased to inform you that you have been pre-approved for a mortgage loan with the following terms:

PRE-APPROVAL DETAILS:
• Loan Amount: {loanAmount}
• Purchase Price: {purchasePrice}
• Property State: {state}
• Loan Type: {loanType}

PRE-APPROVAL CONDITIONS:
This pre-approval is subject to the following conditions:
• Verification of employment and income
• Satisfactory property appraisal
• Clear title report
• No adverse changes to credit or financial status
• Final underwriting approval

VALIDITY PERIOD:
This pre-approval letter is valid for 90 days from the date of issuance.

NEXT STEPS:
With this pre-approval letter, you can confidently make offers on properties within your approved price range. Please contact me when you find a property you'd like to purchase, and we'll move forward with the full loan application process.

If you have any questions or need any clarification, please don't hesitate to reach out.

Best regards,
{brokerName}
{brokerEmail}`;

  const { jsPDF } = await import('jspdf');
  const doc = new jsPDF();
  
  // Set up the document
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;
  const contentWidth = pageWidth - (margin * 2);
  let yPosition = 20;

  // Add logo image centered at the top
  try {
    const logoUrl = 'https://static.readdy.ai/image/36f8e8d5966a64a28c9df71ae3e80f58/55527bd7ed743b25fa34d4611985e821.png';
    
    // Load the image
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
      img.src = logoUrl;
    });
    
    // Calculate dimensions to maintain aspect ratio
    const maxLogoWidth = 120; // Maximum width for the logo
    const maxLogoHeight = 40; // Maximum height for the logo
    
    let logoWidth = img.width;
    let logoHeight = img.height;
    
    // Scale down if needed
    if (logoWidth > maxLogoWidth) {
      const ratio = maxLogoWidth / logoWidth;
      logoWidth = maxLogoWidth;
      logoHeight = logoHeight * ratio;
    }
    
    if (logoHeight > maxLogoHeight) {
      const ratio = maxLogoHeight / logoHeight;
      logoHeight = maxLogoHeight;
      logoWidth = logoWidth * ratio;
    }
    
    // Center the logo horizontally
    const logoX = (pageWidth - logoWidth) / 2;
    
    // Add the logo to the PDF
    doc.addImage(img, 'PNG', logoX, yPosition, logoWidth, logoHeight);
    
    // Update yPosition to be below the logo
    yPosition += logoHeight + 15; // Add 15 units of spacing after logo
  } catch (error) {
    console.error('Error loading logo:', error);
    // Continue without logo if there's an error
  }

  // Add a line separator
  doc.setLineWidth(0.5);
  doc.line(margin, yPosition, pageWidth - margin, yPosition);
  yPosition += 10;

  // Get client name parts
  const firstName = client.first_name || client.name.split(' ')[0] || '';
  const lastName = client.last_name || client.name.split(' ').slice(1).join(' ') || '';
  
  // Get user profile information - prefer userProfileData param, then fall back to session metadata
  const metadata = session.user.user_metadata;
  const brokerFirstName = userProfileData?.firstName || metadata.first_name || '';
  const brokerLastName = userProfileData?.lastName || metadata.last_name || '';
  const brokerPhone = userProfileData?.phone || metadata.phone || '';
  const brokerNMLS = userProfileData?.nmls || metadata.nmls || '';
  const brokerEmail = userProfileData?.email || session.user.email || getBrokerEmail(client.broker, compRates);
  const brokerFullName = userProfileData
    ? `${userProfileData.firstName} ${userProfileData.lastName}`.trim()
    : (brokerFirstName && brokerLastName ? `${brokerFirstName} ${brokerLastName}`.trim() : getBrokerFullName(client.broker, compRates));
  
  // Format broker phone number
  const formattedBrokerPhone = brokerPhone ? formatPhoneNumber(brokerPhone) : '';
  
  // Calculate today's date
  const today = new Date();
  const todaysDateFormatted = today.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
  
  // Calculate expiration date (90 days from today)
  const expirationDate = new Date(today);
  expirationDate.setDate(expirationDate.getDate() + 90);
  const expirationDateFormatted = expirationDate.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  // Format loan amount and purchase price
  const formattedLoanAmount = client.loan_amount ? formatCurrency(parsePurchasePrice(client.loan_amount)) : 'To be determined';
  const formattedPurchasePrice = formatCurrency(parsePurchasePrice(client.purchase_price));

  // Replace template variables with actual client data
  let letterContent = templateContent
    .replace(/\{clientName\}/g, client.name)
    .replace(/\{firstName\}/g, firstName)
    .replace(/\{lastName\}/g, lastName)
    .replace(/\{loanAmount\}/g, formattedLoanAmount)
    .replace(/\{purchasePrice\}/g, formattedPurchasePrice)
    .replace(/\{state\}/g, client.state)
    .replace(/\{loanType\}/g, client.loan_type || 'Conventional')
    .replace(/\{brokerName\}/g, brokerFullName)
    .replace(/\{brokerEmail\}/g, brokerEmail)
    .replace(/\{brokerFirstName\}/g, brokerFirstName)
    .replace(/\{brokerLastName\}/g, brokerLastName)
    .replace(/\{brokerPhone\}/g, formattedBrokerPhone)
    .replace(/\{brokerNMLS\}/g, brokerNMLS)
    .replace(/\{todaysDate\}/g, todaysDateFormatted)
    .replace(/\{expirationDate\}/g, expirationDateFormatted);

  // Split into paragraphs
  const paragraphs = letterContent.split('\n\n');
  
  doc.setFontSize(11);
  
  paragraphs.forEach((paragraph) => {
    if (paragraph.trim()) {
      // Check if it's a heading (all caps or ends with colon)
      if (paragraph.trim().match(/^[A-Z\s:]+:$/) || paragraph.trim() === paragraph.trim().toUpperCase()) {
        doc.setFont('helvetica', 'bold');
        yPosition += 5;
      } else {
        doc.setFont('helvetica', 'normal');
      }
      
      // Split long paragraphs into lines
      const lines = doc.splitTextToSize(paragraph.trim(), contentWidth);
      
      // Check if we need a new page
      if (yPosition + (lines.length * 7) > doc.internal.pageSize.getHeight() - margin) {
        doc.addPage();
        yPosition = margin;
      }
      
      lines.forEach((line: string) => {
        doc.text(line, margin, yPosition);
        yPosition += 7;
      });
      
      yPosition += 3;
    }
  });

  // Add footer
  yPosition += 10;
  doc.setLineWidth(0.5);
  doc.line(margin, yPosition, pageWidth - margin, yPosition);
  yPosition += 7;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'italic');
  doc.text('This pre-approval letter is issued subject to final underwriting approval.', pageWidth / 2, yPosition, { align: 'center' });

  // Save the PDF
  const fileName = `Pre-Approval-${client.name.replace(/\s+/g, '-')}-${todaysDateFormatted.replace(/\s+/g, '-')}.pdf`;
  doc.save(fileName);
};

const calculateComp = (loanAmount: string, broker: string): number => {
  const amount = parsePurchasePrice(loanAmount);
  const personalCompPercentage = getPersonalCompPercentageOfTotal(broker);
  return (amount * personalCompPercentage) / 100;
};

const getPersonalCompPercentageOfTotal = (broker: string): number => {
  // Default percentages based on broker
  if (broker === 'NS') {
    return 0.75; // 75% for NS
  } else if (broker === 'TM') {
    return 0.70; // 70% for TM (can be 85% depending on lead source, but using base rate here)
  }
  return 0.75; // Default to 75%
};

const calculateCompFromBrokerComp = (brokerComp: string, broker: string, leadSource: string): number => {
  const brokerCompAmount = parsePurchasePrice(brokerComp);
  
  if (broker === 'NS') {
    return Math.round(brokerCompAmount * 0.75);
  } else if (broker === 'TM') {
    if (leadSource === 'TM Leads') {
      return Math.round(brokerCompAmount * 0.70);
    } else {
      return Math.round(brokerCompAmount * 0.85);
    }
  }
  
  return brokerCompAmount;
};

export default function CRMPage() {
  const navigate = useNavigate();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const [clients, setClients] = useState<Client[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [filterClientState, setFilterClientState] = useState<string>('All');
  const [filterState, setFilterState] = useState<string>('All');
  const [filterLoanType, setFilterLoanType] = useState<string>('All');
  const [filterLeadSource, setFilterLeadSource] = useState<string>('All');
  const [searchQuery, setSearchQuery] = useState<string>('');
  
  // Add user profile state
  const [userFullName, setUserFullName] = useState<string>('');
  const [userProfile, setUserProfile] = useState<UserProfileData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    nmls: ''
  });
  
  // Add batch selection state
  const [selectedClients, setSelectedClients] = useState<Set<string>>(new Set());
  const [showBatchEmailModal, setShowBatchEmailModal] = useState(false);
  const [showBatchTextModal, setShowBatchTextModal] = useState(false);
  const [showBatchStateModal, setShowBatchStateModal] = useState(false);
  const [batchSelectedState, setBatchSelectedState] = useState<string>('');

  // Add sorting state
  const [sortField, setSortField] = useState<keyof Client | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Add template modal states
  const [showEmailTemplateModal, setShowEmailTemplateModal] = useState(false);
  const [showTextTemplateModal, setShowTextTemplateModal] = useState(false);
  const [selectedClientForTemplate, setSelectedClientForTemplate] = useState<Client | null>(null);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [clientToClose, setClientToClose] = useState<{ id: string; name: string } | null>(null);
  const [closingDate, setClosingDate] = useState<string>('');
  const [interestRate, setInterestRate] = useState<string>('');
  const [lenderLoanNumber, setLenderLoanNumber] = useState<string>('');
  const [ariveLoanNumber, setAriveLoanNumber] = useState<string>('');
  const [closingLenderName, setClosingLenderName] = useState<string>('');
  const [closingLeadSource, setClosingLeadSource] = useState<string>('');
  const [closingPurchasePrice, setClosingPurchasePrice] = useState<string>('');
  const [closingLoanAmount, setClosingLoanAmount] = useState<string>('');
  const [closingCustomLender, setClosingCustomLender] = useState<string>('');
  const [closingCustomLeadSource, setClosingCustomLeadSource] = useState<string>('');
  const [closingBrokerComp, setClosingBrokerComp] = useState<string>('');

  const [showLenderModal, setShowLenderModal] = useState(false);
  const [clientForLender, setClientForLender] = useState<{ id: string; name: string } | null>(null);
  const [selectedLender, setSelectedLender] = useState<string>('');
  const [customLender, setCustomLender] = useState<string>('');
  const [lenderOptions, setLenderOptions] = useState<string[]>([]);

  const [showStatePromptModal, setShowStatePromptModal] = useState(false);
  const [clientForStatePrompt, setClientForStatePrompt] = useState<{ id: string; name: string; newState: string } | null>(null);
  const [statePromptValue, setStatePromptValue] = useState<string>('');
  const [loanAmountPromptValue, setLoanAmountPromptValue] = useState<string>('');
  const [purchasePricePromptValue, setPurchasePricePromptValue] = useState<string>('');
  const [loanTypePromptValue, setLoanTypePromptValue] = useState<string>('');
  const [leadSourcePromptValue, setLeadSourcePromptValue] = useState<string>('');

  const [showDeadDealModal, setShowDeadDealModal] = useState(false);
  const [clientForDeadDeal, setClientForDeadDeal] = useState<{ id: string; name: string } | null>(null);
  const [deadDealNotes, setDeadDealNotes] = useState<string>('');

  const [showOnHoldModal, setShowOnHoldModal] = useState(false);
  const [clientForOnHold, setClientForOnHold] = useState<{ id: string; name: string } | null>(null);
  const [onHoldFollowUpDate, setOnHoldFollowUpDate] = useState<string>('');
  const [onHoldNotes, setOnHoldNotes] = useState<string>('');
  const [onHoldTimeAmount, setOnHoldTimeAmount] = useState<string>('');
  const [onHoldTimeUnit, setOnHoldTimeUnit] = useState<string>('Week(s)');

  const [editingLenderForClient, setEditingLenderForClient] = useState<string>('');
  const [editingCustomLender, setEditingCustomLender] = useState<string>('');

  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>([]);
  const [textTemplates, setTextTemplates] = useState<TextTemplate[]>([]);

  const [clientStateOptions, setClientStateOptions] = useState<ClientStateOption[]>([]);
  const [realtors, setRealtors] = useState<Realtor[]>([]);
  const [compRates, setCompRates] = useState<CompRate[]>([]);
  const [loanTypes, setLoanTypes] = useState<LoanType[]>([]);

  const [showUnderwritingLenderModal, setShowUnderwritingLenderModal] = useState(false);
  const [clientForUnderwritingLender, setClientForUnderwritingLender] = useState<{ id: string; name: string; newState: string } | null>(null);
  const [underwritingSelectedLender, setUnderwritingSelectedLender] = useState<string>('');
  const [underwritingCustomLender, setUnderwritingCustomLender] = useState<string>('');

  const [showRealtorModal, setShowRealtorModal] = useState(false);
  const [customRealtorName, setCustomRealtorName] = useState<string>('');

  const [showLeadSourceRealtorModal, setShowLeadSourceRealtorModal] = useState(false);
  const [customLeadSourceRealtorName, setCustomLeadSourceRealtorName] = useState<string>('');
  const [editingClientIdForLeadSource, setEditingClientIdForLeadSource] = useState<string>('');
  const [addToRealtorsTable, setAddToRealtorsTable] = useState<boolean>(false);

  // Add edit dialog state
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [editDialogLeadSource, setEditDialogLeadSource] = useState<string>('');
  const [editDialogCustomLeadSource, setEditDialogCustomLeadSource] = useState<string>('');
  const [editDialogStateSearch, setEditDialogStateSearch] = useState<string>('');
  const [editDialogLender, setEditDialogLender] = useState<string>('');
  const [editDialogCustomLender, setEditDialogCustomLender] = useState<string>('');

  const [showEditLenderModal, setShowEditLenderModal] = useState(false);
  const [customEditLenderName, setCustomEditLenderName] = useState<string>('');
  const [addEditLenderToTable, setAddEditLenderToTable] = useState<boolean>(false);

  // Add client info dialog state
  const [showClientInfoDialog, setShowClientInfoDialog] = useState(false);
  const [clientInfoDialogClient, setClientInfoDialogClient] = useState<Client | null>(null);
  const [editingClientInfoEmail, setEditingClientInfoEmail] = useState(false);
  const [editingClientInfoPhone, setEditingClientInfoPhone] = useState(false);
  const [editingClientInfoNotes, setEditingClientInfoNotes] = useState(false);
  const [clientInfoEmail, setClientInfoEmail] = useState<string>('');
  const [clientInfoPhone, setClientInfoPhone] = useState<string>('');
  const [clientInfoNotes, setClientInfoNotes] = useState<string>('');
  const [sentTemplates, setSentTemplates] = useState<SentTemplate[]>([]);
  
  // Notes management state
  const [clientNotes, setClientNotes] = useState<ClientNote[]>([]);
  const [newNoteText, setNewNoteText] = useState<string>('');
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingNoteText, setEditingNoteText] = useState<string>('');

  // Discovery call state
  const [showDiscoveryCallDialog, setShowDiscoveryCallDialog] = useState(false);
  const [selectedClientForDiscovery, setSelectedClientForDiscovery] = useState<Client | null>(null);
  const [discoveryCallData, setDiscoveryCallData] = useState({
    current_housing: '',
    purchase_timeline: '',
    preapproval_status: '',
    budget_down_payment: '',
    credit_score_range: '',
    employment_status: '',
    additional_notes: '',
    call_date: new Date().toISOString().split('T')[0],
    shopping_reason: '',
    assets: '',
    liabilities: ''
  });
  const [clientDiscoveryCalls, setClientDiscoveryCalls] = useState<DiscoveryCall[]>([]);

  // Add state for mortgagees
  const [mortgagees, setMortgagees] = useState<MortgageeData[]>([
    { name: '', employments: [{ employment_status: '', current_income: '', employer_job_description: '' }], credit_score_range: '' }
  ]);

  // Add state for last sent templates per client
  const [lastSentTemplates, setLastSentTemplates] = useState<Record<string, { lastEmail?: SentTemplate; lastText?: SentTemplate }>>({});

  // Add state for editing follow-up date
  const [editingFollowUpId, setEditingFollowUpId] = useState<string | null>(null);
  const [editingFollowUpDate, setEditingFollowUpDate] = useState<string>('');

  // Add calendar modal states
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [selectedClientForCalendar, setSelectedClientForCalendar] = useState<Client | null>(null);
  const [calendarAction, setCalendarAction] = useState<'meeting' | 'reminder' | null>(null);
  
  // Meeting invite fields
  const [meetingTitle, setMeetingTitle] = useState<string>('');
  const [meetingDate, setMeetingDate] = useState<string>('');
  const [meetingTime, setMeetingTime] = useState<string>('');
  const [meetingDuration, setMeetingDuration] = useState<string>('30');
  const [meetingNotes, setMeetingNotes] = useState<string>('');
  
  // Follow-up reminder fields
  const [reminderDate, setReminderDate] = useState<string>('');
  const [reminderTime, setReminderTime] = useState<string>('');
  const [reminderNotes, setReminderNotes] = useState<string>('');

  // Add highlighted clients state - load from localStorage
  const [highlightedClients, setHighlightedClients] = useState<Set<string>>(() => {
    const saved = localStorage.getItem('highlightedClients');
    return saved ? new Set(JSON.parse(saved)) : new Set();
  });

  // Add state for highlight colors - load from localStorage
  const [highlightColors, setHighlightColors] = useState<Record<string, 'red' | 'purple'>>(() => {
    const saved = localStorage.getItem('highlightColors');
    return saved ? JSON.parse(saved) : {};
  });

  // Add state for showing color picker
  const [showColorPicker, setShowColorPicker] = useState<string | null>(null);

  // Add state for add client dialog
  const [showAddClientDialog, setShowAddClientDialog] = useState(false);

  // Separate local state for form inputs to prevent lag
  const [localClientName, setLocalClientName] = useState('');
  const [localFirstName, setLocalFirstName] = useState('');
  const [localLastName, setLocalLastName] = useState('');
  const [localEmail, setLocalEmail] = useState('');
  const [localPhone, setLocalPhone] = useState('');
  const [localState, setLocalState] = useState('');
  const [localPurchasePrice, setLocalPurchasePrice] = useState('');
  const [localLoanAmount, setLocalLoanAmount] = useState('');
  const [localLoanType, setLocalLoanType] = useState('');
  const [localRealtorId, setLocalRealtorId] = useState('');
  const [localLenderName, setLocalLenderName] = useState('');
  const [localRealtorName, setLocalRealtorName] = useState('');

  // Add state for collapsing follow-up alerts
  const [followUpAlertsCollapsed, setFollowUpAlertsCollapsed] = useState(true); // Changed to true by default
  const [underwritingAlertsCollapsed, setUnderwritingAlertsCollapsed] = useState(false);

  // Add newClient state - THIS WAS MISSING!
  const [newClient, setNewClient] = useState({
    name: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    state: '',
    purchase_price: '',
    date_created: '',
    loan_type: '',
    loan_amount: '',
    realtor_id: ''
  });

  // Add state for showing state dropdown
  const [showStateDropdown, setShowStateDropdown] = useState(false);

  // Add state for discovery call fields from database
  const [discoveryCallFields, setDiscoveryCallFields] = useState<DiscoveryCallField[]>([]);

  // Add state for tracking if email template was opened from client info dialog
  const [emailTemplateFromClientInfo, setEmailTemplateFromClientInfo] = useState(false);

  // Add state for tracking if text template was opened from client info dialog
  const [textTemplateFromClientInfo, setTextTemplateFromClientInfo] = useState(false);

  // Add state for tracking if discovery call was opened from client info dialog
  const [discoveryCallFromClientInfo, setDiscoveryCallFromClientInfo] = useState(false);

  // Add state for tracking the discovery call being edited
  const [editingDiscoveryCallId, setEditingDiscoveryCallId] = useState<string | null>(null);

  // Add mobile menu state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Add state for quick filters
  const [quickFilter, setQuickFilter] = useState<string>('');
  const [quickFilterConfigs, setQuickFilterConfigs] = useState<QuickFilterConfig[]>([]);

  // Add broker filter state
  const [brokerFilter, setBrokerFilter] = useState<string>('');

  // Add state for "Who Will be on Mortgage?" field
  const [whoWillBeOnMortgage, setWhoWillBeOnMortgage] = useState<string>('');

  // Add state to track original client states for filtering
  const [originalClientStates, setOriginalClientStates] = useState<Record<string, string>>({});

  // Add state for advanced filter
  const [selectedFilterField, setSelectedFilterField] = useState<string>('');
  const [selectedFilterValue, setSelectedFilterValue] = useState<string>('');

  // Add state for discovery call preview modal
  const [showDiscoveryPreview, setShowDiscoveryPreview] = useState(false);

  // Add state for pre-approval template
  const [preApprovalTemplate, setPreApprovalTemplate] = useState<string>('');

  // Add state for column widths - load from localStorage
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() => {
    const saved = localStorage.getItem('crmColumnWidths');
    return saved ? JSON.parse(saved) : {
      name: 150,
      state: 80,
      lead_source: 120,
      purchase_price: 120,
      lender_name: 120,
      broker: 100,
      client_state: 200,
      last_contact: 120,
      follow_up_date: 120,
      last_text: 150,
      last_email: 150,
      actions: 120
    };
  });

  const [resizingColumn, setResizingColumn] = useState<string | null>(null);
  const [resizeStartX, setResizeStartX] = useState<number>(0);
  const [resizeStartWidth, setResizeStartWidth] = useState<number>(0);

  // Add follow-up only filter state
  const [followUpOnlyFilter, setFollowUpOnlyFilter] = useState(false);

  // Handle column resize
  const handleResizeStart = (e: React.MouseEvent, columnKey: string) => {
    e.preventDefault();
    setResizingColumn(columnKey);
    setResizeStartX(e.clientX);
    setResizeStartWidth(columnWidths[columnKey] || 150);
  };

  const handleResizeMove = (e: MouseEvent) => {
    if (!resizingColumn) return;
    const diff = e.clientX - resizeStartX;
    const newWidth = Math.max(80, resizeStartWidth + diff);
    setColumnWidths(prev => ({
      ...prev,
      [resizingColumn]: newWidth
    }));
  };

  const handleResizeEnd = () => {
    setResizingColumn(null);
  };

  useEffect(() => {
    if (resizingColumn) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
      return () => {
        document.removeEventListener('mousemove', handleResizeMove);
        document.removeEventListener('mouseup', handleResizeEnd);
      };
    }
  }, [resizingColumn, resizeStartX, resizeStartWidth]);

  // Save column widths to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('crmColumnWidths', JSON.stringify(columnWidths));
  }, [columnWidths]);

  // Save highlighted clients to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('highlightedClients', JSON.stringify(Array.from(highlightedClients)));
  }, [highlightedClients]);

  // Save highlight colors to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('highlightColors', JSON.stringify(highlightColors));
  }, [highlightColors]);

  // Check authentication
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Session error:', error);
          // Clear any invalid session data
          await supabase.auth.signOut();
          navigate('/login');
          return;
        }
        
        if (!session) {
          navigate('/login');
        } else {
          // Load user profile name
          const metadata = session.user.user_metadata;
          const firstName = metadata.first_name || '';
          const lastName = metadata.last_name || '';
          setUserFullName(`${firstName} ${lastName}`.trim() || 'User');
          
          // Store full user profile for template variables
          setUserProfile({
            firstName,
            lastName,
            email: session.user.email || '',
            phone: metadata.phone || '',
            nmls: metadata.nmls || ''
          });
        }
      } catch (error) {
        console.error('Auth check error:', error);
        await supabase.auth.signOut();
        navigate('/login');
      }
    };
    checkAuth();
  }, [navigate]);

  // Add handleLogout function
  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  // Load clients from Supabase
  useEffect(() => {
    loadClients();
    loadClientStates();
    loadLenderNames();
    loadLoanTypes();
    loadRealtors();
    loadQuickFilterConfigs();
    loadPreApprovalTemplate();
    loadTemplates(); // Add this line to load templates
    loadCompRates(); // Add this line to load comp rates
    loadDiscoveryCallFields(); // Add this line to load discovery call fields
  }, []);

  const loadPreApprovalTemplate = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    const { data, error } = await supabase
      .from('pre_approval_templates')
      .select('template_content')
      .eq('user_id', session.user.id)
      .single();

    if (error && error.code !== 'PGRST116') {
      console.error('Error loading pre-approval template:', error);
      return;
    }

    if (data) {
      setPreApprovalTemplate(data.template_content);
    } else {
      // Set default template
      setPreApprovalTemplate(`Dear {clientName},

I am pleased to inform you that you have been pre-approved for a mortgage loan with the following terms:

PRE-APPROVAL DETAILS:
• Loan Amount: {loanAmount}
• Purchase Price: {purchasePrice}
• Property State: {state}
• Loan Type: {loanType}

PRE-APPROVAL CONDITIONS:
This pre-approval is subject to the following conditions:
• Verification of employment and income
• Satisfactory property appraisal
• Clear title report
• No adverse changes to credit or financial status
• Final underwriting approval

VALIDITY PERIOD:
This pre-approval letter is valid for 90 days from the date of issuance.

NEXT STEPS:
With this pre-approval letter, you can confidently make offers on properties within your approved price range. Please contact me when you find a property you'd like to purchase, and we'll move forward with the full loan application process.

If you have any questions or need any clarification, please don't hesitate to reach out.

Best regards,
{brokerName}
{brokerEmail}`);
    }
  };

  const loadDiscoveryCallFields = async () => {
    const { data, error } = await supabase
      .from('discovery_call_fields')
      .select('*')
      .order('display_order', { ascending: true });

    if (error) {
      console.error('Error loading discovery call fields:', error);
      return;
    }

    if (data && data.length > 0) {
      setDiscoveryCallFields(data);
    } else {
      // Fallback to default fields if none in database
      setDiscoveryCallFields([
        {
          id: '1',
          field_name: 'current_housing',
          field_label: 'Current Housing Situation',
          field_type: 'select',
          dropdown_options: ['Renting', 'Living with Family', 'Own Home', 'Other'],
          display_order: 1,
          is_required: false
        },
        {
          id: '2',
          field_name: 'purchase_timeline',
          field_label: 'Purchase Timeline',
          field_type: 'select',
          dropdown_options: ['0-3 months', '3-6 months', '6-12 months', '12+ months', 'Just exploring'],
          display_order: 2,
          is_required: false
        },
        {
          id: '3',
          field_name: 'preapproval_status',
          field_label: 'Pre-Approval Status',
          field_type: 'select',
          dropdown_options: ['Not started', 'In progress', 'Pre-approved', 'Pre-qualified'],
          display_order: 3,
          is_required: false
        },
        {
          id: '4',
          field_name: 'budget_down_payment',
          field_label: 'Budget / Down Payment',
          field_type: 'text',
          dropdown_options: null,
          display_order: 4,
          is_required: false
        },
        {
          id: '5',
          field_name: 'credit_score_range',
          field_label: 'Credit Score Range',
          field_type: 'select',
          dropdown_options: ['Excellent (740+)', 'Good (670-739)', 'Fair (580-669)', 'Poor (<580)', 'Unknown'],
          display_order: 5,
          is_required: false
        },
        {
          id: '6',
          field_name: 'employment_status',
          field_label: 'Employment Status',
          field_type: 'select',
          dropdown_options: ['Full-time W2', 'Self-employed', 'Part-time', 'Contract', 'Retired', 'Other'],
          display_order: 6,
          is_required: false
        },
        {
          id: '7',
          field_name: 'additional_notes',
          field_label: 'Additional Notes',
          field_type: 'textarea',
          dropdown_options: null,
          display_order: 7,
          is_required: false
        },
        {
          id: '8',
          field_name: 'call_date',
          field_label: 'Call Date',
          field_type: 'date',
          dropdown_options: null,
          display_order: 8,
          is_required: false
        }
      ]);
    }
  };

  const loadCompRates = async () => {
    const { data, error } = await supabase
      .from('comp_rates')
      .select('*')
      .order('broker', { ascending: true });

    if (error) {
      console.error('Error loading comp rates:', error);
      return;
    }

    if (data) {
      setCompRates(data);
    }
  };

  const loadLenderNames = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    // Load both user-specific lenders and global defaults
    const { data, error } = await supabase
      .from('lender_names')
      .select('*')
      .or(`user_id.eq.${session.user.id},is_global.eq.true`)
      .order('display_order', { ascending: true });

    if (error) {
      console.error('Error loading lender names:', error);
      return;
    }

    if (data && data.length > 0) {
      setLenderOptions(data.map(l => l.lender_name));
    } else {
      // Fallback to default lenders if none in database
      setLenderOptions(['Rocket', 'Quicken Loans', 'Better.com', 'LoanDepot']);
    }
  };

  const loadClientStates = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    // Load both user-specific states and global defaults
    const { data, error } = await supabase
      .from('client_states')
      .select('*')
      .or(`user_id.eq.${session.user.id},is_global.eq.true`)
      .order('display_order', { ascending: true });

    if (error) {
      console.error('Error loading client states:', error);
      return;
    }

    if (data && data.length > 0) {
      setClientStateOptions(data);
    } else {
      // Fallback to default states if none in database
      setClientStateOptions([
        { state_name: 'Lead Received; Not Yet Contacted', display_order: 1 },
        { state_name: 'Lead Received; Contacted; No Response', display_order: 2 },
        { state_name: 'Application Sent, Awaiting Completion', display_order: 3 },
        { state_name: 'Application Completed; Pre-Approval/VAL in Process', display_order: 4 },
        { state_name: 'Pre-Approved/VAL Complete; House Shopping', display_order: 5 },
        { state_name: 'Offer Accepted; Loan In Process', display_order: 6 },
        { state_name: 'Closed', display_order: 7 },
        { state_name: 'On Hold; Will Contact at Later Date', display_order: 8 },
        { state_name: 'Not Interested; Dead Deal', display_order: 9 }
      ]);
    }
  };

  const loadClients = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    const { data, error } = await supabase
      .from('clients')
      .select('*')
      .eq('user_id', session.user.id)
      .neq('client_state', 'Closed')
      .neq('client_state', 'Not Interested; Dead Deal')
      .neq('client_state', 'On Hold; Will Contact at Later Date')
      .order('date_created', { ascending: false });

    if (error) {
      console.error('Error loading clients:', error);
      return;
    }

    if (data && data.length > 0) {
      // Don't override broker - preserve the value from database
      // Only update "Never" and "No Contact Yet" to "None"
      const updatedClients = data.map(client => ({
        ...client,
        last_contact: (client.last_contact === 'Never' || client.last_contact === 'No Contact Yet') ? 'None' : client.last_contact
      }));
      setClients(updatedClients);
      
      // Store original client states for filtering
      const originalStates: Record<string, string> = {};
      updatedClients.forEach(client => {
        originalStates[client.id] = client.client_state;
      });
      setOriginalClientStates(originalStates);
      
      // Load last sent templates for all clients
      loadLastSentTemplatesForClients(updatedClients.map(c => c.id));
    }
  };

  const loadLastSentTemplatesForClients = async (clientIds: string[]) => {
    const { data, error } = await supabase
      .from('sent_templates')
      .select('*')
      .in('client_id', clientIds)
      .order('sent_at', { ascending: false });

    if (error) {
      console.error('Error loading last sent templates:', error);
      return;
    }

    if (data) {
      const templatesMap: Record<string, { lastEmail?: SentTemplate; lastText?: SentTemplate }> = {};
      
      data.forEach(template => {
        if (!templatesMap[template.client_id]) {
          templatesMap[template.client_id] = {};
        }
        
        if (template.template_type === 'email' && !templatesMap[template.client_id].lastEmail) {
          templatesMap[template.client_id].lastEmail = template;
        } else if (template.template_type === 'text' && !templatesMap[template.client_id].lastText) {
          templatesMap[template.client_id].lastText = template;
        }
      });
      
      setLastSentTemplates(templatesMap);
    }
  };

  const loadTemplates = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    // Load email templates - filter by user_id
    const { data: emailData } = await supabase
      .from('email_templates')
      .select('*')
      .eq('user_id', session.user.id)
      .order('display_order', { ascending: true, nullsFirst: false });

    if (emailData && emailData.length > 0) {
      setEmailTemplates(emailData);
    } else {
      // Set default templates if user has none
      setEmailTemplates([
        {
          id: '1',
          name: 'Initial Contact',
          subject: 'Hello {clientName} - Let\'s Discuss Your Home Loan',
          body: 'Hi {clientName},\n\nThank you for your interest in securing a home loan. I\'m Heena, and I\'d love to help you find the best mortgage solution for your {state} property.\n\nI noticed you\'re looking at a purchase price of {purchasePrice}. I have some great options that might work perfectly for you.\n\nWhen would be a good time to discuss your financing needs?\n\nBest regards,\nHeena\n{brokerName}\n{brokerEmail}'
        },
        {
          id: '2',
          name: 'Follow Up',
          subject: 'Following Up - {clientName}',
          body: 'Hi {clientName},\n\nI wanted to follow up on my previous email regarding your home loan for the property in {state}.\n\nI have some competitive rates available right now that could save you money on your {purchasePrice} purchase.\n\nLet me know if you have any questions or would like to schedule a call.\n\nBest regards,\nHeena\n{brokerName}\n{brokerEmail}'
        },
        {
          id: '3',
          name: 'Document Request',
          subject: 'Documents Needed - {clientName}',
          body: 'Hi {clientName},\n\nTo move forward with your loan application for the {state} property, I\'ll need the following documents:\n\n• Recent pay stubs (last 2 months)\n• W-2 forms (last 2 years)\n• Bank statements (last 2 months)\n• Photo ID\n• Tax returns (last 2 years)\n\nPlease send these at your earliest convenience so we can keep your application moving forward.\n\nThank you,\nHeena\n{brokerName}\n{brokerEmail}'
        }
      ]);
    }

    // Load text templates - filter by user_id
    const { data: textData } = await supabase
      .from('text_templates')
      .select('*')
      .eq('user_id', session.user.id)
      .order('display_order', { ascending: true, nullsFirst: false });

    if (textData && textData.length > 0) {
      setTextTemplates(textData);
    } else {
      // Set default templates if user has none
      setTextTemplates([
        {
          id: '1',
          name: 'Quick Introduction',
          body: 'Hi {clientName}! This is Heena from {brokerName}. I\'d love to discuss your home loan options for your {state} property. When\'s a good time to chat?'
        },
        {
          id: '2',
          name: 'Quick Follow Up',
          body: 'Hi {clientName}, just following up on your loan application. Do you have any questions? Feel free to call or text me anytime. - Heena'
        },
        {
          id: '3',
          name: 'Document Reminder',
          body: 'Hi {clientName}, friendly reminder that we\'re still waiting on a few documents for your loan application. Let me know if you need help with anything! - Heena'
        }
      ]);
    }
  };

  const loadRealtors = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    const { data, error } = await supabase
      .from('realtors')
      .select('*')
      .eq('user_id', session.user.id)
      .order('display_order', { ascending: true });

    if (error) {
      console.error('Error loading realtors:', error);
      return;
    }

    if (data && data.length > 0) {
      setRealtors(data);
    }
  };

  const loadLoanTypes = async () => {
    const { data, error } = await supabase
      .from('loan_types')
      .select('*')
      .order('display_order', { ascending: true });

    if (error) {
      console.error('Error loading loan types:', error);
      return;
    }

    if (data && data.length > 0) {
      setLoanTypes(data);
    } else {
      // Fallback to default loan types if none in database
      setLoanTypes([
        { id: '1', loan_type: 'Conventional', display_order: 1, intake_mapping: '30-Yr Fixed' },
        { id: '2', loan_type: 'FHA', display_order: 2, intake_mapping: 'FHA' },
        { id: '3', loan_type: 'VA', display_order: 3, intake_mapping: 'VA' },
        { id: '4', loan_type: 'USDA', display_order: 4, intake_mapping: 'USDA' },
        { id: '5', loan_type: 'Jumbo', display_order: 5, intake_mapping: 'Jumbo' }
      ]);
    }
  };

  const loadQuickFilterConfigs = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    // Load user-specific quick filter configs or global defaults
    const { data, error } = await supabase
      .from('quick_filter_configs')
      .select('*')
      .or(`user_id.eq.${session.user.id},is_global.eq.true`)
      .order('display_order', { ascending: true });

    if (error) {
      console.error('Error loading quick filter configs:', error);
      return;
    }

    if (data && data.length > 0) {
      setQuickFilterConfigs(data);
    } else {
      // Fallback to default quick filters if none in database
      setQuickFilterConfigs([
        {
          id: 'lead',
          filter_name: 'Lead',
          client_states: ['Lead Received; Not Yet Contacted', 'Lead Received; Contacted; No Response'],
          display_order: 1
        },
        {
          id: 'in-process',
          filter_name: 'In Process',
          client_states: [
            'Application Sent, Awaiting Completion',
            'Application Completed; Pre-Approval/VAL in Process',
            'Submitted to Underwriting',
            'UW Requires Add\'l Docs from Client',
            'Resubmitted to Underwriting',
            'Pre-Approved/VAL Complete; House Shopping'
          ],
          display_order: 2
        },
        {
          id: 'live-loan',
          filter_name: 'Live Loan',
          client_states: ['Offer Accepted; Loan In Process'],
          display_order: 3
        }
      ]);
    }
  };

  const getLeadSourceDisplay = (client: Client): string => {
    if (client.lead_source === 'Realtor' && client.realtor_id) {
      const realtor = realtors.find(r => r.id === client.realtor_id);
      return realtor ? realtor.name : 'Realtor';
    }
    return client.lead_source || 'Realtor';
  };

  const handleEdit = (id: string) => {
    const client = clients.find(c => c.id === id);
    if (client) {
      setEditingClient({ ...client });
      
      // Set lead source properly - check if it's a realtor or other source
      if (client.realtor_id) {
        const realtor = realtors.find(r => r.id === client.realtor_id);
        setEditDialogLeadSource(realtor ? realtor.name : (client.lead_source || 'Realtor'));
      } else {
        setEditDialogLeadSource(client.lead_source || 'FreeRateUpdate');
      }
      
      setEditDialogCustomLeadSource('');
      setEditDialogStateSearch('');
      
      // Set lender dropdown value
      if (client.lender_name && lenderOptions.includes(client.lender_name)) {
        setEditDialogLender(client.lender_name);
        setEditDialogCustomLender('');
      } else if (client.lender_name) {
        setEditDialogLender('Other');
        setEditDialogCustomLender(client.lender_name);
      } else {
        setEditDialogLender(lenderOptions.length > 0 ? lenderOptions[0] : '');
        setEditDialogCustomLender('');
      }
      
      setShowEditDialog(true);
    }
  };

  const handleSaveEditDialog = async () => {
    if (!editingClient) return;

    // Validate phone number only if provided
    if (editingClient.phone && editingClient.phone.trim() !== '') {
      const phoneDigits = editingClient.phone.replace(/\D/g, '');
      if (phoneDigits.length !== 10) {
        alert('Please enter a valid 10-digit phone number or leave it empty');
        return;
      }
    }

    // Determine final lender name
    const finalLenderName = editDialogLender === 'Other' ? editDialogCustomLender : editDialogLender;

    const { error } = await supabase
      .from('clients')
      .update({
        name: editingClient.name,
        first_name: editingClient.first_name,
        last_name: editingClient.last_name,
        email: editingClient.email,
        phone: editingClient.phone || '',
        state: editingClient.state,
        purchase_price: editingClient.purchase_price,
        broker: editingClient.broker,
        loan_type: editingClient.loan_type,
        loan_amount: editingClient.loan_amount || null,
        lead_source: editingClient.lead_source,
        realtor_id: editingClient.realtor_id || null,
        lender_name: finalLenderName || null
      })
      .eq('id', editingClient.id);

    if (error) {
      console.error('Error updating client:', error);
      alert('Failed to update client');
      return;
    }

    setClients(clients.map(c => c.id === editingClient.id ? { ...editingClient, lender_name: finalLenderName } : c));
    setShowEditDialog(false);
    setEditingClient(null);
    setEditDialogLeadSource('');
    setEditDialogCustomLeadSource('');
    setEditDialogStateSearch('');
    setEditDialogLender('');
    setEditDialogCustomLender('');
  };

  const handleCancelEditDialog = () => {
    setShowEditDialog(false);
    setEditingClient(null);
    setEditDialogLeadSource('');
    setEditDialogCustomLeadSource('');
    setEditDialogStateSearch('');
    setEditDialogLender('');
    setEditDialogCustomLender('');
  };

  const handleEditDialogChange = (field: keyof Client, value: string) => {
    if (!editingClient) return;
    
    let formattedValue = value;
    
    // Format phone number as user types
    if (field === 'phone') {
      const digits = value.replace(/\D/g, '');
      if (digits.length <= 10) {
        if (digits.length >= 6) {
          formattedValue = `(${digits.slice(0, 3)})-${digits.slice(3, 6)}-${digits.slice(6)}`;
        } else if (digits.length >= 3) {
          formattedValue = `(${digits.slice(0, 3)})-${digits.slice(3)}`;
        }
      } else {
        return; // Don't allow more than 10 digits
      }
    }
    
    // Format purchase price
    if (field === 'purchase_price') {
      const digits = value.replace(/[$,]/g, '');
      if (digits) {
        const num = parseInt(digits);
        formattedValue = `$${num.toLocaleString()}`;
      } else {
        formattedValue = '';
      }
    }
    
    // Format loan amount
    if (field === 'loan_amount') {
      const digits = value.replace(/[$,]/g, '');
      if (digits) {
        const num = parseInt(digits);
        formattedValue = `$${num.toLocaleString()}`;
      } else {
        formattedValue = '';
      }
    }
    
    const updatedClient = { ...editingClient, [field]: formattedValue };
    
    // Auto-update broker when state changes
    if (field === 'state') {
      updatedClient.broker = getBrokerByState(formattedValue);
    }
    
    setEditingClient(updatedClient);
  };

  const handleSave = async (id: string) => {
    const client = clients.find(c => c.id === id);
    if (!client) return;

    const { error } = await supabase
      .from('clients')
      .update({
        name: client.name,
        first_name: client.first_name,
        last_name: client.last_name,
        email: client.email,
        phone: client.phone,
        state: client.state,
        purchase_price: client.purchase_price,
        broker: client.broker,
        last_contact: client.last_contact,
        client_state: client.client_state,
        loan_type: client.loan_type,
        loan_amount: client.loan_amount || null,
        realtor_id: client.realtor_id || null
      })
      .eq('id', id);

    if (error) {
      console.error('Error updating client:', error);
      alert('Failed to update client');
      return;
    }

    setEditingId(null);
  };

  const getClientStateSortOrder = (stateName: string): number => {
    const stateOption = clientStateOptions.find(opt => opt.state_name === stateName);
    return stateOption ? stateOption.display_order : 999;
  };

  const handleChange = async (id: string, field: keyof Client, value: string) => {
    const client = clients.find(c => c.id === id);
    if (!client) return;

    // If changing client state to "Closed", show confirmation dialog
    if (field === 'client_state' && value === 'Closed') {
      setClientToClose({ id, name: client.name });
      setClosingDate('');
      setInterestRate('');
      setLenderLoanNumber('');
      setAriveLoanNumber('');
      setClosingBrokerComp('');
      
      // Set lender name dropdown
      if (client.lender_name && lenderOptions.includes(client.lender_name)) {
        setClosingLenderName(client.lender_name);
        setClosingCustomLender('');
      } else if (client.lender_name) {
        setClosingLenderName('Other');
        setClosingCustomLender(client.lender_name);
      } else {
        setClosingLenderName(lenderOptions.length > 0 ? lenderOptions[0] : '');
        setClosingCustomLender('');
      }
      
      // Set lead source dropdown
      if (client.realtor_id) {
        const realtor = realtors.find(r => r.id === client.realtor_id);
        setClosingLeadSource(realtor ? realtor.name : (client.lead_source || 'FreeRateUpdate'));
        setClosingCustomLeadSource('');
      } else {
        setClosingLeadSource(client.lead_source || 'FreeRateUpdate');
        setClosingCustomLeadSource('');
      }
      
      setClosingPurchasePrice(client.purchase_price || '');
      setClosingLoanAmount(client.loan_amount || '');
      setShowConfirmModal(true);
      return;
    }

    // If changing client state to "Not Interested; Dead Deal", show dead deal dialog
    if (field === 'client_state' && value === 'Not Interested; Dead Deal') {
      setClientForDeadDeal({ id, name: client.name });
      setShowDeadDealModal(true);
      return;
    }

    // If changing client state to "On Hold; Will Contact at Later Date", show on hold dialog
    if (field === 'client_state' && value === 'On Hold; Will Contact at Later Date') {
      setClientForOnHold({ id, name: client.name });
      setShowOnHoldModal(true);
      return;
    }

    // Check if we need to prompt for missing fields based on sort order
    if (field === 'client_state') {
      const newStateSortOrder = getClientStateSortOrder(value);
      
      // For sort order 5 or higher, check all required fields
      if (newStateSortOrder >= 5) {
        const needsState = !client.state || client.state.trim() === '';
        const needsPurchasePrice = !client.purchase_price || client.purchase_price.trim() === '';
        const needsLoanAmount = !client.loan_amount || client.loan_amount.trim() === '';
        const needsLoanType = !client.loan_type || client.loan_type.trim() === '';
        const needsLeadSource = !client.lead_source || client.lead_source.trim() === '';
        const needsLenderName = !client.lender_name || client.lender_name.trim() === '';
        
        if (needsState || needsPurchasePrice || needsLoanAmount || needsLoanType || needsLeadSource || needsLenderName) {
          setClientForStatePrompt({ id, name: client.name, newState: value });
          setStatePromptValue(client.state || '');
          setLoanAmountPromptValue(client.loan_amount || '');
          setSelectedLender(client.lender_name || (lenderOptions.length > 0 ? lenderOptions[0] : ''));
          setCustomLender('');
          setShowStatePromptModal(true);
          return;
        }
      }
    }

    // If changing client state to "Submitted to Underwriting" or "Resubmitted to Underwriting", show lender dialog
    if (field === 'client_state' && (value === 'Submitted to Underwriting' || value === 'Resubmitted to Underwriting')) {
      setClientForUnderwritingLender({ id, name: client.name, newState: value });
      setUnderwritingSelectedLender(lenderOptions.length > 0 ? lenderOptions[0] : '');
      setUnderwritingCustomLender('');
      setShowUnderwritingLenderModal(true);
      return;
    }

    // Log state change to history if client_state is changing
    if (field === 'client_state') {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        await supabase.from('client_state_history').insert({
          client_id: client.id,
          client_name: client.name,
          previous_state: client.client_state,
          new_state: value,
          user_id: session.user.id
        });
      }
    }

    setClients(clients.map(c => {
      if (c.id === id) {
        const updatedClient = { ...c, [field]: value };
        // Auto-update broker when state changes
        if (field === 'state') {
          updatedClient.broker = getBrokerByState(value);
        }
        return updatedClient;
      }
      return c;
    }));
  };

  const handleConfirmStatePrompt = async () => {
    if (clientForStatePrompt) {
      const client = clients.find(c => c.id === clientForStatePrompt.id);
      if (!client) return;

      const newStateSortOrder = getClientStateSortOrder(clientForStatePrompt.newState);
      
      // Validate required fields based on what's being prompted
      if (newStateSortOrder >= 5) {
        const needsState = !client.state || client.state.trim() === '';
        const needsPurchasePrice = !client.purchase_price || client.purchase_price.trim() === '';
        const needsLoanAmount = !client.loan_amount || client.loan_amount.trim() === '';
        const needsLoanType = !client.loan_type || client.loan_type.trim() === '';
        const needsLeadSource = !client.lead_source || client.lead_source.trim() === '';
        const needsLenderName = !client.lender_name || client.lender_name.trim() === '';
        
        // Validate state if needed
        if (needsState) {
          const stateAbbr = statePromptValue.trim().toUpperCase();
          if (!stateAbbr || stateAbbr.length !== 2) {
            alert('Please enter a valid 2-character state abbreviation');
            return;
          }
        }

        // Validate purchase price if needed
        if (needsPurchasePrice && (!purchasePricePromptValue || purchasePricePromptValue.trim() === '')) {
          alert('Please enter a purchase price');
          return;
        }

        // Validate loan amount if needed
        if (needsLoanAmount && (!loanAmountPromptValue || loanAmountPromptValue.trim() === '')) {
          alert('Please enter a loan amount');
          return;
        }

        // Validate loan type if needed
        if (needsLoanType && (!loanTypePromptValue || loanTypePromptValue.trim() === '')) {
          alert('Please select a loan type');
          return;
        }

        // Validate lead source if needed
        if (needsLeadSource && (!leadSourcePromptValue || leadSourcePromptValue.trim() === '')) {
          alert('Please select a lead source');
          return;
        }

        // Validate lender name if needed
        if (needsLenderName) {
          const lenderName = selectedLender === 'Other' ? customLender : selectedLender;
          if (!lenderName || lenderName.trim() === '') {
            alert('Please enter a lender name');
            return;
          }
        }
      }

      // Log state change
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        await supabase.from('client_state_history').insert({
          client_id: client.id,
          client_name: client.name,
          previous_state: client.client_state,
          new_state: clientForStatePrompt.newState,
          user_id: session.user.id
        });
      }

      const stateAbbr = statePromptValue.trim().toUpperCase();
      const broker = stateAbbr ? getBrokerByState(stateAbbr) : client.broker;
      const lenderName = selectedLender === 'Other' ? customLender : selectedLender;
      const timestamp = getCurrentTimestamp();

      const updateData: any = {
        client_state: clientForStatePrompt.newState,
        client_state_updated: timestamp
      };

      if (statePromptValue.trim()) {
        updateData.state = stateAbbr;
        updateData.broker = broker;
      }

      if (purchasePricePromptValue.trim()) {
        updateData.purchase_price = purchasePricePromptValue.trim();
      }

      if (loanAmountPromptValue.trim()) {
        updateData.loan_amount = loanAmountPromptValue.trim();
      }

      if (loanTypePromptValue.trim()) {
        updateData.loan_type = loanTypePromptValue.trim();
      }

      if (leadSourcePromptValue.trim()) {
        updateData.lead_source = leadSourcePromptValue.trim();
      }

      if (lenderName) {
        updateData.lender_name = lenderName;
      }

      const { error } = await supabase
        .from('clients')
        .update(updateData)
        .eq('id', clientForStatePrompt.id);

      if (error) {
        console.error('Error updating client:', error);
        alert('Failed to update client');
      } else {
        setClients(clients.map(c => 
          c.id === clientForStatePrompt.id 
            ? { ...c, ...updateData }
            : c
        ));
      }
    }
    
    setShowStatePromptModal(false);
    setClientForStatePrompt(null);
    setStatePromptValue('');
    setLoanAmountPromptValue('');
    setPurchasePricePromptValue('');
    setLoanTypePromptValue('');
    setLeadSourcePromptValue('');
    setSelectedLender('');
    setCustomLender('');
  };

  const handleCancelStatePrompt = () => {
    setShowStatePromptModal(false);
    setClientForStatePrompt(null);
    setStatePromptValue('');
    setLoanAmountPromptValue('');
    setPurchasePricePromptValue('');
    setLoanTypePromptValue('');
    setLeadSourcePromptValue('');
    setSelectedLender('');
    setCustomLender('');
  };

  const handleConfirmDeadDeal = async () => {
    if (clientForDeadDeal) {
      const client = clients.find(c => c.id === clientForDeadDeal.id);
      if (client) {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          // Log state change
          await supabase.from('client_state_history').insert({
            client_id: client.id,
            client_name: client.name,
            previous_state: client.client_state,
            new_state: 'Not Interested; Dead Deal',
            user_id: session.user.id
          });
        }
      }

      const timestamp = getCurrentTimestamp();

      const { error } = await supabase
        .from('clients')
        .update({
          client_state: 'Not Interested; Dead Deal',
          notes: deadDealNotes,
          client_state_updated: timestamp
        })
        .eq('id', clientForDeadDeal.id);

      if (error) {
        console.error('Error updating client:', error);
        alert('Failed to update client');
      } else {
        setClients(clients.filter(c => c.id !== clientForDeadDeal.id));
      }
    }
    
    setShowDeadDealModal(false);
    setClientForDeadDeal(null);
    setDeadDealNotes('');
  };

  const handleCancelDeadDeal = () => {
    setShowDeadDealModal(false);
    setClientForDeadDeal(null);
    setDeadDealNotes('');
  };

  const handleConfirmOnHold = async () => {
    if (clientForOnHold) {
      let finalFollowUpDate = onHoldFollowUpDate;
      
      // Calculate date based on time amount and unit if not manually set
      if (!onHoldFollowUpDate && onHoldTimeAmount && onHoldTimeUnit) {
        const today = new Date();
        const amount = parseInt(onHoldTimeAmount);
        
        switch (onHoldTimeUnit) {
          case 'Day(s)':
            today.setDate(today.getDate() + amount);
            break;
          case 'Week(s)':
            today.setDate(today.getDate() + (amount * 7));
            break;
          case 'Month(s)':
            today.setMonth(today.getMonth() + amount);
            break;
          case 'Year(s)':
            today.setFullYear(today.getFullYear() + amount);
            break;
        }
        
        finalFollowUpDate = today.toISOString().split('T')[0];
      }
      
      if (!finalFollowUpDate) {
        alert('Please select a follow-up date or use the time selector');
        return;
      }

      const client = clients.find(c => c.id === clientForOnHold.id);
      if (client) {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          // Log state change
          await supabase.from('client_state_history').insert({
            client_id: client.id,
            client_name: client.name,
            previous_state: client.client_state,
            new_state: 'On Hold; Will Contact at Later Date',
            user_id: session.user.id
          });
        }
      }

      const timestamp = getCurrentTimestamp();

      const { error } = await supabase
        .from('clients')
        .update({
          client_state: 'On Hold; Will Contact at Later Date',
          follow_up_date: finalFollowUpDate,
          notes: onHoldNotes,
          client_state_updated: timestamp
        })
        .eq('id', clientForOnHold.id);

      if (error) {
        console.error('Error updating client:', error);
        alert('Failed to update client');
      } else {
        setClients(clients.filter(c => c.id !== clientForOnHold.id));
      }
    }
    
    setShowOnHoldModal(false);
    setClientForOnHold(null);
    setOnHoldFollowUpDate('');
    setOnHoldNotes('');
    setOnHoldTimeAmount('1');
    setOnHoldTimeUnit('Week(s)');
  };

  const handleCancelOnHold = () => {
    setShowOnHoldModal(false);
    setClientForOnHold(null);
    setOnHoldFollowUpDate('');
    setOnHoldNotes('');
    setOnHoldTimeAmount('1');
    setOnHoldTimeUnit('Week(s)');
  };

  const handleLenderChange = (id: string, value: string) => {
    setEditingLenderForClient(value);
    if (value !== 'Other') {
      setEditingCustomLender('');
      handleChange(id, 'lender_name', value);
    }
  };

  const handleCustomLenderChange = (id: string, value: string) => {
    setEditingCustomLender(value);
    handleChange(id, 'lender_name', value);
  };

  const handleConfirmLender = async () => {
    if (clientForLender) {
      const lenderName = selectedLender === 'Other' ? customLender : selectedLender;
      
      if (!lenderName || lenderName.trim() === '') {
        alert('Please enter a lender name');
        return;
      }

      const client = clients.find(c => c.id === clientForLender.id);
      if (client) {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          // Log state change
          await supabase.from('client_state_history').insert({
            client_id: client.id,
            client_name: client.name,
            previous_state: client.client_state,
            new_state: 'App Completed; PA/VAL in Process',
            user_id: session.user.id
          });
        }
      }

      const timestamp = getCurrentTimestamp();

      const { error } = await supabase
        .from('clients')
        .update({
          client_state: 'App Completed; PA/VAL in Process',
          lender_name: lenderName,
          client_state_updated: timestamp
        })
        .eq('id', clientForLender.id);

      if (error) {
        console.error('Error updating client:', error);
        alert('Failed to update client');
      } else {
        setClients(clients.map(client => 
          client.id === clientForLender.id 
            ? { ...client, client_state: 'App Completed; PA/VAL in Process', lender_name: lenderName, client_state_updated: timestamp }
            : client
        ));
      }
    }
    
    setShowLenderModal(false);
    setClientForLender(null);
    setSelectedLender('');
    setCustomLender('');
  };

  const handleCancelLender = () => {
    setShowLenderModal(false);
    setClientForLender(null);
    setSelectedLender('');
    setCustomLender('');
  };

  const handleConfirmUnderwritingLender = async () => {
    if (clientForUnderwritingLender) {
      const lenderName = underwritingSelectedLender === 'Other' ? underwritingCustomLender : underwritingSelectedLender;
      
      if (!lenderName || lenderName.trim() === '') {
        alert('Please enter a lender name');
        return;
      }

      const client = clients.find(c => c.id === clientForUnderwritingLender.id);
      if (client) {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          // Log state change
          await supabase.from('client_state_history').insert({
            client_id: client.id,
            client_name: client.name,
            previous_state: client.client_state,
            new_state: clientForUnderwritingLender.newState,
            user_id: session.user.id
          });
        }
      }

      const timestamp = getCurrentTimestamp();

      const { error } = await supabase
        .from('clients')
        .update({
          client_state: clientForUnderwritingLender.newState,
          lender_name: lenderName,
          client_state_updated: timestamp
        })
        .eq('id', clientForUnderwritingLender.id);

      if (error) {
        console.error('Error updating client:', error);
        alert('Failed to update client');
      } else {
        setClients(clients.map(client => 
          client.id === clientForUnderwritingLender.id 
            ? { ...client, client_state: clientForUnderwritingLender.newState, lender_name: lenderName, client_state_updated: timestamp }
            : client
        ));
      }
    }
    
    setShowUnderwritingLenderModal(false);
    setClientForUnderwritingLender(null);
    setUnderwritingSelectedLender('');
    setUnderwritingCustomLender('');
  };

  const handleCancelUnderwritingLender = () => {
    setShowUnderwritingLenderModal(false);
    setClientForUnderwritingLender(null);
    setUnderwritingSelectedLender('');
    setUnderwritingCustomLender('');
  };

  const handleConfirmClosing = async () => {
    if (clientToClose) {
      // Validate all required fields
      const errors: string[] = [];
      
      if (!closingDate) {
        errors.push('Closing Date is required');
      }
      
      if (!interestRate || interestRate.trim() === '') {
        errors.push('Interest Rate is required');
      }
      
      if (!lenderLoanNumber || lenderLoanNumber.trim() === '') {
        errors.push('Lender Loan Number is required');
      }
      
      if (!ariveLoanNumber || ariveLoanNumber.trim() === '') {
        errors.push('Arive Loan Number is required');
      }
      
      if (!closingBrokerComp || closingBrokerComp.trim() === '' || closingBrokerComp === '-') {
        errors.push('Broker Comp is required');
      }
      
      // Validate lender name
      const finalLenderName = closingLenderName === 'Other' ? closingCustomLender : closingLenderName;
      if (!finalLenderName || finalLenderName.trim() === '' || finalLenderName === '-') {
        errors.push('Lender Name is required');
      }
      
      // Validate lead source
      const finalLeadSource = closingLeadSource === 'Other' ? closingCustomLeadSource : closingLeadSource;
      if (!finalLeadSource || finalLeadSource.trim() === '' || finalLeadSource === '-') {
        errors.push('Lead Source is required');
      }
      
      if (!closingPurchasePrice || closingPurchasePrice.trim() === '' || closingPurchasePrice === '-') {
        errors.push('Purchase Price is required');
      }
      
      if (!closingLoanAmount || closingLoanAmount.trim() === '' || closingLoanAmount === '-') {
        errors.push('Loan Amount is required');
      }
      
      if (errors.length > 0) {
        alert('Please complete all required fields:\n\n' + errors.join('\n'));
        return;
      }

      const client = clients.find(c => c.id === clientToClose.id);
      if (client) {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          // Log state change
          await supabase.from('client_state_history').insert({
            client_id: client.id,
            client_name: client.name,
            previous_state: client.client_state,
            new_state: 'Closed',
            user_id: session.user.id
          });
        }
      }

      const timestamp = getCurrentTimestamp();

      // Calculate comp from broker comp
      const calculatedComp = calculateCompFromBrokerComp(closingBrokerComp, client?.broker || 'NS', finalLeadSource);

      const { error } = await supabase
        .from('clients')
        .update({
          client_state: 'Closed',
          client_state_updated: timestamp,
          closing_date: closingDate,
          interest_rate: interestRate,
          lender_loan_number: lenderLoanNumber,
          arive_loan_number: ariveLoanNumber,
          lender_name: finalLenderName,
          lead_source: finalLeadSource,
          purchase_price: closingPurchasePrice,
          loan_amount: closingLoanAmount,
          broker_comp: closingBrokerComp,
          comp: formatCurrency(calculatedComp)
        })
        .eq('id', clientToClose.id);

      if (error) {
        console.error('Error updating client:', error);
        alert('Failed to update client');
      } else {
        setClients(clients.filter(c => c.id !== clientToClose.id));
      }
    }
    
    setShowConfirmModal(false);
    setClientToClose(null);
    setClosingDate('');
    setInterestRate('');
    setLenderLoanNumber('');
    setAriveLoanNumber('');
    setClosingLenderName('');
    setClosingLeadSource('');
    setClosingPurchasePrice('');
    setClosingLoanAmount('');
    setClosingCustomLender('');
    setClosingCustomLeadSource('');
    setClosingBrokerComp('');
  };

  const handleCancelClosing = () => {
    setShowConfirmModal(false);
    setClientToClose(null);
    setClosingDate('');
    setInterestRate('');
    setLenderLoanNumber('');
    setAriveLoanNumber('');
    setClosingLenderName('');
    setClosingLeadSource('');
    setClosingPurchasePrice('');
    setClosingLoanAmount('');
    setClosingCustomLender('');
    setClosingCustomLeadSource('');
    setClosingBrokerComp('');
  };

  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this client?')) {
      const { error } = await supabase
        .from('clients')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting client:', error);
        alert('Failed to delete client');
        return;
      }

      setClients(clients.filter(client => client.id !== id));
    }
  };

  const handleSaveClientState = async (id: string) => {
    const client = clients.find(c => c.id === id);
    if (!client) return;

    const timestamp = getCurrentTimestamp();

    // Log state change to history
    const { data: { session } } = await supabase.auth.getSession();
    if (session) {
      await supabase.from('client_state_history').insert({
        client_id: client.id,
        client_name: client.name,
        previous_state: originalClientStates[client.id] || client.client_state,
        new_state: client.client_state,
        user_id: session.user.id
      });
    }

    const { error } = await supabase
      .from('clients')
      .update({
        client_state: client.client_state,
        client_state_updated: timestamp
      })
      .eq('id', id);

    if (error) {
      console.error('Error updating client state:', error);
      alert('Failed to update client state');
      return;
    }

    // Update the original state after successful save
    setOriginalClientStates(prev => ({
      ...prev,
      [id]: client.client_state
    }));

    setClients(clients.map(c => 
      c.id === id ? { ...c, client_state_updated: timestamp } : c
    ));
  };

  const handleAddClient = async () => {
    // Build client data from local state
    const clientData = {
      name: localClientName,
      first_name: localFirstName,
      last_name: localLastName,
      email: localEmail,
      phone: localPhone,
      state: localState,
      purchase_price: localPurchasePrice,
      loan_amount: localLoanAmount,
      loan_type: localLoanType,
      realtor_id: localRealtorId,
      realtor_name: localRealtorName
    };

    if (clientData.name && clientData.email && clientData.realtor_id) {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      // Validate phone number only if provided
      if (clientData.phone && clientData.phone.trim() !== '') {
        const phoneDigits = clientData.phone.replace(/\D/g, '');
        if (phoneDigits.length !== 10) {
          alert('Please enter a valid 10-digit phone number or leave it empty');
          return;
        }
      }

      const broker = clientData.state ? getBrokerByState(clientData.state) : 'NS';
      const timestamp = getCurrentTimestamp();
      
      // Auto-populate first_name and last_name from name if not provided
      const nameParts = clientData.name.split(' ');
      const firstName = clientData.first_name || nameParts[0] || '';
      const lastName = clientData.last_name || nameParts.slice(1).join(' ') || '';
      
      const finalClientData = {
        name: clientData.name,
        first_name: firstName,
        last_name: lastName,
        email: clientData.email,
        phone: clientData.phone || '-',
        state: clientData.state || '-',
        purchase_price: clientData.purchase_price || '-',
        broker,
        last_contact: 'None',
        client_state: 'Lead Received; Not Yet Contacted',
        date_created: timestamp,
        user_id: session.user.id,
        loan_type: clientData.loan_type || '-',
        lead_source: 'Realtor',
        loan_amount: clientData.loan_amount || '-',
        realtor_id: clientData.realtor_id || null,
        realtor_name: clientData.realtor_name || null,
        client_state_updated: timestamp
      };

      const { data, error } = await supabase
        .from('clients')
        .insert([finalClientData])
        .select()
        .single();

      if (error) {
        console.error('Error adding client:', error);
        alert('Failed to add client');
        return;
      }

      if (data) {
        setClients([data, ...clients]);
      }
      
      // Reset local state
      setLocalClientName('');
      setLocalFirstName('');
      setLocalLastName('');
      setLocalEmail('');
      setLocalPhone('');
      setLocalState('');
      setLocalPurchasePrice('');
      setLocalLoanAmount('');
      setLocalLoanType('');
      setLocalRealtorId('');
      setLocalLenderName('');
      setLocalRealtorName('');
      setShowAddClientDialog(false);
    } else {
      alert('Please fill in all required fields: Name, Email, and Lead Source');
    }
  };

  const handleAddClientWithDiscovery = async () => {
    // Build client data from local state
    const clientData = {
      name: localClientName,
      first_name: localFirstName,
      last_name: localLastName,
      email: localEmail,
      phone: localPhone,
      state: localState,
      purchase_price: localPurchasePrice,
      loan_amount: localLoanAmount,
      loan_type: localLoanType,
      realtor_id: localRealtorId,
      realtor_name: localRealtorName
    };

    if (clientData.name && clientData.email && clientData.realtor_id) {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      // Validate phone number only if provided
      if (clientData.phone && clientData.phone.trim() !== '') {
        const phoneDigits = clientData.phone.replace(/\D/g, '');
        if (phoneDigits.length !== 10) {
          alert('Please enter a valid 10-digit phone number or leave it empty');
          return;
        }
      }

      const broker = clientData.state ? getBrokerByState(clientData.state) : 'NS';
      const timestamp = getCurrentTimestamp();
      
      // Auto-populate first_name and last_name from name if not provided
      const nameParts = clientData.name.split(' ');
      const firstName = clientData.first_name || nameParts[0] || '';
      const lastName = clientData.last_name || nameParts.slice(1).join(' ') || '';
      
      const finalClientData = {
        name: clientData.name,
        first_name: firstName,
        last_name: lastName,
        email: clientData.email,
        phone: clientData.phone || '-',
        state: clientData.state || '-',
        purchase_price: clientData.purchase_price || '-',
        broker,
        last_contact: 'None',
        client_state: 'Lead Received; Not Yet Contacted',
        date_created: timestamp,
        user_id: session.user.id,
        loan_type: clientData.loan_type || '-',
        lead_source: 'Realtor',
        loan_amount: clientData.loan_amount || '-',
        realtor_id: clientData.realtor_id || null,
        realtor_name: clientData.realtor_name || null,
        client_state_updated: timestamp
      };

      const { data, error } = await supabase
        .from('clients')
        .insert([finalClientData])
        .select()
        .single();

      if (error) {
        console.error('Error adding client:', error);
        alert('Failed to add client');
        return;
      }

      if (data) {
        setClients([data, ...clients]);
        
        // Close add client dialog and open discovery call dialog
        setShowAddClientDialog(false);
        
        // Reset local state
        setLocalClientName('');
        setLocalFirstName('');
        setLocalLastName('');
        setLocalEmail('');
        setLocalPhone('');
        setLocalState('');
        setLocalPurchasePrice('');
        setLocalLoanAmount('');
        setLocalLoanType('');
        setLocalRealtorId('');
        setLocalLenderName('');
        setLocalRealtorName('');
        
        // Open discovery call dialog for the new client
        setSelectedClientForDiscovery(data);
        setDiscoveryCallData({
          current_housing: '',
          purchase_timeline: '',
          preapproval_status: '',
          budget_down_payment: '',
          credit_score_range: '',
          employment_status: '',
          additional_notes: '',
          call_date: new Date().toISOString().split('T')[0],
          shopping_reason: '',
          assets: '',
          liabilities: ''
        });
        setMortgagees([
          { name: '', employments: [{ employment_status: '', current_income: '', employer_job_description: '' }], credit_score_range: '' }
        ]);
        setShowDiscoveryCallDialog(true);
      }
    } else {
      alert('Please fill in all required fields: Name, Email, and Lead Source');
    }
  };

  const handleClient = (client: Client) => {
    const brokerEmail = getBrokerEmail(client.broker, compRates);
    window.location.href = `mailto:${client.email}?subject=Hello ${client.name}`;
    
    // Update last contact timestamp
    updateLastContact(client.id);
  };

  const handleSendText = (client: Client) => {
    window.location.href = `sms:${client.phone}?body=Hi ${client.name},`;
    
    // Update last contact timestamp
    updateLastContact(client.id);
  };

  const updateLastContact = async (id: string) => {
    const timestamp = getCurrentTimestamp();

    const { error } = await supabase
      .from('clients')
      .update({ last_contact: timestamp })
      .eq('id', id);

    if (error) {
      console.error('Error updating last contact:', error);
    }
    
    setClients(clients.map(client => 
      client.id === id ? { ...client, last_contact: timestamp } : client
    ));
  };

  const handleUpdateContact = (id: string) => {
    updateLastContact(id);
  };

  const handleSendEmailWithTemplate = (client: Client, template: EmailTemplate) => {
    const subject = replaceTemplateVariables(template.subject, client, compRates, userProfile);
    // Convert HTML to plain text while preserving paragraph/line breaks
    const bodyHtml = replaceTemplateVariables(template.body, client, compRates, userProfile);
    const body = bodyHtml
      .replace(/<\/p>/gi, '\n\n')
      .replace(/<\/div>/gi, '\n')
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<li[^>]*>/gi, '• ')
      .replace(/<\/li>/gi, '\n')
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
    
    // Use simpler mailto format without from address
    const mailtoLink = `mailto:${client.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoLink;
    
    // Update last contact timestamp
    updateLastContact(client.id);
    
    // Track sent template
    trackSentTemplate(client.id, template.id, template.name, 'email');
    
    setShowEmailTemplateModal(false);
    setSelectedClientForTemplate(null);
    
    // Reopen client info dialog if email template was opened from there
    if (emailTemplateFromClientInfo) {
      setEmailTemplateFromClientInfo(false);
      // Reopen the client info dialog
      handleClientNameClick(client);
    }
  };

  const handleSendTextWithTemplate = (client: Client, template: TextTemplate) => {
    const rawBody = replaceTemplateVariables(template.body, client, compRates, userProfile);
    
    // Convert HTML to plain text while preserving paragraph breaks
    const plainTextBody = rawBody
      .replace(/<\/div>/gi, '\n')
      .replace(/<\/p>/gi, '\n\n')
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<[^>]*>/g, '')
      .replace(/\n{3,}/g, '\n\n') // Limit consecutive newlines to 2
      .trim();
    
    window.location.href = `sms:${client.phone}?body=${encodeURIComponent(plainTextBody)}`;
    
    // Update last contact timestamp
    updateLastContact(client.id);
    
    // Track sent template
    trackSentTemplate(client.id, template.id, template.name, 'text');
    
    setShowTextTemplateModal(false);
    setSelectedClientForTemplate(null);
    
    // Reopen client info dialog if text template was opened from there
    if (textTemplateFromClientInfo) {
      setTextTemplateFromClientInfo(false);
      // Reopen the client info dialog
      handleClientNameClick(client);
    }
  };

  // Add batch send handlers
  const handleBatchSendEmail = (template: EmailTemplate) => {
    const selectedClientsList = clients.filter(c => selectedClients.has(c.id));
    
    if (selectedClientsList.length === 0) return;
    
    // Show confirmation dialog if more than 3 recipients
    if (selectedClientsList.length > 3) {
      const confirmed = confirm(
        `You are about to send emails to ${selectedClientsList.length} recipients.\n\n` +
        `This will take approximately ${Math.ceil(selectedClientsList.length * 1.5)} seconds to generate all email drafts.\n\n` +
        `Do you want to continue?`
      );
      
      if (!confirmed) {
        return;
      }
    }
    
    // Open all emails with delays using anchor elements
    selectedClientsList.forEach((client, index) => {
      setTimeout(() => {
        const subject = replaceTemplateVariables(template.subject, client, compRates, userProfile);
        const bodyHtml = replaceTemplateVariables(template.body, client, compRates, userProfile);
        const body = bodyHtml
          .replace(/<\/p>/gi, '\n\n')
          .replace(/<\/div>/gi, '\n')
          .replace(/<br\s*\/?>/gi, '\n')
          .replace(/<li[^>]*>/gi, '• ')
          .replace(/<\/li>/gi, '\n')
          .replace(/<[^>]*>/g, '')
          .replace(/&nbsp;/g, ' ')
          .replace(/&amp;/g, '&')
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>')
          .replace(/&quot;/g, '"')
          .replace(/\n{3,}/g, '\n\n')
          .trim();
        
        // Create a temporary anchor element with simpler format without from address
        const link = document.createElement('a');
        link.href = `mailto:${client.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        link.target = '_blank';
        link.style.display = 'none';
        document.body.appendChild(link);
        
        // Trigger click
        link.click();
        
        // Clean up immediately
        document.body.removeChild(link);
        
        // Update last contact timestamp
        updateLastContact(client.id);
        
        // Track sent template
        trackSentTemplate(client.id, template.id, template.name, 'email');
      }, index * 1500); // Increased delay to 1.5 seconds between each email
    });
    
    setShowBatchEmailModal(false);
    setSelectedClients(new Set());
  };

  const handleBatchSendText = (template: TextTemplate) => {
    const selectedClientsList = clients.filter(c => selectedClients.has(c.id));
    
    if (selectedClientsList.length === 0) return;
    
    // Show confirmation dialog if more than 3 recipients
    if (selectedClientsList.length > 3) {
      const confirmed = confirm(
        `You are about to send text messages to ${selectedClientsList.length} recipients.\n\n` +
        `This will take approximately ${Math.ceil(selectedClientsList.length * 1.5)} seconds to generate all message drafts.\n\n` +
        `Do you want to continue?`
      );
      
      if (!confirmed) {
        return;
      }
    }
    
    // Open all SMS with delays using anchor elements
    selectedClientsList.forEach((client, index) => {
      setTimeout(() => {
        const rawBody = replaceTemplateVariables(template.body, client, compRates, userProfile);
        
        // Convert HTML to plain text while preserving paragraph breaks
        const plainTextBody = rawBody
          .replace(/<\/div>/gi, '\n')
          .replace(/<\/p>/gi, '\n\n')
          .replace(/<br\s*\/?>/gi, '\n')
          .replace(/<[^>]*>/g, '')
          .replace(/\n{3,}/g, '\n\n') // Limit consecutive newlines to 2
          .trim();
        
        // Create a temporary anchor element
        const link = document.createElement('a');
        link.href = `sms:${client.phone}?body=${encodeURIComponent(plainTextBody)}`;
        link.target = '_blank';
        link.style.display = 'none';
        document.body.appendChild(link);
        
        // Trigger click
        link.click();
        
        // Clean up immediately
        document.body.removeChild(link);
        
        // Update last contact timestamp
        updateLastContact(client.id);
        
        // Track sent template
        trackSentTemplate(client.id, template.id, template.name, 'text');
      }, index * 1500); // Increased delay to 1.5 seconds between each SMS
    });
    
    setShowBatchTextModal(false);
    setSelectedClients(new Set());
  };

  const toggleClientSelection = (clientId: string) => {
    const newSelected = new Set(selectedClients);
    if (newSelected.has(clientId)) {
      newSelected.delete(clientId);
    } else {
      newSelected.add(clientId);
    }
    setSelectedClients(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedClients.size === sortedAndFilteredClients.length) {
      setSelectedClients(new Set());
    } else {
      setSelectedClients(new Set(sortedAndFilteredClients.map(c => c.id)));
    }
  };

  const handleClientNameClick = async (client: Client) => {
    // Load all data BEFORE showing the dialog
    const [notesData, templatesData] = await Promise.all([
      supabase
        .from('client_notes')
        .select('*')
        .eq('client_id', client.id)
        .order('created_at', { ascending: false }),
      supabase
        .from('sent_templates')
        .select('*')
        .eq('client_id', client.id)
        .order('sent_at', { ascending: false })
    ]);

    // Set all the data
    setClientInfoDialogClient(client);
    setClientInfoEmail(client.email);
    setClientInfoPhone(client.phone);
    setClientInfoNotes(client.notes || '');
    setEditingClientInfoEmail(false);
    setEditingClientInfoPhone(false);
    setEditingClientInfoNotes(false);
    
    if (notesData.data) {
      setClientNotes(notesData.data);
    }
    
    if (templatesData.data) {
      setSentTemplates(templatesData.data);
    }
    
    setNewNoteText('');
    setEditingNoteId(null);
    setEditingNoteText('');
    
    // Load discovery calls
    await loadDiscoveryCalls(client.id);
    
    // NOW show the dialog
    setShowClientInfoDialog(true);
  };

  const loadClientNotes = async (clientId: string) => {
    const { data, error } = await supabase
      .from('client_notes')
      .select('*')
      .eq('client_id', clientId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error loading client notes:', error);
      return;
    }

    if (data) {
      setClientNotes(data);
    }
  };

  const loadDiscoveryCalls = async (clientId: string) => {
    const { data, error } = await supabase
      .from('discovery_calls')
      .select('*')
      .eq('client_id', clientId)
      .order('call_date', { ascending: false });

    if (error) {
      console.error('Error loading discovery calls:', error);
      return;
    }

    if (data) {
      setClientDiscoveryCalls(data);
    }
  };

  const handleOpenDiscoveryCallDialog = (client: Client) => {
    setSelectedClientForDiscovery(client);
    setDiscoveryCallData({
      current_housing: '',
      purchase_timeline: '',
      preapproval_status: '',
      budget_down_payment: '',
      credit_score_range: '',
      employment_status: '',
      additional_notes: '',
      call_date: new Date().toISOString().split('T')[0],
      shopping_reason: '',
      assets: '',
      liabilities: ''
    });
    setMortgagees([
      { name: '', employments: [{ employment_status: '', current_income: '', employer_job_description: '' }], credit_score_range: '' }
    ]);
    setShowDiscoveryCallDialog(true);
  };

  const handleEditDiscoveryCall = (client: Client, call: DiscoveryCall) => {
    setSelectedClientForDiscovery(client);
    setEditingDiscoveryCallId(call.id); // Set the ID of the call being edited
    setDiscoveryCallData({
      current_housing: call.current_housing || '',
      purchase_timeline: call.purchase_timeline || '',
      preapproval_status: call.preapproval_status || '',
      budget_down_payment: call.budget_down_payment || '',
      credit_score_range: call.credit_score_range || '',
      employment_status: call.employment_status || '',
      additional_notes: call.additional_notes || '',
      call_date: call.call_date || new Date().toISOString().split('T')[0],
      shopping_reason: call.shopping_reason || '',
      assets: call.assets || '',
      liabilities: call.liabilities || ''
    });
    // Load mortgagees if they exist
    if (call.mortgagees && call.mortgagees.length > 0) {
      setMortgagees(call.mortgagees);
    } else {
      setMortgagees([
        { name: '', employments: [{ employment_status: '', current_income: '', employer_job_description: '' }], credit_score_range: '' }
      ]);
    }
    setDiscoveryCallFromClientInfo(true);
    setShowClientInfoDialog(false);
    setShowDiscoveryCallDialog(true);
  };

  const handleSaveDiscoveryCall = async () => {
    if (!selectedClientForDiscovery) return;

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    // Include all fields including shopping_reason, assets, and liabilities
    const baseFields = {
      client_id: selectedClientForDiscovery.id,
      current_housing: discoveryCallData.current_housing || null,
      purchase_timeline: discoveryCallData.purchase_timeline || null,
      preapproval_status: discoveryCallData.preapproval_status || null,
      budget_down_payment: discoveryCallData.budget_down_payment || null,
      credit_score_range: discoveryCallData.credit_score_range || null,
      employment_status: discoveryCallData.employment_status || null,
      additional_notes: discoveryCallData.additional_notes || null,
      call_date: discoveryCallData.call_date || new Date().toISOString().split('T')[0],
      shopping_reason: discoveryCallData.shopping_reason || null,
      assets: discoveryCallData.assets || null,
      liabilities: discoveryCallData.liabilities || null,
      mortgagees: mortgagees,
      user_id: session.user.id
    };

    let error;
    
    // Check if we're editing an existing call or creating a new one
    if (editingDiscoveryCallId) {
      // Update existing discovery call
      const updateResult = await supabase
        .from('discovery_calls')
        .update(baseFields)
        .eq('id', editingDiscoveryCallId);
      
      error = updateResult.error;
    } else {
      // Insert new discovery call
      const insertResult = await supabase
        .from('discovery_calls')
        .insert(baseFields);
      
      error = insertResult.error;
    }

    if (error) {
      console.error('Error saving discovery call:', error);
      alert('Failed to save discovery call');
      return;
    }

    // Reload discovery calls for this client
    await loadDiscoveryCalls(selectedClientForDiscovery.id);
    
    // Close the discovery call dialog
    setShowDiscoveryCallDialog(false);
    
    // Reset mortgagees and editing ID
    setMortgagees([
      { name: '', employments: [{ employment_status: '', current_income: '', employer_job_description: '' }], credit_score_range: '' }
    ]);
    setEditingDiscoveryCallId(null);
    
    // If opened from client info dialog, reopen it
    if (discoveryCallFromClientInfo) {
      setDiscoveryCallFromClientInfo(false);
      setShowClientInfoDialog(true);
    } else {
      // If the client info dialog is open for this client, keep it open and refresh the data
      if (clientInfoDialogClient && clientInfoDialogClient.id === selectedClientForDiscovery.id) {
        // Keep the dialog open, the discovery calls have been reloaded
        setSelectedClientForDiscovery(null);
      } else {
        // Close the discovery call dialog
        setSelectedClientForDiscovery(null);
      }
    }
  };

  const handleCancelDiscoveryCall = () => {
    const wasFromClientInfo = discoveryCallFromClientInfo;
    const clientToReopen = selectedClientForDiscovery;
    
    setShowDiscoveryCallDialog(false);
    setSelectedClientForDiscovery(null);
    setDiscoveryCallFromClientInfo(false);
    setEditingDiscoveryCallId(null); // Reset editing ID
    setMortgagees([
      { name: '', employments: [{ employment_status: '', current_income: '', employer_job_description: '' }], credit_score_range: '' }
    ]);
    
    // If opened from client info dialog, reopen it
    if (wasFromClientInfo && clientToReopen) {
      setShowClientInfoDialog(true);
    }
  };

  const addMortgagee = () => {
    setMortgagees([...mortgagees, { name: '', employments: [{ employment_status: '', current_income: '', employer_job_description: '' }], credit_score_range: '' }]);
  };

  const removeMortgagee = (index: number) => {
    if (mortgagees.length > 1) {
      setMortgagees(mortgagees.filter((_, i) => i !== index));
    }
  };

  const updateMortgagee = (index: number, field: keyof Omit<MortgageeData, 'employments'>, value: string) => {
    const updated = [...mortgagees];
    if (field === 'name' || field === 'credit_score_range') {
      updated[index][field] = value;
    }
    setMortgagees(updated);
  };

  const addEmployment = (mortgageeIndex: number) => {
    const updated = [...mortgagees];
    updated[mortgageeIndex].employments.push({ employment_status: '', current_income: '', employer_job_description: '' });
    setMortgagees(updated);
  };

  const removeEmployment = (mortgageeIndex: number, employmentIndex: number) => {
    const updated = [...mortgagees];
    if (updated[mortgageeIndex].employments.length > 1) {
      updated[mortgageeIndex].employments = updated[mortgageeIndex].employments.filter((_, i) => i !== employmentIndex);
      setMortgagees(updated);
    }
  };

  const updateEmployment = (mortgageeIndex: number, employmentIndex: number, field: 'employment_status' | 'current_income' | 'employer_job_description', value: string) => {
    const updated = [...mortgagees];
    updated[mortgageeIndex].employments[employmentIndex][field] = value;
    setMortgagees(updated);
  };

  const handleAddNote = async () => {
    if (!clientInfoDialogClient || !newNoteText.trim()) return;

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    const { data, error } = await supabase
      .from('client_notes')
      .insert({
        client_id: clientInfoDialogClient.id,
        note: newNoteText.trim(),
        user_id: session.user.id
      })
      .select()
      .single();

    if (error) {
      console.error('Error adding note:', error);
      alert('Failed to add note');
      return;
    }

    if (data) {
      setClientNotes([data, ...clientNotes]);
      setNewNoteText('');
    }
  };

  const handleEditNote = (note: ClientNote) => {
    setEditingNoteId(note.id);
    setEditingNoteText(note.note);
  };

  const handleSaveNote = async (noteId: string) => {
    if (!editingNoteText.trim()) return;

    const { error } = await supabase
      .from('client_notes')
      .update({
        note: editingNoteText.trim(),
        updated_at: new Date().toISOString()
      })
      .eq('id', noteId);

    if (error) {
      console.error('Error updating note:', error);
      alert('Failed to update note');
      return;
    }

    setClientNotes(clientNotes.map(n => 
      n.id === noteId 
        ? { ...n, note: editingNoteText.trim(), updated_at: new Date().toISOString() }
        : n
    ));
    setEditingNoteId(null);
    setEditingNoteText('');
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!confirm('Are you sure you want to delete this note?')) return;

    const { error } = await supabase
      .from('client_notes')
      .delete()
      .eq('id', noteId);

    if (error) {
      console.error('Error deleting note:', error);
      alert('Failed to delete note');
      return;
    }

    setClientNotes(clientNotes.filter(n => n.id !== noteId));
  };

  const handleCancelEditNote = () => {
    setEditingNoteId(null);
    setEditingNoteText('');
  };

  const handleSaveClientInfo = async () => {
    if (!clientInfoDialogClient) return;

    // Validate phone number only if provided
    if (clientInfoPhone && clientInfoPhone.trim() !== '') {
      const phoneDigits = clientInfoPhone.replace(/\D/g, '');
      if (phoneDigits.length !== 10) {
        alert('Please enter a valid 10-digit phone number or leave it empty');
        return;
      }
    }

    // Validate email
    if (!clientInfoEmail || !clientInfoEmail.includes('@')) {
      alert('Please enter a valid email address');
      return;
    }

    // Use .select() so we get back the saved row — if 0 rows matched (e.g. RLS),
    // data will be null and we can catch it instead of silently reverting.
    const { data: savedData, error } = await supabase
      .from('clients')
      .update({
        email: clientInfoEmail,
        phone: clientInfoPhone || ''
      })
      .eq('id', clientInfoDialogClient.id)
      .select()
      .maybeSingle();

    if (error) {
      console.error('Error updating client info:', error);
      alert(`Failed to update client information: ${error.message}`);
      return;
    }

    if (!savedData) {
      // Update matched 0 rows — likely an RLS or ID mismatch issue
      console.error('Update returned no data — 0 rows affected for id:', clientInfoDialogClient.id);
      alert('Save failed: the record could not be updated. Please refresh and try again.');
      return;
    }

    // Use the data confirmed by the database to update local state
    setClients(prev => prev.map(c =>
      c.id === savedData.id ? { ...c, ...savedData } : c
    ));

    setClientInfoDialogClient({ ...clientInfoDialogClient, ...savedData });
    setClientInfoPhone(savedData.phone || '');
    setClientInfoEmail(savedData.email || '');

    setEditingClientInfoEmail(false);
    setEditingClientInfoPhone(false);
  };

  const handleClientInfoPhoneChange = (value: string) => {
    // Always slice to first 10 digits so typing/pasting over an existing number works
    const digits = value.replace(/\D/g, '').slice(0, 10);
    let formattedValue = digits;
    if (digits.length >= 6) {
      formattedValue = `(${digits.slice(0, 3)})-${digits.slice(3, 6)}-${digits.slice(6)}`;
    } else if (digits.length >= 3) {
      formattedValue = `(${digits.slice(0, 3)})-${digits.slice(3)}`;
    }
    setClientInfoPhone(formattedValue);
  };

  const handleEditFollowUpDate = (clientId: string, currentDate: string) => {
    setEditingFollowUpId(clientId);
    setEditingFollowUpDate(currentDate || '');
  };

  const handleSaveFollowUpDate = async (clientId: string) => {
    const { error } = await supabase
      .from('clients')
      .update({ follow_up_date: editingFollowUpDate || null })
      .eq('id', clientId);

    if (error) {
      console.error('Error updating follow-up date:', error);
      alert('Failed to update follow-up date');
      return;
    }

    setClients(clients.map(c => 
      c.id === clientId ? { ...c, follow_up_date: editingFollowUpDate || '' } : c
    ));

    setEditingFollowUpId(null);
    setEditingFollowUpDate('');
  };

  const handleCancelFollowUpEdit = () => {
    setEditingFollowUpId(null);
    setEditingFollowUpDate('');
  };

  const handleClearFollowUpDate = async (clientId: string) => {
    if (!confirm('Are you sure you want to clear this follow-up date?')) return;

    try {
      const { error } = await supabase
        .from('clients')
        .update({ follow_up_date: null })
        .eq('id', clientId);

      if (error) {
        console.error('Error clearing follow-up date:', error);
        alert('Failed to clear follow-up date. Please try again.');
        return;
      }

      // Update local state
      setClients(prevClients => prevClients.map(c => 
        c.id === clientId ? { ...c, follow_up_date: '' } : c
      ));

      // If the client info dialog is open for this client, update it too
      if (clientInfoDialogClient && clientInfoDialogClient.id === clientId) {
        setClientInfoDialogClient({
          ...clientInfoDialogClient,
          follow_up_date: ''
        });
      }
    } catch (error) {
      console.error('Unexpected error clearing follow-up date:', error);
      alert('An unexpected error occurred. Please refresh the page and try again.');
    }
  };

  const trackSentTemplate = async (clientId: string, templateId: string, templateName: string, templateType: 'email' | 'text') => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    const { data, error } = await supabase.from('sent_templates').insert({
      client_id: clientId,
      template_id: templateId,
      template_name: templateName,
      template_type: templateType,
      user_id: session.user.id
    }).select().single();

    if (error) {
      console.error('Error tracking sent template:', error);
      return;
    }

    // Update the last sent templates map
    if (data) {
      setLastSentTemplates(prev => ({
        ...prev,
        [clientId]: {
          ...prev[clientId],
          [templateType === 'email' ? 'lastEmail' : 'lastText']: data
        }
      }));
    }
  };

  const loadSentTemplates = async (clientId: string) => {
    const { data, error } = await supabase
      .from('sent_templates')
      .select('*')
      .eq('client_id', clientId)
      .order('sent_at', { ascending: false });

    if (error) {
      console.error('Error loading sent templates:', error);
      return;
    }

    if (data) {
      setSentTemplates(data);
    }
  };

  const handleDeleteSentTemplate = async (templateId: string) => {
    if (confirm('Are you sure you want to delete this sent template record?')) {
      const { error } = await supabase
        .from('sent_templates')
        .delete()
        .eq('id', templateId);

      if (error) {
        console.error('Error deleting sent template:', error);
        alert('Failed to delete sent template');
        return;
      }

      // Reload sent templates
      if (clientInfoDialogClient) {
        loadSentTemplates(clientInfoDialogClient.id);
      }
    }
  };

  const openEmailTemplateModal = (client: Client) => {
    setSelectedClientForTemplate(client);
    setShowEmailTemplateModal(true);
  };

  const openTextTemplateModal = (client: Client) => {
    setSelectedClientForTemplate(client);
    setShowTextTemplateModal(true);
  };

  // Get unique values for filter dropdowns
  const getFilterValues = (): string[] => {
    if (!selectedFilterField) return [];
    
    switch (selectedFilterField) {
      case 'last_email_sent':
        // Sort by display_order from email_templates
        const emailTemplateNames = Array.from(new Set(
          Object.values(lastSentTemplates)
            .map(t => t.lastEmail?.template_name)
            .filter(Boolean)
        )) as string[];
        
        // Sort based on template display order
        return emailTemplateNames.sort((a, b) => {
          const templateA = emailTemplates.find(t => t.name === a);
          const templateB = emailTemplates.find(t => t.name === b);
          const orderA = templateA?.display_order ?? 999;
          const orderB = templateB?.display_order ?? 999;
          return orderA - orderB;
        });
      case 'last_text_sent':
        // Sort by display_order from text_templates
        const textTemplateNames = Array.from(new Set(
          Object.values(lastSentTemplates)
            .map(t => t.lastText?.template_name)
            .filter(Boolean)
        )) as string[];
        
        // Sort based on template display order
        return textTemplateNames.sort((a, b) => {
          const templateA = textTemplates.find(t => t.name === a);
          const templateB = textTemplates.find(t => t.name === b);
          const orderA = templateA?.display_order ?? 999;
          const orderB = templateB?.display_order ?? 999;
          return orderA - orderB;
        });
      default:
        return [];
    }
  };

  // Filtering and sorting logic
  const filteredClients = clients.filter(client => {
    // Apply quick filter first - use ORIGINAL state for filtering
    if (quickFilter) {
      const selectedQuickFilter = quickFilterConfigs.find(qf => qf.filter_name === quickFilter);
      if (selectedQuickFilter && !selectedQuickFilter.client_states.includes(originalClientStates[client.id] || client.client_state)) {
        return false;
      }
    }

    // Apply broker filter
    if (brokerFilter && client.broker !== brokerFilter) {
      return false;
    }

    // Apply follow-up only filter
    if (followUpOnlyFilter && !client.follow_up_date) {
      return false;
    }

    // Apply advanced filter
    if (selectedFilterField && selectedFilterValue) {
      switch (selectedFilterField) {
        case 'last_email_sent':
          if (lastSentTemplates[client.id]?.lastEmail?.template_name !== selectedFilterValue) return false;
          break;
        case 'last_text_sent':
          if (lastSentTemplates[client.id]?.lastText?.template_name !== selectedFilterValue) return false;
          break;
      }
    }

    // Search filter
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      const searchableFields = [
        client.name,
        client.first_name || '',
        client.last_name || '',
        client.email,
        client.phone,
        client.state,
        client.purchase_price,
        client.loan_amount || '',
        client.loan_type,
        getLeadSourceDisplay(client),
        client.lender_name || '',
        client.broker,
        client.client_state,
        client.notes || ''
      ];

      const matches = searchableFields.some(field => 
        field.toLowerCase().includes(query)
      );

      if (!matches) {
        return false;
      }
    }

    return true;
  });

  // Sorting logic
  const sortedAndFilteredClients = [...filteredClients].sort((a, b) => {
    // If follow-up only filter is active, sort by follow_up_date ascending
    if (followUpOnlyFilter && sortField === null) {
      const aDate = a.follow_up_date ? new Date(a.follow_up_date + 'T00:00:00').getTime() : 0;
      const bDate = b.follow_up_date ? new Date(b.follow_up_date + 'T00:00:00').getTime() : 0;
      return aDate - bDate;
    }

    if (!sortField) return 0;

    // Handle special sorting for last sent templates
    if (sortField === 'lastText' as any) {
      const aTime = lastSentTemplates[a.id]?.lastText?.sent_at ? new Date(lastSentTemplates[a.id].lastText!.sent_at).getTime() : 0;
      const bTime = lastSentTemplates[b.id]?.lastText?.sent_at ? new Date(lastSentTemplates[b.id].lastText!.sent_at).getTime() : 0;
      return sortDirection === 'asc' ? aTime - bTime : bTime - aTime;
    }

    if (sortField === 'lastEmail' as any) {
      const aTime = lastSentTemplates[a.id]?.lastEmail?.sent_at ? new Date(lastSentTemplates[a.id].lastEmail!.sent_at).getTime() : 0;
      const bTime = lastSentTemplates[b.id]?.lastEmail?.sent_at ? new Date(lastSentTemplates[b.id].lastEmail!.sent_at).getTime() : 0;
      return sortDirection === 'asc' ? aTime - bTime : bTime - aTime;
    }

    let aValue: any = a[sortField];
    let bValue: any = b[sortField];

    // Handle special cases
    if (sortField === 'purchase_price' || sortField === 'loan_amount') {
      aValue = parsePurchasePrice(aValue || '0');
      bValue = parsePurchasePrice(bValue || '0');
    } else if (sortField === 'follow_up_date') {
      // Fix follow-up date sorting
      aValue = a.follow_up_date ? new Date(a.follow_up_date + 'T00:00:00').getTime() : 0;
      bValue = b.follow_up_date ? new Date(b.follow_up_date + 'T00:00:00').getTime() : 0;
    } else if (sortField === 'last_contact') {
      // Fix last contact sorting
      aValue = a.last_contact && a.last_contact !== 'None' ? new Date(a.last_contact).getTime() : 0;
      bValue = b.last_contact && b.last_contact !== 'None' ? new Date(b.last_contact).getTime() : 0;
    } else if (sortField === 'client_state') {
      // Sort by client_state_updated timestamp instead of state name
      aValue = a.client_state_updated ? new Date(a.client_state_updated).getTime() : 0;
      bValue = b.client_state_updated ? new Date(b.client_state_updated).getTime() : 0;
    } else if (sortField === 'lead_source') {
      aValue = getLeadSourceDisplay(a);
      bValue = getLeadSourceDisplay(b);
    }

    // Convert to strings for comparison if not numbers
    if (typeof aValue !== 'number') {
      aValue = String(aValue || '').toLowerCase();
      bValue = String(bValue || '').toLowerCase();
    }

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const handleSort = (field: keyof Client | 'lastText' | 'lastEmail') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field as any);
      setSortDirection('asc');
    }
  };

  const handleFollowUpOnlyToggle = () => {
    setFollowUpOnlyFilter(!followUpOnlyFilter);
    if (!followUpOnlyFilter) {
      // When enabling follow-up only filter, sort by follow_up_date ascending
      setSortField(null);
      setSortDirection('asc');
    }
  };

  const renderSortableHeader = (primaryLabel: string, secondaryLabel: string | null, primaryField: keyof Client, secondaryField: keyof Client | null, columnKey: string) => {
    const isPrimarySorted = sortField === primaryField;
    const isSecondarySorted = secondaryField && sortField === secondaryField;
    
    return (
      <th className={`px-2 py-2 text-center font-semibold relative`} style={{ fontSize: '9.375px', width: `${columnWidths[columnKey]}px`, minWidth: '80px' }}>
        <div 
          className={`cursor-pointer hover:bg-gray-100 transition-colors rounded px-1 py-0.5 ${isPrimarySorted ? 'bg-gray-100' : ''}`}
          onClick={() => handleSort(primaryField)}
        >
          <div className={`flex items-center justify-center gap-1 ${isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-900'}`}>
            <span>{primaryLabel}</span>
            {isPrimarySorted && (
              <i className={`ri-arrow-${sortDirection === 'asc' ? 'up' : 'down'}-s-line text-teal`}></i>
            )}
          </div>
        </div>
        {secondaryLabel && secondaryField && (
          <div 
            className={`cursor-pointer hover:bg-gray-100 transition-colors rounded px-1 py-0.5 mt-0.5 ${isSecondarySorted ? 'bg-gray-100' : ''}`}
            onClick={() => handleSort(secondaryField)}
          >
            <div className={`flex items-center justify-center gap-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              <span>{secondaryLabel}</span>
              {isSecondarySorted && (
                <i className={`ri-arrow-${sortDirection === 'asc' ? 'up' : 'down'}-s-line text-teal`}></i>
              )}
            </div>
          </div>
        )}
        <div
          className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-teal/50 transition-colors"
          onMouseDown={(e) => handleResizeStart(e, columnKey)}
        />
      </th>
    );
  };

  const renderSingleSortableHeader = (label: string, field: keyof Client, colorClass: string, columnKey: string) => {
    const isSorted = sortField === field;
    
    return (
      <th className={`px-2 py-2 text-center font-semibold relative`} style={{ fontSize: '9.375px', width: `${columnWidths[columnKey]}px`, minWidth: '80px' }}>
        <div 
          className={`cursor-pointer hover:bg-gray-100 transition-colors rounded px-1 py-0.5 ${isSorted ? 'bg-gray-100' : ''}`}
          onClick={() => handleSort(field)}
        >
          <div className={`flex items-center justify-center gap-1 ${colorClass}`}>
            <span>{label}</span>
            {isSorted && (
              <i className={`ri-arrow-${sortDirection === 'asc' ? 'up' : 'down'}-s-line text-teal`}></i>
            )}
          </div>
        </div>
        <div
          className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-teal/50 transition-colors"
          onMouseDown={(e) => handleResizeStart(e, columnKey)}
        />
      </th>
    );
  };

  const renderHeader = (label: string, columnKey: string) => {
    return (
      <th 
        className={`px-2 py-2 text-center font-semibold relative ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}
        style={{ fontSize: '9.375px', width: `${columnWidths[columnKey]}px`, minWidth: '80px' }}
      >
        {label}
        <div
          className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-teal/50 transition-colors"
          onMouseDown={(e) => handleResizeStart(e, columnKey)}
        />
      </th>
    );
  };

  const renderSortableTemplateHeader = (label: string, templateType: 'lastText' | 'lastEmail', columnKey: string) => {
    const isSorted = sortField === templateType as any;
    
    return (
      <th className={`px-2 py-2 text-center font-semibold relative`} style={{ fontSize: '9.375px', width: `${columnWidths[columnKey]}px`, minWidth: '80px' }}>
        <div 
          className={`cursor-pointer hover:bg-gray-100 transition-colors rounded px-1 py-0.5 ${isSorted ? 'bg-gray-100' : ''}`}
          onClick={() => handleSort(templateType as any)}
        >
          <div className={`flex items-center justify-center gap-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            <span>{label}</span>
            {isSorted && (
              <i className={`ri-arrow-${sortDirection === 'asc' ? 'up' : 'down'}-s-line text-teal`}></i>
            )}
          </div>
        </div>
        <div
          className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-teal/50 transition-colors"
          onMouseDown={(e) => handleResizeStart(e, columnKey)}
        />
      </th>
    );
  };

  // Get unique values for filters
  const uniqueStates = Array.from(new Set(clients.map(c => c.state).filter(Boolean)));
  const uniqueLoanTypes = Array.from(new Set(clients.map(c => c.loan_type).filter(Boolean)));
  const uniqueLeadSources = Array.from(new Set(clients.map(c => getLeadSourceDisplay(c))));

  // Calculate statistics
  const activeClients = sortedAndFilteredClients;
  const nextStepClients = activeClients.filter(c => c.broker === 'NS');
  const trademarkClients = activeClients.filter(c => c.broker === 'TM');

  const totalActiveAmount = activeClients.reduce((sum, client) => sum + parsePurchasePrice(client.purchase_price), 0);
  const nextStepAmount = nextStepClients.reduce((sum, client) => sum + parsePurchasePrice(client.purchase_price), 0);
  const trademarkAmount = trademarkClients.reduce((sum, client) => sum + parsePurchasePrice(client.purchase_price), 0);

  const totalActiveLoanAmount = activeClients.reduce((sum, client) => sum + parsePurchasePrice(client.loan_amount || '0'), 0);
  const nextStepLoanAmount = nextStepClients.reduce((sum, client) => sum + parsePurchasePrice(client.loan_amount || '0'), 0);
  const trademarkLoanAmount = trademarkClients.reduce((sum, client) => sum + parsePurchasePrice(client.loan_amount || '0'), 0);

  // Check for clients due for follow-up
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const clientsDueForFollowUp = clients.filter(client => {
    // Apply quick filter first - use ORIGINAL state for filtering
    if (quickFilter) {
      const selectedQuickFilter = quickFilterConfigs.find(qf => qf.filter_name === quickFilter);
      if (selectedQuickFilter && !selectedQuickFilter.client_states.includes(originalClientStates[client.id] || client.client_state)) {
        return false;
      }
    }

    // Apply broker filter
    if (brokerFilter && client.broker !== brokerFilter) {
      return false;
    }

    // Check follow-up date
    if (client.follow_up_date) {
      // Use the same string-based comparison as isFollowUpDue
      if (client.follow_up_date.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = client.follow_up_date.split('-').map(Number);
        const followUp = new Date(year, month - 1, day);
        followUp.setHours(6, 0, 0, 0);
        
        const now = new Date();
        return now.getTime() >= followUp.getTime();
      }
      
      const followUpDate = new Date(client.follow_up_date);
      followUpDate.setHours(6, 0, 0, 0);
      
      const now = new Date();
      return now.getTime() >= followUpDate.getTime();
    }
    return false;
  });

  // Check for clients in underwriting for more than 48 hours
  const underwritingClients = clients.filter(client => {
    // Apply quick filter first - use ORIGINAL state for filtering
    if (quickFilter) {
      const selectedQuickFilter = quickFilterConfigs.find(qf => qf.filter_name === quickFilter);
      if (selectedQuickFilter && !selectedQuickFilter.client_states.includes(originalClientStates[client.id] || client.client_state)) {
        return false;
      }
    }

    // Apply broker filter
    if (brokerFilter && client.broker !== brokerFilter) {
      return false;
    }

    if ((client.client_state === 'Submitted to Underwriting' || client.client_state === 'Resubmitted to Underwriting') && client.client_state_updated) {
      const stateUpdatedDate = new Date(client.client_state_updated);
      const hoursSinceUpdate = (Date.now() - stateUpdatedDate.getTime()) / (1000 * 60 * 60);
      return hoursSinceUpdate > 48;
    }
    return false;
  });

  const openCalendarModal = (client: Client) => {
    setSelectedClientForCalendar(client);
    setCalendarAction(null);
    setShowCalendarModal(true);
  };

  const closeCalendarModal = () => {
    setShowCalendarModal(false);
    setSelectedClientForCalendar(null);
    setCalendarAction(null);
    setMeetingTitle('');
    setMeetingDate('');
    setMeetingTime('');
    setMeetingDuration('30');
    setMeetingNotes('');
    setReminderDate('');
    setReminderTime('');
    setReminderNotes('');
  };

  const handleCreateMeeting = () => {
    if (!selectedClientForCalendar) return;

    // Validate meeting date and time
    if (!meetingDate || !meetingTime) {
      alert('Please select both date and time for the meeting');
      return;
    }

    // Parse the meeting date and time
    const meetingDateTime = new Date(`${meetingDate}T${meetingTime}`);
    const durationMinutes = parseInt(meetingDuration);
    const endDateTime = new Date(meetingDateTime.getTime() + durationMinutes * 60000);
    
    const formatICSDateTime = (date: Date) => {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };

    const now = new Date();

    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Loans with Heena//CRM//EN',
      'BEGIN:VEVENT',
      `UID:${Date.now()}@loanswithheena.com`,
      `DTSTAMP:${formatICSDateTime(now)}`,
      `DTSTART:${formatICSDateTime(meetingDateTime)}`,
      `DTEND:${formatICSDateTime(endDateTime)}`,
      `SUMMARY:${meetingTitle || `Meeting with ${selectedClientForCalendar.name}`}`,
      `DESCRIPTION:Meeting with ${selectedClientForCalendar.name}\\nEmail: ${selectedClientForCalendar.email}\\nPhone: ${selectedClientForCalendar.phone}${meetingNotes ? `\\n\\nNotes: ${meetingNotes}` : ''}`,
      'STATUS:CONFIRMED',
      'SEQUENCE:0',
      'END:VEVENT',
      'END:VCALENDAR'
    ].join('\r\n');

    // Create blob and download link
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.setAttribute('download', `meeting-${selectedClientForCalendar.name.replace(/\s+/g, '-')}.ics`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    alert('Calendar invite file downloaded! Open it to add the meeting to Outlook.');
    closeCalendarModal();
  };

  const handleCreateGoogleCalendarMeeting = () => {
    if (!selectedClientForCalendar) return;

    // Validate meeting date and time
    if (!meetingDate || !meetingTime) {
      alert('Please select both date and time for the meeting');
      return;
    }

    // Parse the meeting date and time
    const meetingDateTime = new Date(`${meetingDate}T${meetingTime}`);
    const durationMinutes = parseInt(meetingDuration);
    const endDateTime = new Date(meetingDateTime.getTime() + durationMinutes * 60000);
    
    // Format dates for Google Calendar (yyyyMMddTHHmmss)
    const formatGoogleDateTime = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${year}${month}${day}T${hours}${minutes}${seconds}`;
    };

    const title = meetingTitle || `Meeting with ${selectedClientForCalendar.name}`;
    const description = `Meeting with ${selectedClientForCalendar.name}\nEmail: ${selectedClientForCalendar.email}\nPhone: ${selectedClientForCalendar.phone}${meetingNotes ? `\n\nNotes: ${meetingNotes}` : ''}`;
    
    // Build Google Calendar URL
    const googleCalendarUrl = new URL('https://calendar.google.com/calendar/render');
    googleCalendarUrl.searchParams.append('action', 'TEMPLATE');
    googleCalendarUrl.searchParams.append('text', title);
    googleCalendarUrl.searchParams.append('dates', `${formatGoogleDateTime(meetingDateTime)}/${formatGoogleDateTime(endDateTime)}`);
    googleCalendarUrl.searchParams.append('details', description);
    googleCalendarUrl.searchParams.append('sf', 'true');
    googleCalendarUrl.searchParams.append('output', 'xml');

    // Open Google Calendar in new tab
    window.open(googleCalendarUrl.toString(), '_blank');
    
    closeCalendarModal();
  };

  const handleCreateReminder = async () => {
    if (!selectedClientForCalendar || !reminderDate) {
      alert('Please fill in the reminder date');
      return;
    }

    const reminderDateTime = reminderTime 
      ? `${reminderDate}T${reminderTime}` 
      : `${reminderDate}T09:00`;

    const { error } = await supabase
      .from('clients')
      .update({
        follow_up_date: reminderDate,
        notes: reminderNotes || selectedClientForCalendar.notes
      })
      .eq('id', selectedClientForCalendar.id);

    if (error) {
      console.error('Error setting reminder:', error);
      alert('Failed to set reminder');
      return;
    }

    // Update local state
    setClients(clients.map(c => 
      c.id === selectedClientForCalendar.id 
        ? { ...c, follow_up_date: reminderDate, notes: reminderNotes || c.notes }
        : c
    ));

    alert(`Reminder set for ${new Date(reminderDateTime).toLocaleString()}`);
    closeCalendarModal();
  };

  const toggleHighlight = (clientId: string, color?: 'red' | 'purple') => {
    const newHighlighted = new Set(highlightedClients);
    const newColors = { ...highlightColors };
    
    if (newHighlighted.has(clientId)) {
      // If already highlighted and no color specified, remove highlight
      if (!color) {
        newHighlighted.delete(clientId);
        delete newColors[clientId];
      } else {
        // Update color
        newColors[clientId] = color;
      }
    } else {
      // Add new highlight with color (default to red)
      newHighlighted.add(clientId);
      newColors[clientId] = color || 'red';
    }
    
    setHighlightedClients(newHighlighted);
    setHighlightColors(newColors);
    setShowColorPicker(null);
  };

  const handleDeleteDiscoveryCall = async (callId: string) => {
    if (!confirm('Are you sure you want to delete this discovery call?')) {
      return;
    }

    const { error } = await supabase
      .from('discovery_calls')
      .delete()
      .eq('id', callId);

    if (error) {
      console.error('Error deleting discovery call:', error);
      alert('Failed to delete discovery call');
      return;
    }

    // Reload discovery calls for the current client
    if (clientInfoDialogClient) {
      await loadDiscoveryCalls(clientInfoDialogClient.id);
    }
  };

  // Add currency formatting helper function
  const formatCurrencyInput = (value: string): string => {
    // Remove all non-numeric characters except decimal point
    const numericValue = value.replace(/[^0-9.]/g, '');
    
    // Parse to number and round to whole number
    const number = Math.round(parseFloat(numericValue) || 0);
    
    // Format with comma separator
    if (number === 0 && !value) return '';
    return `$${number.toLocaleString('en-US')}`;
  };

  const parseCurrencyInput = (value: string): string => {
    // Remove dollar sign and commas
    return value.replace(/[$,]/g, '');
  };

  const handleExportDiscoveryCalls = (client: Client, calls: DiscoveryCall[]) => {
    if (calls.length === 0) {
      alert('No discovery calls to export');
      return;
    }

    // Prepare data for Excel
    const exportData: any[] = [];

    calls.forEach((call, index) => {
      // Basic call information
      const baseData: any = {
        '**Call Number**': index + 1,
        '**Client Name**': client.name,
        '**Call Date**': formatDate(call.call_date),
      };

      // Add all discovery call fields
      discoveryCallFields.forEach(field => {
        if (field.field_name !== 'mortgagee_section') {
          const value = call[field.field_name as keyof DiscoveryCall];
          baseData[`**${field.field_label}**`] = value || '-';
        }
      });

      // Add mortgagee information if exists
      if (call.mortgagees && call.mortgagees.length > 0) {
        call.mortgagees.forEach((mortgagee, mIndex) => {
          const mortgageeData = { ...baseData };
          mortgageeData['**Mortgagee Number**'] = mIndex + 1;
          mortgageeData['**Mortgagee Name**'] = mortgagee.name || '-';
          mortgageeData['**Mortgagee Credit Score**'] = mortgagee.credit_score_range || '-';

          // Add employment information
          if (mortgagee.employments && mortgagee.employments.length > 0) {
            mortgagee.employments.forEach((employment, eIndex) => {
              const employmentData = { ...mortgageeData };
              employmentData['**Job Number**'] = eIndex + 1;
              employmentData['**Employment Status**'] = employment.employment_status || '-';
              employmentData['**Current Income**'] = employment.current_income || '-';
              employmentData['**Employer/Job Description**'] = employment.employer_job_description || '-';
              exportData.push(employmentData);
            });
          } else {
            exportData.push(mortgageeData);
          }
        });
      } else {
        exportData.push(baseData);
      }
    });

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);

    // Auto-size columns
    const colWidths = Object.keys(exportData[0] || {}).map(key => ({
      wch: Math.max(key.length, 15)
    }));
    ws['!cols'] = colWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Discovery Calls');

    // Generate filename
    const filename = `${client.name.replace(/\s+/g, '_')}_Discovery_Calls_${new Date().toISOString().split('T')[0]}.xlsx`;

    // Write file with proper options to avoid trust issues
    XLSX.writeFile(wb, filename, { 
      bookType: 'xlsx',
      type: 'binary',
      compression: true
    });
  };

  const handleCopyDiscoveryCalls = (client: Client, calls: DiscoveryCall[]) => {
    if (calls.length === 0) {
      alert('No discovery calls to copy');
      return;
    }

    // Helper function to properly capitalize field names
    const capitalizeFieldName = (fieldName: string): string => {
      return fieldName
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    };

    // Create HTML content with bold labels
    let htmlContent = '<div style="font-family: Arial, sans-serif;">';
    
    calls.forEach((call, callIndex) => {
      if (callIndex > 0) {
        htmlContent += '<hr style="margin: 20px 0; border: 1px solid #ccc;">';
      }
      
      // Add call date
      htmlContent += '<p><strong>Call Date:</strong> ' + new Date(call.call_date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }) + '</p>';

      // Add all discovery call fields from discoveryCallFields
      discoveryCallFields.forEach(field => {
        if (field.field_name !== 'mortgagee_section' && field.field_name !== 'call_date') {
          const value = call[field.field_name as keyof DiscoveryCall];
          if (value && value !== '') {
            htmlContent += `<p><strong>${field.field_label}:</strong> ${value}</p>`;
          }
        }
      });

      // Add shopping_reason if it exists and is not in discoveryCallFields
      if (call.shopping_reason && !discoveryCallFields.some(f => f.field_name === 'shopping_reason')) {
        htmlContent += `<p><strong>Shopping Reason:</strong> ${call.shopping_reason}</p>`;
      }

      // Add assets if it exists and is not in discoveryCallFields
      if (call.assets && !discoveryCallFields.some(f => f.field_name === 'assets')) {
        htmlContent += `<p><strong>Assets:</strong> ${call.assets}</p>`;
      }

      // Add liabilities if it exists and is not in discoveryCallFields
      if (call.liabilities && !discoveryCallFields.some(f => f.field_name === 'liabilities')) {
        htmlContent += `<p><strong>Liabilities:</strong> ${call.liabilities}</p>`;
      }

      // Add mortgagees section if exists
      if (call.mortgagees && call.mortgagees.length > 0) {
        htmlContent += '<p><strong>Mortgagee Information:</strong></p>';
        
        call.mortgagees.forEach((mortgagee: any, index: number) => {
          htmlContent += `<p style="margin-left: 20px;"><strong>Mortgagee ${index + 1}:</strong></p>`;
          
          if (mortgagee.name) {
            htmlContent += `<p style="margin-left: 40px;"><strong>Name:</strong> ${mortgagee.name}</p>`;
          }
          if (mortgagee.credit_score_range) {
            htmlContent += `<p style="margin-left: 40px;"><strong>Credit Score:</strong> ${mortgagee.credit_score_range}</p>`;
          }
          
          if (mortgagee.employments && Array.isArray(mortgagee.employments) && mortgagee.employments.length > 0) {
            htmlContent += '<p style="margin-left: 40px;"><strong>Employment & Income:</strong></p>';
            mortgagee.employments.forEach((job: any, jobIndex: number) => {
              htmlContent += `<p style="margin-left: 60px;"><strong>Job ${jobIndex + 1}:</strong></p>`;
              if (job.employer_job_description) {
                htmlContent += `<p style="margin-left: 80px;"><strong>Employer/Job:</strong> ${job.employer_job_description}</p>`;
              }
              if (job.employment_status) {
                htmlContent += `<p style="margin-left: 80px;"><strong>Employment Status:</strong> ${job.employment_status}</p>`;
              }
              if (job.current_income) {
                htmlContent += `<p style="margin-left: 80px;"><strong>Current Income:</strong> ${job.current_income}</p>`;
              }
            });
          }
        });
      }
    });

    htmlContent += '</div>';

    // Create plain text version as fallback
    let plainText = '';
    
    calls.forEach((call, callIndex) => {
      if (callIndex > 0) {
        plainText += '\n\n---\n\n';
      }
      
      plainText += 'Call Date: ' + new Date(call.call_date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }) + '\n\n';

      // Add all discovery call fields
      discoveryCallFields.forEach(field => {
        if (field.field_name !== 'mortgagee_section' && field.field_name !== 'call_date') {
          const value = call[field.field_name as keyof DiscoveryCall];
          if (value && value !== '') {
            plainText += `${field.field_label}: ${value}\n`;
          }
        }
      });

      // Add shopping_reason if it exists and is not in discoveryCallFields
      if (call.shopping_reason && !discoveryCallFields.some(f => f.field_name === 'shopping_reason')) {
        plainText += `Shopping Reason: ${call.shopping_reason}\n`;
      }

      // Add assets if it exists and is not in discoveryCallFields
      if (call.assets && !discoveryCallFields.some(f => f.field_name === 'assets')) {
        plainText += `Assets: ${call.assets}\n`;
      }

      // Add liabilities if it exists and is not in discoveryCallFields
      if (call.liabilities && !discoveryCallFields.some(f => f.field_name === 'liabilities')) {
        plainText += `Liabilities: ${call.liabilities}\n`;
      }

      if (call.mortgagees && call.mortgagees.length > 0) {
        plainText += '\nMortgagee Information:\n\n';
        
        call.mortgagees.forEach((mortgagee: any, index: number) => {
          plainText += `Mortgagee ${index + 1}:\n`;
          
          if (mortgagee.name) {
            plainText += `  Name: ${mortgagee.name}\n`;
          }
          if (mortgagee.credit_score_range) {
            plainText += `  Credit Score: ${mortgagee.credit_score_range}\n`;
          }
          
          if (mortgagee.employments && Array.isArray(mortgagee.employments) && mortgagee.employments.length > 0) {
            plainText += '  Employment & Income:\n';
            mortgagee.employments.forEach((job: any, jobIndex: number) => {
              plainText += `    Job ${jobIndex + 1}:\n`;
              if (job.employer_job_description) {
                plainText += `      Employer/Job: ${job.employer_job_description}\n`;
              }
              if (job.employment_status) {
                plainText += `      Employment Status: ${job.employment_status}\n`;
              }
              if (job.current_income) {
                plainText += `      Current Income: ${job.current_income}\n`;
              }
            });
          }
          
          plainText += '\n';
        });
      }
    });

    // Method 1: Try using the new Clipboard API with better HTML support
    const tryModernClipboard = async () => {
      try {
        const htmlBlob = new Blob([htmlContent], { type: 'text/html' });
        const textBlob = new Blob([plainText], { type: 'text/plain' });
        
        const clipboardItem = new ClipboardItem({
          'text/html': htmlBlob,
          'text/plain': textBlob
        });

        await navigator.clipboard.write([clipboardItem]);
        return true;
      } catch (err) {
        console.log('Modern clipboard API failed:', err);
        return false;
      }
    };

    // Method 2: Use a temporary textarea with HTML content
    const tryLegacyClipboard = () => {
      try {
        // Create a temporary div with the HTML content
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = htmlContent;
        tempDiv.style.position = 'fixed';
        tempDiv.style.left = '-9999px';
        tempDiv.contentEditable = 'true';
        document.body.appendChild(tempDiv);

        // Select the content
        const range = document.createRange();
        range.selectNodeContents(tempDiv);
        const selection = window.getSelection();
        if (selection) {
          selection.removeAllRanges();
          selection.addRange(range);

          // Copy the selection
          const success = document.execCommand('copy');
          
          // Clean up
          selection.removeAllRanges();
          document.body.removeChild(tempDiv);
          
          return success;
        }
        
        document.body.removeChild(tempDiv);
        return false;
      } catch (err) {
        console.log('Legacy clipboard method failed:', err);
        return false;
      }
    };

    // Method 3: Fallback to plain text
    const fallbackToPlainText = async () => {
      try {
        await navigator.clipboard.writeText(plainText);
        return true;
      } catch (err) {
        console.log('Plain text clipboard failed:', err);
        return false;
      }
    };

    // Try methods in order
    tryModernClipboard().then(success => {
      if (!success) {
        const legacySuccess = tryLegacyClipboard();
        if (!legacySuccess) {
          fallbackToPlainText().then(plainSuccess => {
            if (!plainSuccess) {
              alert('Failed to copy to clipboard. Please try again.');
            }
          });
        }
      }
    });
  };

  const handleExportToExcel = () => {
    // Prepare data for Excel
    const exportData = sortedAndFilteredClients.map(client => ({
      'Client Name': client.name,
      'State': getStateAbbreviation(client.state),
      'Lead Source': getLeadSourceDisplay(client),
      'Purchase Price': client.purchase_price,
      'Loan Amount': client.loan_amount || '-',
      'Lender Name': client.lender_name || '-',
      'Loan Type': client.loan_type || '-',
      'Broker': client.broker,
      'Client State': client.client_state,
      'Last Contact': formatDateTime(client.last_contact),
      'Follow-Up Date': client.follow_up_date ? formatDate(client.follow_up_date) : '-',
      'Last Text Sent': lastSentTemplates[client.id]?.lastText?.template_name || 'None',
      'Last Email Sent': lastSentTemplates[client.id]?.lastEmail?.template_name || 'None'
    }));

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);

    // Auto-size columns
    const colWidths = Object.keys(exportData[0] || {}).map(key => ({
      wch: Math.max(key.length, 15)
    }));
    ws['!cols'] = colWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Active Clients');

    // Generate filename with current date
    const filename = `CRM_Active_Clients_${new Date().toISOString().split('T')[0]}.xlsx`;

    // Write file with .xlsx extension
    XLSX.writeFile(wb, filename);
  };

  const handleBatchStateChange = async () => {
    if (!batchSelectedState) {
      alert('Please select a client state');
      return;
    }

    const selectedClientsList = clients.filter(c => selectedClients.has(c.id));
    
    if (selectedClientsList.length === 0) return;

    // Show confirmation dialog
    const confirmed = confirm(
      `You are about to change the client state for ${selectedClientsList.length} client${selectedClientsList.length > 1 ? 's' : ''} to "${batchSelectedState}".\n\n` +
      `Do you want to continue?`
    );
    
    if (!confirmed) {
      return;
    }

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    const timestamp = getCurrentTimestamp();

    // Update all selected clients
    for (const client of selectedClientsList) {
      // Log state change to history
      await supabase.from('client_state_history').insert({
        client_id: client.id,
        client_name: client.name,
        previous_state: client.client_state,
        new_state: batchSelectedState,
        user_id: session.user.id
      });

      // Update client state
      await supabase
        .from('clients')
        .update({
          client_state: batchSelectedState,
          client_state_updated: timestamp
        })
        .eq('id', client.id);
    }

    // Update local state
    setClients(clients.map(c => 
      selectedClients.has(c.id) 
        ? { ...c, client_state: batchSelectedState, client_state_updated: timestamp }
        : c
    ));

    // Clear selection and close modal
    setSelectedClients(new Set());
    setShowBatchStateModal(false);
    setBatchSelectedState('');
    
    alert(`Successfully updated ${selectedClientsList.length} client${selectedClientsList.length > 1 ? 's' : ''}`);
  };

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-black' : 'bg-gray-50'}`}>
      {/* Navigation */}
      <nav className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} shadow-sm border-b`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 flex items-center justify-center">
              <i className="ri-home-heart-line text-3xl text-navy"></i>
            </div>
            <span className={`text-xl font-bold ${isDarkMode ? 'text-gray-200' : 'text-navy'}`}>{userFullName || 'Loans with Heena'}</span>
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-4">
            <a href="/" className={`${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium whitespace-nowrap cursor-pointer`}>
              <i className="ri-home-5-line mr-2"></i>
              Home
            </a>
            <a href="/crm" className={`${isDarkMode ? 'text-teal' : 'text-navy'} font-semibold whitespace-nowrap cursor-pointer`}>
              <i className="ri-contacts-book-2-line mr-2"></i>
              CRM
            </a>
            <a href="/intake" className={`${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium whitespace-nowrap cursor-pointer`}>
              <i className="ri-inbox-line mr-2"></i>
              Intake
            </a>
            
            {/* Pages Dropdown */}
            <div className="relative group">
              <button className={`${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium whitespace-nowrap cursor-pointer flex items-center gap-1`}>
                <i className="ri-pages-line mr-2"></i>
                Pages
                <i className="ri-arrow-down-s-line"></i>
              </button>
              <div className={`absolute top-full left-0 mt-1 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border rounded-lg shadow-lg py-2 min-w-[200px] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50`}>
                <a href="/closed" className={`block px-4 py-2 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700 hover:text-gray-100' : 'text-gray-600 hover:bg-gray-50 hover:text-navy'} transition-colors cursor-pointer`}>
                  <i className="ri-archive-line mr-2"></i>
                  Closed
                </a>
                <a href="/dead-deals" className={`block px-4 py-2 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700 hover:text-gray-100' : 'text-gray-600 hover:bg-gray-50 hover:text-navy'} transition-colors cursor-pointer`}>
                  <i className="ri-close-circle-line mr-2"></i>
                  Dead Deals
                </a>
                <a href="/on-hold" className={`block px-4 py-2 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700 hover:text-gray-100' : 'text-gray-600 hover:bg-gray-50 hover:text-navy'} transition-colors cursor-pointer`}>
                  <i className="ri-pause-circle-line mr-2"></i>
                  On Hold
                </a>
                <a href="/templates" className={`block px-4 py-2 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700 hover:text-gray-100' : 'text-gray-600 hover:bg-gray-50 hover:text-navy'} transition-colors cursor-pointer`}>
                  <i className="ri-file-text-line mr-2"></i>
                  Templates
                </a>
                <a href="/history" className={`block px-4 py-2 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700 hover:text-gray-100' : 'text-gray-600 hover:bg-gray-50 hover:text-navy'} transition-colors cursor-pointer`}>
                  <i className="ri-history-line mr-2"></i>
                  History
                </a>
                <a href="/admin" className={`block px-4 py-2 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700 hover:text-gray-100' : 'text-gray-600 hover:bg-gray-50 hover:text-navy'} transition-colors cursor-pointer`}>
                  <i className="ri-settings-3-line mr-2"></i>
                  Admin
                </a>
              </div>
            </div>
            
            {/* Dark Mode Toggle */}
            <button
              onClick={toggleDarkMode}
              className={`${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium whitespace-nowrap cursor-pointer`}
              title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              <i className={`${isDarkMode ? 'ri-sun-line' : 'ri-moon-line'} text-xl`}></i>
            </button>
            
            <button
              onClick={handleLogout}
              className="text-red-600 hover:text-red-700 transition-colors font-medium whitespace-nowrap cursor-pointer"
            >
              <i className="ri-logout-box-line mr-2"></i>
              Logout
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-gray-600 hover:text-navy transition-colors cursor-pointer"
          >
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className={`md:hidden border-t ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}
          >
            <div className="px-6 py-4 space-y-3">
              <a href="/" className={`block py-2 ${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium cursor-pointer`}>
                <i className="ri-home-5-line mr-2"></i>
                Home
              </a>
              <a href="/crm" className={`block py-2 ${isDarkMode ? 'text-teal' : 'text-navy'} font-semibold cursor-pointer`}>
                <i className="ri-contacts-book-2-line mr-2"></i>
                CRM
              </a>
              <a href="/intake" className={`block py-2 ${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium cursor-pointer`}>
                <i className="ri-inbox-line mr-2"></i>
                Intake
              </a>
              <a href="/closed" className={`block py-2 ${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium cursor-pointer`}>
                <i className="ri-archive-line mr-2"></i>
                Closed
              </a>
              <a href="/dead-deals" className={`block py-2 ${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium cursor-pointer`}>
                <i className="ri-close-circle-line mr-2"></i>
                Dead Deals
              </a>
              <a href="/on-hold" className={`block py-2 ${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium cursor-pointer`}>
                <i className="ri-pause-circle-line mr-2"></i>
                On Hold
              </a>
              <a href="/templates" className={`block py-2 ${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium cursor-pointer`}>
                <i className="ri-file-text-line mr-2"></i>
                Templates
              </a>
              <a href="/history" className={`block py-2 ${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium cursor-pointer`}>
                <i className="ri-history-line mr-2"></i>
                History
              </a>
              <a href="/admin" className={`block py-2 ${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium cursor-pointer`}>
                <i className="ri-settings-3-line mr-2"></i>
                Admin
              </a>
              <button
                onClick={toggleDarkMode}
                className={`block w-full text-left py-2 ${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium cursor-pointer`}
              >
                <i className={`${isDarkMode ? 'ri-sun-line' : 'ri-moon-line'} mr-2`}></i>
                {isDarkMode ? 'Light Mode' : 'Dark Mode'}
              </button>
              <button
                onClick={handleLogout}
                className="block w-full text-left py-2 text-red-600 hover:text-red-700 transition-colors font-medium cursor-pointer"
              >
                <i className="ri-logout-box-line mr-2"></i>
                Logout
              </button>
            </div>
          </motion.div>
        )}
      </nav>

      {/* Main Content */}
      <div className="max-w-[1750px] mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row items-center justify-between mb-6 gap-4">
          <div className="w-full md:w-auto text-center md:text-left">
            <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-gray-200' : 'text-gray-900'} mb-1`}>Client Management</h1>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} hidden md:block`}>Manage your active client contacts and communications</p>
          </div>
          <div className="flex flex-col md:flex-row items-center gap-3 w-full md:w-auto">
            {selectedClients.size > 0 && (
              <div className="relative group w-full md:w-auto">
                <button className="w-full md:w-auto bg-purple-600 text-white px-5 py-2 rounded-lg font-medium hover:bg-purple-700 transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 text-sm">
                  <i className="ri-mail-send-line text-base"></i>
                  Batch Actions ({selectedClients.size})
                  <i className="ri-arrow-down-s-line"></i>
                </button>
                <div className={`absolute top-full right-0 mt-1 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border rounded-lg shadow-lg py-2 min-w-[200px] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50`}>
                  <button
                    onClick={() => setShowBatchStateModal(true)}
                    className={`w-full text-left px-4 py-2 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700 hover:text-blue-400' : 'text-gray-600 hover:bg-gray-50 hover:text-blue-600'} transition-colors cursor-pointer flex items-center gap-2`}
                  >
                    <i className="ri-refresh-line"></i>
                    Change Client State
                  </button>
                  <button
                    onClick={() => setShowBatchEmailModal(true)}
                    className={`w-full text-left px-4 py-2 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700 hover:text-teal' : 'text-gray-600 hover:bg-gray-50 hover:text-teal'} transition-colors cursor-pointer flex items-center gap-2`}
                  >
                    <i className="ri-mail-line"></i>
                    Send Email Template
                  </button>
                  <button
                    onClick={() => setShowBatchTextModal(true)}
                    className={`w-full text-left px-4 py-2 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700 hover:text-purple-400' : 'text-gray-600 hover:bg-gray-50 hover:text-purple-600'} transition-colors cursor-pointer flex items-center gap-2`}
                  >
                    <i className="ri-message-3-line"></i>
                    Send Text Template
                  </button>
                  <button
                    onClick={() => setSelectedClients(new Set())}
                    className={`w-full text-left px-4 py-2 text-red-600 ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-red-50'} transition-colors cursor-pointer flex items-center gap-2`}
                  >
                    <i className="ri-close-circle-line"></i>
                    Clear Selection
                  </button>
                </div>
              </div>
            )}
            <button
              onClick={handleExportToExcel}
              className={`hidden md:flex w-full md:w-auto ${isDarkMode ? 'bg-green-700 hover:bg-green-800' : 'bg-green-600 hover:bg-green-700'} text-white px-5 py-2 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer items-center justify-center gap-2 text-sm`}
            >
              <i className="ri-file-excel-2-line text-base"></i>
              Export to Excel
            </button>
            <button
              onClick={() => setShowAddClientDialog(true)}
              className={`w-full md:w-auto ${isDarkMode ? 'bg-teal/90 hover:bg-teal' : 'bg-teal hover:bg-teal/90'} text-white px-5 py-2 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 text-sm`}
            >
              <i className="ri-add-line text-base"></i>
              Add New Client
            </button>
            <button
              onClick={() => setShowAddClientDialog(true)}
              className={`w-full md:w-auto ${isDarkMode ? 'bg-green-700 hover:bg-green-800' : 'bg-green-600 hover:bg-green-700'} text-white px-5 py-2 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 text-sm`}
            >
              <i className="ri-phone-line text-base"></i>
              Add New Client with Discovery
            </button>
          </div>
        </div>

        {/* Follow-Up Alert Banners */}
        {clientsDueForFollowUp.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-yellow-900">
                Follow-Up Alerts ({clientsDueForFollowUp.length})
              </h3>
              <button
                onClick={() => setFollowUpAlertsCollapsed(!followUpAlertsCollapsed)}
                className="text-yellow-700 hover:text-yellow-900 transition-colors cursor-pointer flex items-center gap-2 text-sm font-medium"
              >
                {followUpAlertsCollapsed ? (
                  <>
                    <i className="ri-arrow-down-s-line"></i>
                    Expand
                  </>
                ) : (
                  <>
                    <i className="ri-arrow-up-s-line"></i>
                    Collapse
                  </>
                )}
              </button>
            </div>
            {!followUpAlertsCollapsed && (
              <div className="space-y-3">
                {clientsDueForFollowUp.map((client) => (
                  <motion.div
                    key={client.id}
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-yellow-50 border-2 border-yellow-300 rounded-xl p-3"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 flex items-center justify-center bg-yellow-100 rounded-lg">
                        <i className="ri-alarm-warning-line text-lg text-yellow-600"></i>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-sm font-semibold text-yellow-900">
                          Follow-Up Due: {client.name}
                        </h3>
                        <p className="text-xs text-yellow-700">
                          Follow-up date: {new Date(client.follow_up_date!).toLocaleDateString()} • 
                          {client.notes && ` Notes: ${client.notes}`}
                        </p>
                      </div>
                      <button
                        onClick={() => handleClientNameClick(client)}
                        className="bg-yellow-600 text-white px-3 py-1.5 rounded-lg font-medium hover:bg-yellow-700 transition-colors whitespace-nowrap cursor-pointer text-xs"
                      >
                        View Details
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Underwriting Review Alert Banners */}
        {underwritingClients.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-yellow-900">
                Underwriting Review Alerts ({underwritingClients.length})
              </h3>
              <button
                onClick={() => setUnderwritingAlertsCollapsed(!underwritingAlertsCollapsed)}
                className="text-yellow-700 hover:text-yellow-900 transition-colors cursor-pointer flex items-center gap-2 text-sm font-medium"
              >
                {underwritingAlertsCollapsed ? (
                  <>
                    <i className="ri-arrow-down-s-line"></i>
                    Expand
                  </>
                ) : (
                  <>
                    <i className="ri-arrow-up-s-line"></i>
                    Collapse
                  </>
                )}
              </button>
            </div>
            {!underwritingAlertsCollapsed && (
              <div className="space-y-3">
                {underwritingClients.map((client) => (
                  <motion.div
                    key={client.id}
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-yellow-50 border-2 border-yellow-300 rounded-xl p-3"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 flex items-center justify-center bg-yellow-100 rounded-lg">
                        <i className="ri-alarm-warning-line text-lg text-yellow-600"></i>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-sm font-semibold text-yellow-900">
                          Underwriting Review Needed: {client.name}
                        </h3>
                        <p className="text-xs text-yellow-700">
                          Client has been in "{client.client_state}" for more than 48 hours • 
                          Last updated: {client.client_state_updated} • 
                          Please create a ticket for underwriting review
                        </p>
                      </div>
                      <button
                        onClick={() => handleClientNameClick(client)}
                        className="bg-yellow-600 text-white px-3 py-1.5 rounded-lg font-medium hover:bg-yellow-700 transition-colors whitespace-nowrap cursor-pointer text-xs"
                      >
                        View Details
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Filter Section - Removed sticky */}
        <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-sm border p-4 pb-4 mb-6`}>
          {/* Desktop Filters - Combined Quick Filters and Broker Filter on same line */}
          <div className="hidden md:block">
            {/* First line with Client State, Broker, Search, and F/U Only */}
            <div className="flex items-center gap-3 flex-wrap">
              {/* Client State Filter - Changed to Dropdown */}
              <label className={`text-xs font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} whitespace-nowrap`}>Client State:</label>
              <select
                value={quickFilter}
                onChange={(e) => setQuickFilter(e.target.value)}
                className={`px-3 py-1.5 border ${isDarkMode ? 'border-gray-600 bg-gray-700 text-gray-200' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-xs cursor-pointer`}
              >
                <option value="">All Clients</option>
                {quickFilterConfigs.map((config) => (
                  <option key={config.id} value={config.filter_name}>{config.filter_name}</option>
                ))}
              </select>
              
              {/* Broker Filter */}
              <label className={`text-xs font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} whitespace-nowrap ml-4`}>Broker:</label>
              {compRates.map((rate) => (
                <button
                  key={rate.id}
                  onClick={() => setBrokerFilter(brokerFilter === rate.broker ? '' : rate.broker)}
                  className={`px-3 py-1.5 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer text-xs ${
                    brokerFilter === rate.broker
                      ? rate.broker === 'NS'
                        ? 'bg-black text-[#54a7dc]'
                        : 'bg-[#f3e8c5] text-[#1c272b]'
                      : isDarkMode
                        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {rate.broker}
                </button>
              ))}

              {/* Search Box and F/U Only Button - Moved to right */}
              <div className="flex items-center gap-3 ml-auto">
                {/* Search Box - Made 25% larger (240px) */}
                <div className="relative w-60">
                  <input
                    type="text"
                    placeholder="Search clients..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className={`w-full px-4 py-2.5 pl-10 border ${isDarkMode ? 'bg-gray-800 border-gray-700 text-gray-200 placeholder-gray-500' : 'border-gray-300 text-gray-900'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm`}
                  />
                  <i className={`ri-search-line absolute left-3 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}></i>
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className={`absolute right-3 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'} transition-colors cursor-pointer`}
                    >
                      <i className="ri-close-line"></i>
                    </button>
                  )}
                </div>

                {/* F/U Only Button - Relabeled */}
                <button
                  onClick={handleFollowUpOnlyToggle}
                  className={`px-3 py-1.5 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer text-xs ${
                    followUpOnlyFilter
                      ? 'bg-teal text-white'
                      : isDarkMode
                        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <i className="ri-calendar-check-line mr-1"></i>
                  Show Follow-Up Only
                </button>
              </div>
            </div>

            {/* Second line with Advanced Filter and Clear All Filters */}
            <div className="flex items-center gap-3 flex-wrap mt-3">
              {/* Advanced Filter */}
              <label className={`text-xs font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} whitespace-nowrap`}>Advanced:</label>
              <select
                value={selectedFilterField}
                onChange={(e) => {
                  setSelectedFilterField(e.target.value);
                  setSelectedFilterValue('');
                }}
                className={`px-3 py-1.5 border ${isDarkMode ? 'border-gray-600 bg-gray-700 text-gray-200' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-xs cursor-pointer`}
              >
                <option value="">Select Field...</option>
                <option value="last_email_sent">Last Email Sent</option>
                <option value="last_text_sent">Last Text Sent</option>
              </select>
              {selectedFilterField && (
                <select
                  value={selectedFilterValue}
                  onChange={(e) => setSelectedFilterValue(e.target.value)}
                  className={`px-3 py-1.5 border ${isDarkMode ? 'border-gray-600 bg-gray-700 text-gray-200' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-xs cursor-pointer`}
                >
                  <option value="">Select Value...</option>
                  {getFilterValues().map((value) => (
                    <option key={value} value={value}>{value}</option>
                  ))}
                </select>
              )}

              {/* Clear All Filters - Moved to right of advanced filter */}
              {(searchQuery.trim() !== '' || quickFilter || brokerFilter || followUpOnlyFilter || selectedFilterField) && (
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setQuickFilter('');
                    setBrokerFilter('');
                    setFollowUpOnlyFilter(false);
                    setSelectedFilterField('');
                    setSelectedFilterValue('');
                  }}
                  className="text-xs text-teal hover:text-teal/80 font-medium whitespace-nowrap cursor-pointer"
                >
                  Clear All Filters
                </button>
              )}
            </div>
          </div>

          {/* Mobile Filters */}
          <div className="md:hidden space-y-4">
            {/* Quick Filter Dropdown */}
            <div>
              <label className={`block text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>Quick Filter:</label>
              <select
                value={quickFilter}
                onChange={(e) => setQuickFilter(e.target.value)}
                className={`w-full px-3 py-2 border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-200' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm cursor-pointer`}
              >
                <option value="">All Clients</option>
                {quickFilterConfigs.map((config) => (
                  <option key={config.id} value={config.filter_name}>{config.filter_name}</option>
                ))}
              </select>
            </div>

            {/* Broker Filter Dropdown */}
            <div>
              <label className={`block text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>Broker:</label>
              <select
                value={brokerFilter}
                onChange={(e) => setBrokerFilter(e.target.value)}
                className={`w-full px-3 py-2 border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-200' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm cursor-pointer`}
              >
                <option value="">All Brokers</option>
                {compRates.map((rate) => (
                  <option key={rate.id} value={rate.broker}>{rate.broker}</option>
                ))}
              </select>
            </div>

            {/* Search Field - Added under broker dropdown */}
            <div>
              <label className={`block text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>Search:</label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search clients..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={`w-full px-4 py-2.5 pl-10 border ${isDarkMode ? 'bg-gray-800 border-gray-700 text-gray-200 placeholder-gray-500' : 'border-gray-300 text-gray-900'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm`}
                />
                <i className={`ri-search-line absolute left-3 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}></i>
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className={`absolute right-3 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'} transition-colors cursor-pointer`}
                  >
                    <i className="ri-close-line"></i>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Spacing and Client Count */}
        <div className="mb-6 flex items-center justify-end">
          <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Showing {sortedAndFilteredClients.length} active clients
          </div>
        </div>

        {/* Clients Table - Removed sticky header */}
        <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-sm border overflow-hidden`}>
          <div className="overflow-x-auto">
            {/* Desktop Table */}
            <table className="w-full hidden md:table" style={{ fontSize: '9.375px', tableLayout: 'fixed' }}>
              <thead className={`${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'} border-b`}>
                <tr>
                  <th className={`px-2 py-2 text-center font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`} style={{ fontSize: '9.375px', width: '50px' }}>
                    <input
                      type="checkbox"
                      checked={selectedClients.size === sortedAndFilteredClients.length && sortedAndFilteredClients.length > 0}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 text-teal border-gray-300 rounded focus:ring-teal cursor-pointer"
                    />
                  </th>
                  <th className={`px-2 py-2 text-center font-semibold relative ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`} style={{ fontSize: '9.375px', width: `${columnWidths.actions}px`, minWidth: '80px' }}>
                    Actions
                    <div
                      className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-teal/50 transition-colors"
                      onMouseDown={(e) => handleResizeStart(e, 'actions')}
                    />
                  </th>
                  {renderSingleSortableHeader('Client Name', 'name', isDarkMode ? 'text-gray-400' : 'text-gray-600', 'name')}
                  {renderSingleSortableHeader('State', 'state', isDarkMode ? 'text-gray-400' : 'text-gray-600', 'state')}
                  {renderSingleSortableHeader('Lead Source', 'lead_source', isDarkMode ? 'text-gray-400' : 'text-gray-600', 'lead_source')}
                  {renderSortableHeader('Purchase Price', 'Loan Amount', 'purchase_price', 'loan_amount', 'purchase_price')}
                  {renderSortableHeader('Lender Name', 'Loan Type', 'lender_name', 'loan_type', 'lender_name')}
                  {renderSingleSortableHeader('Broker Abbreviation', 'broker', isDarkMode ? 'text-gray-400' : 'text-gray-600', 'broker')}
                  {renderSingleSortableHeader('Client State', 'client_state', isDarkMode ? 'text-gray-400' : 'text-gray-600', 'client_state')}
                  {renderSingleSortableHeader('Last Contact', 'last_contact', isDarkMode ? 'text-gray-400' : 'text-gray-600', 'last_contact')}
                  {renderSingleSortableHeader('Follow-Up Date', 'follow_up_date', isDarkMode ? 'text-gray-400' : 'text-gray-600', 'follow_up_date')}
                  {renderSortableTemplateHeader('Last Text Sent', 'lastText', 'last_text')}
                  {renderSortableTemplateHeader('Last Email Sent', 'lastEmail', 'last_email')}
                </tr>
              </thead>
              <tbody className={`${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'} divide-y`}>
                {sortedAndFilteredClients.map((client, index) => (
                  <tr 
                    key={client.id} 
                    className={`transition-colors ${
                      highlightedClients.has(client.id) 
                        ? highlightColors[client.id] === 'purple' 
                          ? 'bg-purple-200' 
                          : 'bg-red-200'
                        : isDarkMode 
                          ? index % 2 === 0 ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-900 hover:bg-gray-700'
                          : index % 2 === 0 ? 'bg-white hover:bg-gray-50' : 'bg-gray-50 hover:bg-gray-100'
                    }`}
                  >
                    <td className="px-2 py-2 text-center">
                      <input
                        type="checkbox"
                        checked={selectedClients.has(client.id)}
                        onChange={() => toggleClientSelection(client.id)}
                        className="w-4 h-4 text-teal border-gray-300 rounded focus:ring-teal cursor-pointer"
                      />
                    </td>
                    <td className="px-2 py-2">
                      <div className="flex flex-col items-center justify-center gap-1">
                        {/* Top row: Edit, Text Template, Email Template */}
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => handleEdit(client.id)}
                            className="w-5 h-5 flex items-center justify-center bg-[#007AFF]/10 text-[#007AFF] rounded-lg hover:bg-[#007AFF]/20 transition-colors cursor-pointer"
                            title="Edit"
                          >
                            <i className="ri-edit-line text-xs"></i>
                          </button>
                          <button
                            onClick={() => openTextTemplateModal(client)}
                            className="w-5 h-5 flex items-center justify-center bg-[#34C759]/10 text-[#34C759] rounded-lg hover:bg-[#34C759]/20 transition-colors cursor-pointer"
                            title="Send Text with Template"
                          >
                            <i className="ri-message-3-line text-xs"></i>
                          </button>
                          <button
                            onClick={() => openEmailTemplateModal(client)}
                            className="w-5 h-5 flex items-center justify-center bg-[#007AFF]/10 text-[#007AFF] rounded-lg hover:bg-[#007AFF]/20 transition-colors cursor-pointer"
                            title="Send Email with Template"
                          >
                            <i className="ri-mail-line text-xs"></i>
                          </button>
                        </div>
                        {/* Bottom row: Calendar, Highlight, Delete */}
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => openCalendarModal(client)}
                            className="w-5 h-5 flex items-center justify-center bg-[#FF3B30]/10 text-[#FF3B30] rounded-lg hover:bg-[#FF3B30]/20 transition-colors cursor-pointer"
                            title="Calendar"
                          >
                            <i className="ri-calendar-line text-xs"></i>
                          </button>
                          <div className="relative">
                            <button
                              onClick={() => {
                                // Always show color picker on click
                                setShowColorPicker(showColorPicker === client.id ? null : client.id);
                              }}
                              className={`w-5 h-5 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                                highlightedClients.has(client.id)
                                  ? highlightColors[client.id] === 'purple'
                                    ? 'bg-[#AF52DE] text-white hover:bg-[#AF52DE]/90'
                                    : 'bg-[#FF9500] text-white hover:bg-[#FF9500]/90'
                                  : 'bg-[#FF9500]/10 text-[#FF9500] hover:bg-[#FF9500]/20'
                              }`}
                              title={highlightedClients.has(client.id) ? 'Change color or unhighlight' : 'Highlight'}
                            >
                              <i className={`${highlightedClients.has(client.id) ? 'ri-bookmark-fill' : 'ri-bookmark-line'} text-xs`}></i>
                            </button>
                            {showColorPicker === client.id && (
                              <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-2 z-10 flex flex-col gap-1">
                                <button
                                  onClick={() => toggleHighlight(client.id, 'red')}
                                  className="flex items-center gap-2 px-2 py-1 hover:bg-gray-50 rounded text-xs whitespace-nowrap"
                                >
                                  <div className="w-3 h-3 bg-[#FF9500] rounded"></div>
                                  <span className="text-gray-700">Action Item</span>
                                </button>
                                <button
                                  onClick={() => toggleHighlight(client.id, 'purple')}
                                  className="flex items-center gap-2 px-2 py-1 hover:bg-gray-50 rounded text-xs whitespace-nowrap"
                                >
                                  <div className="w-3 h-3 bg-[#AF52DE] rounded"></div>
                                  <span className="text-gray-700">Waiting</span>
                                </button>
                                <button
                                  onClick={() => toggleHighlight(client.id)}
                                  className="flex items-center gap-2 px-2 py-1 hover:bg-gray-50 rounded text-xs whitespace-nowrap text-red-600"
                                >
                                  <i className="ri-close-line"></i>
                                  <span>Remove</span>
                                </button>
                              </div>
                            )}
                          </div>
                          <button
                            onClick={() => handleDelete(client.id)}
                            className="w-5 h-5 flex items-center justify-center bg-[#FF3B30]/10 text-[#FF3B30] rounded-lg hover:bg-[#FF3B30]/20 transition-colors cursor-pointer"
                            title="Delete"
                          >
                            <i className="ri-delete-bin-line text-xs"></i>
                          </button>
                        </div>
                      </div>
                    </td>
                    <td className="px-2 py-2 text-center">
                      <button
                        onClick={() => handleClientNameClick(client)}
                        className="font-medium text-teal hover:text-teal/80 underline cursor-pointer"
                        style={{ fontSize: '9.375px' }}
                      >
                        {client.name}
                      </button>
                    </td>
                    <td className="px-2 py-2 text-center">
                      <span className={`${highlightedClients.has(client.id) ? 'text-black' : isDarkMode ? 'text-gray-300' : 'text-gray-600'}`} style={{ fontSize: '9.375px' }}>{getStateAbbreviation(client.state)}</span>
                    </td>
                    <td className="px-2 py-2 text-center">
                      <div className="flex flex-col items-center">
                        <span className={`${highlightedClients.has(client.id) ? 'text-black' : isDarkMode ? 'text-gray-300' : 'text-gray-600'}`} style={{ fontSize: '9.375px' }}>{getLeadSourceDisplay(client)}</span>
                        {client.realtor_name && (
                          <span className={`${highlightedClients.has(client.id) ? 'text-black' : isDarkMode ? 'text-gray-400' : 'text-gray-500'} italic`} style={{ fontSize: '8px' }}>
                            {client.realtor_name}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-2 py-2 text-center">
                      <div className="space-y-0.5">
                        <div className={`font-medium ${highlightedClients.has(client.id) ? 'text-black' : isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-900'}`} style={{ fontSize: '9.375px' }}>{formatPurchasePrice(client.purchase_price)}</div>
                        <div className={`font-medium ${highlightedClients.has(client.id) ? 'text-black' : isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} style={{ fontSize: '9.375px' }}>{client.loan_amount ? formatPurchasePrice(client.loan_amount) : '-'}</div>
                      </div>
                    </td>
                    <td className="px-2 py-2 text-center">
                      <div className="space-y-0.5">
                        <div className={`${highlightedClients.has(client.id) ? 'text-black' : isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-900'}`} style={{ fontSize: '9.375px' }}>{client.lender_name || '-'}</div>
                        <div className={`${highlightedClients.has(client.id) ? 'text-black' : isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} style={{ fontSize: '9.375px' }}>{client.loan_type || '-'}</div>
                      </div>
                    </td>
                    <td className="px-2 py-2 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full font-medium ${
                          client.broker === 'NS' 
                            ? 'bg-black text-[#54a7dc]' 
                            : 'bg-[#f3e8c5] text-[#1c272b]'
                        }`} style={{ fontSize: '9.375px' }}>
                          {client.broker}
                        </span>
                      </div>
                    </td>
                    <td className="px-2 py-2">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-1 justify-center">
                          <select
                            value={client.client_state}
                            onChange={(e) => handleChange(client.id, 'client_state', e.target.value)}
                            className={`px-1.5 py-0.5 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-[#e6e7eb]' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-teal cursor-pointer text-center`}
                            style={{ fontSize: '9.375px' }}
                          >
                            {clientStateOptions.map((option) => (
                              <option key={option.state_name} value={option.state_name} className="text-center">{option.state_name}</option>
                            ))}
                          </select>
                          <button
                            onClick={() => handleSaveClientState(client.id)}
                            className="w-4 h-4 flex items-center justify-center bg-green-100 text-green-600 rounded hover:bg-green-200 transition-colors cursor-pointer"
                            title="Save"
                          >
                            <i className="ri-save-line" style={{ fontSize: '9.375px' }}></i>
                          </button>
                        </div>
                        <div className="flex justify-center">
                          <span className={`${highlightedClients.has(client.id) ? 'text-black' : 'text-gray-500'}`} style={{ fontSize: '9.375px' }}>
                            Last Updated: {formatDateTime(client.client_state_updated || '-')}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="px-2 py-2 text-center">
                      {(() => {
                        const formatted = formatDateTimeMultiLine(client.last_contact);
                        return (
                          <div className="flex flex-col items-center gap-0.5">
                            <div className="flex items-center gap-1 justify-center">
                              <span className={`${highlightedClients.has(client.id) ? 'text-black' : isDarkMode ? 'text-gray-400' : 'text-gray-900'}`} style={{ fontSize: '9.375px' }}>{formatted.date}</span>
                              <button
                                onClick={() => handleUpdateContact(client.id)}
                                className="w-4 h-4 flex items-center justify-center text-gray-400 hover:text-teal transition-colors cursor-pointer"
                                title="Update to now"
                              >
                                <i className="ri-refresh-line" style={{ fontSize: '9.375px' }}></i>
                              </button>
                            </div>
                            {formatted.time && (
                              <span className={`${highlightedClients.has(client.id) ? 'text-black' : isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} style={{ fontSize: '9.375px' }}>{formatted.time}</span>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-2 py-2 text-center">
                      {client.follow_up_date ? (
                        <div className="flex items-center gap-1 justify-center">
                          <div className="flex flex-1 justify-center">
                            {editingFollowUpId === client.id ? (
                              <div className="flex items-center gap-1 justify-center">
                                <input
                                  type="date"
                                  value={editingFollowUpDate}
                                  onChange={(e) => setEditingFollowUpDate(e.target.value)}
                                  className="px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal text-xs"
                                />
                                <button
                                  onClick={() => handleSaveFollowUpDate(client.id)}
                                  className="w-4 h-4 flex items-center justify-center bg-green-100 text-green-600 rounded hover:bg-green-200 transition-colors cursor-pointer"
                                  title="Save"
                                >
                                  <i className="ri-check-line" style={{ fontSize: '9.375px' }}></i>
                                </button>
                                <button
                                  onClick={handleCancelFollowUpEdit}
                                  className="w-4 h-4 flex items-center justify-center bg-gray-100 text-gray-600 rounded hover:bg-gray-200 transition-colors cursor-pointer"
                                  title="Cancel"
                                >
                                  <i className="ri-close-line" style={{ fontSize: '9.375px' }}></i>
                                </button>
                              </div>
                            ) : (
                              <>
                                <div className="flex flex-col items-center justify-center">
                                  <span className={`font-medium ${
                                    (() => {
                                      const today = new Date();
                                      today.setHours(0, 0, 0, 0);
                                      const followUpDate = new Date(client.follow_up_date + 'T00:00:00');
                                      return followUpDate <= today ? 'text-red-600' : (highlightedClients.has(client.id) ? 'text-black' : isDarkMode ? 'text-gray-400' : 'text-gray-900');
                                    })()
                                  }`} style={{ fontSize: '9.375px' }}>
                                    {(() => {
                                      const date = new Date(client.follow_up_date + 'T00:00:00');
                                      const month = String(date.getMonth() + 1).padStart(2, '0');
                                      const day = String(date.getDate()).padStart(2, '0');
                                      const year = date.getFullYear();
                                      return `${month}/${day}/${year}`;
                                    })()}
                                  </span>
                                  {(() => {
                                    const today = new Date();
                                    today.setHours(0, 0, 0, 0);
                                    const followUpDate = new Date(client.follow_up_date + 'T00:00:00');
                                    return followUpDate <= today ? (
                                      <div className="flex items-center gap-1 mt-1">
                                        <span className="text-red-600" style={{ fontSize: '9.375px' }}>Due</span>
                                        <button
                                          onClick={() => handleEditFollowUpDate(client.id, client.follow_up_date)}
                                          className="w-4 h-4 flex items-center justify-center text-gray-400 hover:text-blue-600 transition-colors cursor-pointer"
                                          title="Edit follow-up date"
                                        >
                                          <i className="ri-edit-line" style={{ fontSize: '9.375px' }}></i>
                                        </button>
                                        <button
                                          onClick={() => handleClearFollowUpDate(client.id)}
                                          className="w-4 h-4 flex items-center justify-center text-gray-400 hover:text-red-600 transition-colors cursor-pointer"
                                          title="Clear follow-up date"
                                        >
                                          <i className="ri-close-circle-line" style={{ fontSize: '9.375px' }}></i>
                                        </button>
                                      </div>
                                    ) : (
                                      <div className="flex items-center gap-1 mt-1">
                                        <button
                                          onClick={() => handleEditFollowUpDate(client.id, client.follow_up_date)}
                                          className="w-4 h-4 flex items-center justify-center text-gray-400 hover:text-blue-600 transition-colors cursor-pointer"
                                          title="Edit follow-up date"
                                        >
                                          <i className="ri-edit-line" style={{ fontSize: '9.375px' }}></i>
                                        </button>
                                        <button
                                          onClick={() => handleClearFollowUpDate(client.id)}
                                          className="w-4 h-4 flex items-center justify-center text-gray-400 hover:text-red-600 transition-colors cursor-pointer"
                                          title="Clear follow-up date"
                                        >
                                          <i className="ri-close-circle-line" style={{ fontSize: '9.375px' }}></i>
                                        </button>
                                      </div>
                                    );
                                  })()}
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => {
                            setSelectedClientForCalendar(client);
                            setCalendarAction('reminder');
                            setShowCalendarModal(true);
                          }}
                          className="text-gray-400 hover:text-teal transition-colors cursor-pointer italic"
                          style={{ fontSize: '9.375px' }}
                          title="Add follow-up date"
                        >
                          None
                        </button>
                      )}
                    </td>
                    <td className="px-2 py-2 text-center">
                      {lastSentTemplates[client.id]?.lastText ? (
                        <div className="flex flex-col items-center">
                          <span className={`font-medium ${highlightedClients.has(client.id) ? 'text-black' : isDarkMode ? 'text-gray-400' : 'text-gray-900'}`} style={{ fontSize: '9.375px' }}>
                            {lastSentTemplates[client.id].lastText!.template_name}
                          </span>
                          <span className={`${highlightedClients.has(client.id) ? 'text-black' : isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} style={{ fontSize: '8px' }}>
                            {formatDateTime(lastSentTemplates[client.id].lastText!.sent_at)}
                          </span>
                        </div>
                      ) : (
                        <span className="text-gray-400 italic" style={{ fontSize: '9.375px' }}>None</span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-center">
                      {lastSentTemplates[client.id]?.lastEmail ? (
                        <div className="flex flex-col items-center">
                          <span className={`font-medium ${highlightedClients.has(client.id) ? 'text-black' : isDarkMode ? 'text-gray-400' : 'text-gray-900'}`} style={{ fontSize: '9.375px' }}>
                            {lastSentTemplates[client.id].lastEmail!.template_name}
                          </span>
                          <span className={`${highlightedClients.has(client.id) ? 'text-black' : isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} style={{ fontSize: '8px' }}>
                            {formatDateTime(lastSentTemplates[client.id].lastEmail!.sent_at)}
                          </span>
                        </div>
                      ) : (
                        <span className="text-gray-400 italic" style={{ fontSize: '9.375px' }}>None</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile View - Shown only on mobile */}
            <div className="md:hidden">
              {sortedAndFilteredClients.map((client, index) => (
                <div
                  key={client.id}
                  className={`p-4 border-b ${
                    highlightedClients.has(client.id)
                      ? highlightColors[client.id] === 'purple'
                        ? 'bg-purple-200'
                        : 'bg-red-200'
                      : isDarkMode
                        ? index % 2 === 0 ? 'bg-gray-800 border-gray-700' : 'bg-gray-900 border-gray-700'
                        : index % 2 === 0 ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  {/* Top line: Client Name and State */}
                  <div className="flex items-center justify-between mb-2 gap-2">
                    <button
                      onClick={() => handleClientNameClick(client)}
                      className="font-semibold text-teal hover:text-teal/80 underline cursor-pointer text-sm truncate flex-1 text-left"
                    >
                      {client.name}
                    </button>
                    <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} flex-shrink-0`}>
                      {getStateAbbreviation(client.state)}
                    </span>
                  </div>

                  {/* Bottom line: Client State Dropdown */}
                  <div className="flex items-center gap-2">
                    <select
                      value={client.client_state}
                      onChange={(e) => handleChange(client.id, 'client_state', e.target.value)}
                      className={`flex-1 px-3 py-2 border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-200' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-xs cursor-pointer min-w-0`}
                    >
                      {clientStateOptions.map((option) => (
                        <option key={option.state_name} value={option.state_name}>{option.state_name}</option>
                      ))}
                    </select>
                    <button
                      onClick={() => handleSaveClientState(client.id)}
                      className="w-8 h-8 flex items-center justify-center bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition-colors cursor-pointer flex-shrink-0"
                      title="Save"
                    >
                      <i className="ri-save-line text-sm"></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-sm border p-6`}>
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 flex items-center justify-center bg-teal/10 rounded-lg">
                <i className="ri-user-line text-2xl text-teal"></i>
              </div>
              <div>
                <p className="text-sm text-gray-600 font-medium">Total Active Clients</p>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Clients:</span>
                <span className="text-2xl font-bold text-gray-900">{activeClients.length}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Purchase Price Total:</span>
                <span className="text-xl font-bold text-teal">{formatCurrency(totalActiveAmount)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Loan Amount Total:</span>
                <span className="text-xl font-bold text-teal">{formatCurrency(totalActiveLoanAmount)}</span>
              </div>
            </div>
          </div>
          
          <div className="bg-black rounded-xl shadow-sm border border-gray-800 p-6">
            <div className="flex items-center gap-4 mb-4">
              <div>
                <p className="text-sm text-[#54a7dc] font-medium">NS (Next Step Lending)</p>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-400">Clients:</span>
                <span className="text-2xl font-bold text-white">{nextStepClients.length}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-400">Purchase Price Total:</span>
                <span className="text-xl font-bold text-[#54a7dc]">{formatCurrency(nextStepAmount)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-400">Loan Amount Total:</span>
                <span className="text-xl font-bold text-[#54a7dc]">{formatCurrency(nextStepLoanAmount)}</span>
              </div>
            </div>
          </div>
          
          <div className="bg-[#f3e8c5] rounded-xl shadow-sm border border-[#e5d9b0] p-6">
            <div className="flex items-center gap-4 mb-4">
              <div>
                <p className="text-sm text-[#1c272b] font-medium">TM (Trademark Financing)</p>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-[#5a5a5a]">Clients:</span>
                <span className="text-2xl font-bold text-[#1c272b]">{trademarkClients.length}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-[#5a5a5a]">Purchase Price Total:</span>
                <span className="text-xl font-bold text-[#1c272b]">{formatCurrency(trademarkAmount)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-[#5a5a5a]">Loan Amount Total:</span>
                <span className="text-xl font-bold text-[#1c272b]">{formatCurrency(trademarkLoanAmount)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Info Box */}
        <div className={`mt-6 ${isDarkMode ? 'bg-blue-900/30 border-blue-700' : 'bg-blue-50 border-blue-200'} border rounded-xl p-4`}>
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 flex items-center justify-center">
              <i className={`ri-information-line text-xl ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}></i>
            </div>
            <div className="flex-1">
              <h4 className={`text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-900'} mb-1`}>About Client Management</h4>
              <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                The "Last Contact" timestamp updates automatically when you click the email or text buttons. You can also manually update it by clicking the refresh icon.
              </p>
              <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                <strong>Broker Email Addresses:</strong><br/>
                • NS (Next Step Lending): heena@nextstep-lending.com<br/>
                • TM (Trademark Financing): heena@trademarkfinancing.com
              </p>
              <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <strong>Note:</strong> Clients with "Closed" status are automatically moved to the Closed page.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Email Template Modal */}
      {showEmailTemplateModal && selectedClientForTemplate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl max-w-xl w-full max-h-[80vh] overflow-y-auto"
          >
            <div className="p-5 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-gray-900">
                  Select Email Template for {selectedClientForTemplate.name}
                </h3>
                <button
                  onClick={() => {
                    setShowEmailTemplateModal(false);
                    setSelectedClientForTemplate(null);
                  }}
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
            </div>
            <div className="p-5 space-y-4">
              {emailTemplates
                .filter(template => 
                  template.broker === 'All' || 
                  template.broker === selectedClientForTemplate.broker ||
                  !template.broker
                )
                .map((template) => (
                <div
                  key={template.id}
                  className="border border-gray-200 rounded-lg p-4 hover:border-teal transition-colors cursor-pointer"
                  onClick={() => handleSendEmailWithTemplate(selectedClientForTemplate, template)}
                >
                  <h4 className="font-semibold text-gray-900 mb-2 text-sm">{template.name}</h4>
                  <p className="text-xs text-gray-600">
                    <strong>Subject:</strong> {replaceTemplateVariables(template.subject, selectedClientForTemplate, compRates, userProfile)}
                  </p>
                </div>
              ))}
              {emailTemplates.filter(template => 
                template.broker === 'All' || 
                template.broker === selectedClientForTemplate.broker ||
                !template.broker
              ).length === 0 && (
                <div className="text-center py-8">
                  <p className="text-gray-500 text-sm">No templates available for this broker</p>
                </div>
              )}
              <div className="pt-4 border-t border-gray-200">
                <a
                  href="/templates"
                  className="text-teal hover:text-teal/80 font-medium text-xs cursor-pointer"
                >
                  <i className="ri-settings-3-line mr-2"></i>
                  Manage Templates
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Text Template Modal */}
      {showTextTemplateModal && selectedClientForTemplate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl max-w-xl w-full max-h-[80vh] overflow-y-auto"
          >
            <div className="p-5 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-gray-900">
                  Select Text Template for {selectedClientForTemplate.name}
                </h3>
                <button
                  onClick={() => {
                    setShowTextTemplateModal(false);
                    setSelectedClientForTemplate(null);
                  }}
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
            </div>
            <div className="p-5 space-y-4">
              {textTemplates
                .filter(template => 
                  template.broker === 'All' || 
                  template.broker === selectedClientForTemplate.broker ||
                  !template.broker
                )
                .map((template) => (
                <div
                  key={template.id}
                  className="border border-gray-200 rounded-lg p-4 hover:border-teal transition-colors cursor-pointer"
                  onClick={() => handleSendTextWithTemplate(selectedClientForTemplate, template)}
                >
                  <h4 className="font-semibold text-gray-900 mb-2 text-sm">{template.name}</h4>
                </div>
              ))}
              {textTemplates.filter(template => 
                template.broker === 'All' || 
                template.broker === selectedClientForTemplate.broker ||
                !template.broker
              ).length === 0 && (
                <div className="text-center py-8">
                  <p className="text-gray-500 text-sm">No templates available for this broker</p>
                </div>
              )}
              <div className="pt-4 border-t border-gray-200">
                <a
                  href="/templates"
                  className="text-teal hover:text-teal/80 font-medium text-xs cursor-pointer"
                >
                  <i className="ri-settings-3-line mr-2"></i>
                  Manage Templates
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Batch Email Template Modal */}
      {showBatchEmailModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl max-w-xl w-full max-h-[80vh] overflow-y-auto"
          >
            <div className="p-5 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-gray-900">
                  Send Email Template to {selectedClients.size} Client{selectedClients.size > 1 ? 's' : ''}
                </h3>
                <button
                  onClick={() => setShowBatchEmailModal(false)}
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
            </div>
            <div className="p-5 space-y-4">
              {emailTemplates.map((template) => (
                <div
                  key={template.id}
                  className="border border-gray-200 rounded-lg p-4 hover:border-teal transition-colors cursor-pointer"
                  onClick={() => handleBatchSendEmail(template)}
                >
                  <h4 className="font-semibold text-gray-900 mb-2 text-sm">{template.name}</h4>
                  <p className="text-xs text-gray-600">
                    <strong>Subject:</strong> {template.subject}
                  </p>
                </div>
              ))}
              {emailTemplates.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-gray-500 text-sm">No templates available</p>
                </div>
              )}
              <div className="pt-4 border-t border-gray-200">
                <a
                  href="/templates"
                  className="text-teal hover:text-teal/80 font-medium text-xs cursor-pointer"
                >
                  <i className="ri-settings-3-line mr-2"></i>
                  Manage Templates
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Batch Text Template Modal */}
      {showBatchTextModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl max-w-xl w-full max-h-[80vh] overflow-y-auto"
          >
            <div className="p-5 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-gray-900">
                  Send Text Template to {selectedClients.size} Client{selectedClients.size > 1 ? 's' : ''}
                </h3>
                <button
                  onClick={() => setShowBatchTextModal(false)}
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
            </div>
            <div className="p-5 space-y-4">
              {textTemplates.map((template) => (
                <div
                  key={template.id}
                  className="border border-gray-200 rounded-lg p-4 hover:border-teal transition-colors cursor-pointer"
                  onClick={() => handleBatchSendText(template)}
                >
                  <h4 className="font-semibold text-gray-900 mb-2 text-sm">{template.name}</h4>
                </div>
              ))}
              {textTemplates.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-gray-500 text-sm">No templates available</p>
                </div>
              )}
              <div className="pt-4 border-t border-gray-200">
                <a
                  href="/templates"
                  className="text-teal hover:text-teal/80 font-medium text-xs cursor-pointer"
                >
                  <i className="ri-settings-3-line mr-2"></i>
                  Manage Templates
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Batch State Change Modal */}
      {showBatchStateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl max-w-md w-full"
          >
            <div className="p-5 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-gray-900">
                  Change Client State for {selectedClients.size} Client{selectedClients.size > 1 ? 's' : ''}
                </h3>
                <button
                  onClick={() => {
                    setShowBatchStateModal(false);
                    setBatchSelectedState('');
                  }}
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
            </div>
            <div className="p-5">
              <p className="text-sm text-gray-700 mb-4">
                Select the new client state for all selected clients:
              </p>
              <div className="mb-4">
                <select
                  value={batchSelectedState}
                  onChange={(e) => setBatchSelectedState(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm cursor-pointer"
                  autoFocus
                >
                  <option value="">Select Client State...</option>
                  {clientStateOptions.map((option) => (
                    <option key={option.state_name} value={option.state_name}>{option.state_name}</option>
                  ))}
                </select>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleBatchStateChange}
                  disabled={!batchSelectedState}
                  className="flex-1 bg-purple-600 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-purple-700 transition-colors whitespace-nowrap cursor-pointer text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <i className="ri-check-line mr-2"></i>
                  Update {selectedClients.size} Client{selectedClients.size > 1 ? 's' : ''}
                </button>
                <button
                  onClick={() => {
                    setShowBatchStateModal(false);
                    setBatchSelectedState('');
                  }}
                  className="flex-1 bg-gray-200 text-gray-700 px-6 py-2.5 rounded-lg font-medium hover:bg-gray-300 transition-colors whitespace-nowrap cursor-pointer text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && clientToClose && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className={`${isDarkMode ? 'bg-[#212936]' : 'bg-white'} rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto`}
          >
            <div className={`p-5 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-10 h-10 flex items-center justify-center ${isDarkMode ? 'bg-green-900/30' : 'bg-green-100'} rounded-full`}>
                  <i className={`ri-check-double-line text-xl ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}></i>
                </div>
                <div>
                  <h3 className={`text-lg font-bold ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>Set Closing Information</h3>
                  <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{clientToClose.name}</p>
                </div>
              </div>
              <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-3`}>
                Please complete all required fields for <strong>{clientToClose.name}</strong>:
              </p>
            </div>
            
            <div className="p-5">
              <div className="space-y-3">
                {/* Closing Date */}
                <div>
                  <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                    Closing Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={closingDate}
                    onChange={(e) => setClosingDate(e.target.value)}
                    className={`w-full px-4 py-2 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-gray-200' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm`}
                  />
                </div>

                {/* Interest Rate */}
                <div>
                  <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                    Interest Rate <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="e.g., 6.5"
                      value={interestRate}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || /^\d*\.?\d*$/.test(value)) {
                          setInterestRate(value);
                        }
                      }}
                      className={`w-full px-4 py-2 pr-8 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-gray-200' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm`}
                    />
                    <span className={`absolute right-3 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} text-sm`}>%</span>
                  </div>
                </div>

                {/* Lender Loan Number */}
                <div>
                  <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                    Lender Loan Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Enter lender loan number"
                    value={lenderLoanNumber}
                    onChange={(e) => setLenderLoanNumber(e.target.value)}
                    className={`w-full px-4 py-2 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-gray-200' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm`}
                  />
                </div>

                {/* Arive Loan Number */}
                <div>
                  <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                    Arive Loan Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Enter arive loan number"
                    value={ariveLoanNumber}
                    onChange={(e) => setAriveLoanNumber(e.target.value)}
                    className={`w-full px-4 py-2 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-gray-200' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm`}
                  />
                </div>

                {/* Broker Comp */}
                <div>
                  <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                    Broker Comp <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., $5,000"
                    value={closingBrokerComp}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      if (value) {
                        const formatted = `$${parseInt(value).toLocaleString()}`;
                        setClosingBrokerComp(formatted);
                      } else {
                        setClosingBrokerComp('');
                      }
                    }}
                    className={`w-full px-4 py-2 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-gray-200' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm`}
                  />
                  <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mt-1`}>
                    {(() => {
                      if (!closingBrokerComp || closingBrokerComp.trim() === '') return 'Enter broker comp to calculate final comp';
                      
                      const client = clients.find(c => c.id === clientToClose?.id);
                      const broker = client?.broker || 'NS';
                      const leadSource = closingLeadSource === 'Other' ? closingCustomLeadSource : closingLeadSource;
                      const calculatedComp = calculateCompFromBrokerComp(closingBrokerComp, broker, leadSource);
                      
                      let percentage = '75%';
                      if (broker === 'TM') {
                        percentage = leadSource === 'TM Leads' ? '70%' : '85%';
                      }
                      
                      return `Final Comp: ${formatCurrency(calculatedComp)} (${percentage} of Broker Comp)`;
                    })()}
                  </p>
                </div>

                {/* Lender Name */}
                <div>
                  <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                    Lender Name <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={closingLenderName}
                    onChange={(e) => {
                      setClosingLenderName(e.target.value);
                      if (e.target.value !== 'Other') {
                        setClosingCustomLender('');
                      }
                    }}
                    className={`w-full px-4 py-2 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-gray-200' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm cursor-pointer`}
                  >
                    {lenderOptions.map((lender) => (
                      <option key={lender} value={lender}>{lender}</option>
                    ))}
                    <option value="Other">Other</option>
                  </select>
                  {closingLenderName === 'Other' && (
                    <input
                      type="text"
                      placeholder="Enter custom lender name"
                      value={closingCustomLender}
                      onChange={(e) => setClosingCustomLender(e.target.value)}
                      className={`w-full px-4 py-2 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-gray-200' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm mt-2`}
                    />
                  )}
                </div>

                {/* Lead Source */}
                <div>
                  <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                    Lead Source <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={closingLeadSource}
                    onChange={(e) => {
                      setClosingLeadSource(e.target.value);
                      if (e.target.value !== 'Other') {
                        setClosingCustomLeadSource('');
                      }
                    }}
                    className={`w-full px-4 py-2 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-gray-200' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm cursor-pointer`}
                  >
                    {realtors.map((realtor) => (
                      <option key={realtor.id} value={realtor.name}>{realtor.name}</option>
                    ))}
                    <option value="Other">Other</option>
                  </select>
                  {closingLeadSource === 'Other' && (
                    <input
                      type="text"
                      placeholder="Enter custom lead source"
                      value={closingCustomLeadSource}
                      onChange={(e) => setClosingCustomLeadSource(e.target.value)}
                      className={`w-full px-4 py-2 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-gray-200' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm mt-2`}
                    />
                  )}
                </div>

                {/* Purchase Price */}
                <div>
                  <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                    Purchase Price <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., $500,000"
                    value={closingPurchasePrice}
                    onChange={(e) => setClosingPurchasePrice(e.target.value)}
                    className={`w-full px-4 py-2 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-gray-200' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm`}
                  />
                </div>

                {/* Loan Amount */}
                <div>
                  <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                    Loan Amount <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., $400,000"
                    value={closingLoanAmount}
                    onChange={(e) => setClosingLoanAmount(e.target.value)}
                    className={`w-full px-4 py-2 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-gray-200' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm`}
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-5">
                <button
                  onClick={handleConfirmClosing}
                  className={`flex-1 ${isDarkMode ? 'bg-green-700 hover:bg-green-800' : 'bg-green-600 hover:bg-green-700'} text-white px-5 py-2 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer text-sm`}
                >
                  <i className="ri-check-line mr-2"></i>
                  Confirm
                </button>
                <button
                  onClick={handleCancelClosing}
                  className={`flex-1 ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'} px-5 py-2 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer text-sm`}
                >
                  Cancel
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* State Prompt Modal */}
      {showStatePromptModal && clientForStatePrompt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 flex items-center justify-center bg-blue-100 rounded-full">
                  <i className="ri-information-line text-xl text-blue-600"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Additional Information Needed</h3>
                  <p className="text-xs text-gray-600">{clientForStatePrompt.name}</p>
                </div>
              </div>
              
              {(() => {
                const client = clients.find(c => c.id === clientForStatePrompt.id);
                const newStateSortOrder = getClientStateSortOrder(clientForStatePrompt.newState);
                const needsState = newStateSortOrder >= 5 && (!client?.state || client.state.trim() === '');
                const needsPurchasePrice = newStateSortOrder >= 5 && (!client?.purchase_price || client.purchase_price.trim() === '');
                const needsLoanAmount = newStateSortOrder >= 5 && (!client?.loan_amount || client.loan_amount.trim() === '');
                const needsLoanType = newStateSortOrder >= 5 && (!client?.loan_type || client.loan_type.trim() === '');
                const needsLeadSource = newStateSortOrder >= 5 && (!client?.lead_source || client.lead_source.trim() === '');
                const needsLenderName = newStateSortOrder >= 5 && (!client?.lender_name || client.lender_name.trim() === '');
                
                return (
                  <>
                    {needsState && (
                      <div className="mb-3">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          State (Required)
                        </label>
                        <input
                          type="text"
                          placeholder="e.g., TX, CA, FL"
                          value={statePromptValue}
                          onChange={(e) => setStatePromptValue(e.target.value.toUpperCase())}
                          maxLength={2}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm uppercase"
                        />
                        <p className="text-xs text-gray-500 mt-1">Enter 2-character state abbreviation</p>
                      </div>
                    )}
                    
                    {needsPurchasePrice && (
                      <div className="mb-3">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Purchase Price (Required)
                        </label>
                        <input
                          type="text"
                          placeholder="e.g., $500,000"
                          value={purchasePricePromptValue}
                          onChange={(e) => setPurchasePricePromptValue(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm"
                        />
                      </div>
                    )}

                    {needsLoanAmount && (
                      <div className="mb-3">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Loan Amount (Required)
                        </label>
                        <input
                          type="text"
                          placeholder="e.g., $400,000"
                          value={loanAmountPromptValue}
                          onChange={(e) => setLoanAmountPromptValue(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm"
                        />
                      </div>
                    )}

                    {needsLoanType && (
                      <div className="mb-3">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Loan Type (Required)
                        </label>
                        <select
                          value={loanTypePromptValue}
                          onChange={(e) => setLoanTypePromptValue(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm cursor-pointer"
                        >
                          <option value="">Select Loan Type</option>
                          {loanTypes.map((type) => (
                            <option key={type.id} value={type.loan_type}>{type.loan_type}</option>
                          ))}
                        </select>
                      </div>
                    )}

                    {needsLeadSource && (
                      <div className="mb-3">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Lead Source (Required)
                        </label>
                        <select
                          value={editDialogLeadSource}
                          onChange={(e) => {
                            if (e.target.value === 'other') {
                              setShowEditLenderModal(true);
                            } else {
                              setEditDialogLeadSource(e.target.value);
                              setEditDialogCustomLeadSource('');
                            }
                          }}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm cursor-pointer"
                        >
                          {realtors.map((realtor) => (
                            <option key={realtor.id} value={realtor.name}>{realtor.name}</option>
                          ))}
                          <option value="other">Other</option>
                        </select>
                      </div>
                    )}

                    {needsLenderName && (
                      <div className="mb-3">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Lender Name (Required)
                        </label>
                        <select
                          value={editDialogLender}
                          onChange={(e) => {
                            if (e.target.value === 'other') {
                              setShowEditLenderModal(true);
                            } else {
                              setEditDialogLender(e.target.value);
                              setEditDialogCustomLender('');
                            }
                          }}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm cursor-pointer"
                        >
                          {lenderOptions.map((lender) => (
                            <option key={lender} value={lender}>{lender}</option>
                          ))}
                          <option value="other">Other</option>
                        </select>
                        {editDialogLender === 'other' && editDialogCustomLender && (
                          <div className="mt-2 text-sm text-gray-600">
                            Custom Lender: <span className="font-medium">{editDialogCustomLender}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                );
              })()}
              
              <div className="flex gap-3">
                <button
                  onClick={handleConfirmStatePrompt}
                  className="flex-1 bg-blue-600 text-white px-5 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer text-sm"
                >
                  <i className="ri-check-line mr-2"></i>
                  Confirm
                </button>
                <button
                  onClick={handleCancelStatePrompt}
                  className="flex-1 bg-gray-200 text-gray-700 px-5 py-2 rounded-lg font-medium hover:bg-gray-300 transition-colors whitespace-nowrap cursor-pointer text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Dead Deal Modal */}
      {showDeadDealModal && clientForDeadDeal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl max-w-sm w-full"
          >
            <div className="p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 flex items-center justify-center bg-red-100 rounded-full">
                  <i className="ri-close-circle-line text-xl text-red-600"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Dead Deal</h3>
                  <p className="text-xs text-gray-600">{clientForDeadDeal.name}</p>
                </div>
              </div>
              <p className="text-sm text-gray-700 mb-3">
                Please add any notes about why this deal didn't work out:
              </p>
              <div className="mb-3">
                <textarea
                  placeholder="Enter notes..."
                  value={deadDealNotes}
                  onChange={(e) => setDeadDealNotes(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm resize-none"
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleConfirmDeadDeal}
                  className="flex-1 bg-red-600 text-white px-5 py-2 rounded-lg font-medium hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer text-sm"
                >
                  <i className="ri-check-line mr-2"></i>
                  Confirm
                </button>
                <button
                  onClick={handleCancelDeadDeal}
                  className="flex-1 bg-gray-200 text-gray-700 px-5 py-2 rounded-lg font-medium hover:bg-gray-300 transition-colors whitespace-nowrap cursor-pointer text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* On Hold Modal */}
      {showOnHoldModal && clientForOnHold && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl max-w-sm w-full"
          >
            <div className="p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 flex items-center justify-center bg-yellow-100 rounded-full">
                  <i className="ri-pause-circle-line text-xl text-yellow-600"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">On Hold</h3>
                  <p className="text-xs text-gray-600">{clientForOnHold.name}</p>
                </div>
              </div>
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quick Select Follow-Up Time
                </label>
                <div className="flex gap-2">
                  <select
                    value={onHoldTimeAmount}
                    onChange={(e) => setOnHoldTimeAmount(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal cursor-pointer"
                    style={{ fontSize: '9.375px' }}
                  >
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(num => (
                      <option key={num} value={num}>{num}</option>
                    ))}
                  </select>
                  <select
                    value={onHoldTimeUnit}
                    onChange={(e) => setOnHoldTimeUnit(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal cursor-pointer"
                    style={{ fontSize: '9.375px' }}
                  >
                    <option value="Day(s)">Day(s)</option>
                    <option value="Week(s)">Week(s)</option>
                    <option value="Month(s)">Month(s)</option>
                    <option value="Year(s)">Year(s)</option>
                  </select>
                </div>
              </div>
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Or Select Specific Date
                </label>
                <input
                  type="date"
                  value={onHoldFollowUpDate}
                  onChange={(e) => setOnHoldFollowUpDate(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm"
                />
              </div>
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes
                </label>
                <textarea
                  placeholder="Enter notes..."
                  value={onHoldNotes}
                  onChange={(e) => setOnHoldNotes(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm resize-none"
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleConfirmOnHold}
                  className="flex-1 bg-yellow-600 text-white px-5 py-2 rounded-lg font-medium hover:bg-yellow-700 transition-colors whitespace-nowrap cursor-pointer text-sm"
                >
                  <i className="ri-check-line mr-2"></i>
                  Confirm
                </button>
                <button
                  onClick={handleCancelOnHold}
                  className="flex-1 bg-gray-200 text-gray-700 px-5 py-2 rounded-lg font-medium hover:bg-gray-300 transition-colors whitespace-nowrap cursor-pointer text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Lender Selection Modal */}
      {showLenderModal && clientForLender && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl max-w-sm w-full"
          >
            <div className="p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 flex items-center justify-center bg-teal/10 rounded-full">
                  <i className="ri-bank-line text-xl text-teal"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Select Lender</h3>
                  <p className="text-xs text-gray-600">{clientForLender.name}</p>
                </div>
              </div>
              <p className="text-sm text-gray-700 mb-3">
                Please select the lender for this pre-approval/VAL:
              </p>
              <div className="mb-3">
                <select
                  value={selectedLender}
                  onChange={(e) => setSelectedLender(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm cursor-pointer"
                >
                  {lenderOptions.map((lender) => (
                    <option key={lender} value={lender}>{lender}</option>
                  ))}
                  <option value="Other">Other</option>
                </select>
              </div>
              {selectedLender === 'Other' && (
                <div className="mb-3">
                  <input
                    type="text"
                    placeholder="Enter lender name"
                    value={customLender}
                    onChange={(e) => setCustomLender(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm"
                  />
                </div>
              )}
              <div className="flex gap-3">
                <button
                  onClick={handleConfirmLender}
                  className="flex-1 bg-teal text-white px-5 py-2 rounded-lg font-medium hover:bg-teal/90 transition-colors whitespace-nowrap cursor-pointer text-sm"
                >
                  <i className="ri-check-line mr-2"></i>
                  Confirm
                </button>
                <button
                  onClick={handleCancelLender}
                  className="flex-1 bg-gray-200 text-gray-700 px-5 py-2 rounded-lg font-medium hover:bg-gray-300 transition-colors whitespace-nowrap cursor-pointer text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Underwriting Lender Selection Modal */}
      {showUnderwritingLenderModal && clientForUnderwritingLender && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl max-w-sm w-full"
          >
            <div className="p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 flex items-center justify-center bg-teal/10 rounded-full">
                  <i className="ri-bank-line text-xl text-teal"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Select Lender</h3>
                  <p className="text-xs text-gray-600">{clientForUnderwritingLender.name}</p>
                </div>
              </div>
              <p className="text-sm text-gray-700 mb-3">
                Please select the lender for underwriting:
              </p>
              <div className="mb-3">
                <select
                  value={underwritingSelectedLender}
                  onChange={(e) => setUnderwritingSelectedLender(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm cursor-pointer"
                >
                  {lenderOptions.map((lender) => (
                    <option key={lender} value={lender}>{lender}</option>
                  ))}
                  <option value="Other">Other</option>
                </select>
              </div>
              {underwritingSelectedLender === 'Other' && (
                <div className="mb-3">
                  <input
                    type="text"
                    placeholder="Enter lender name"
                    value={underwritingCustomLender}
                    onChange={(e) => setUnderwritingCustomLender(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm"
                  />
                </div>
              )}
              <div className="flex gap-3">
                <button
                  onClick={handleConfirmUnderwritingLender}
                  className="flex-1 bg-teal text-white px-5 py-2 rounded-lg font-medium hover:bg-teal/90 transition-colors whitespace-nowrap cursor-pointer text-sm"
                >
                  <i className="ri-check-line mr-2"></i>
                  Confirm
                </button>
                <button
                  onClick={handleCancelUnderwritingLender}
                  className="flex-1 bg-gray-200 text-gray-700 px-5 py-2 rounded-lg font-medium hover:bg-gray-300 transition-colors whitespace-nowrap cursor-pointer text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Edit Client Dialog */}
      {showEditDialog && editingClient && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">Edit Client</h3>
                <button
                  onClick={handleCancelEditDialog}
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Client Name</label>
                  <input
                    type="text"
                    value={editingClient.name}
                    onChange={(e) => handleEditDialogChange('name', e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
                  <input
                    type="text"
                    value={editingClient.first_name || ''}
                    onChange={(e) => handleEditDialogChange('first_name', e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
                  <input
                    type="text"
                    value={editingClient.last_name || ''}
                    onChange={(e) => handleEditDialogChange('last_name', e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                  <input
                    type="email"
                    value={editingClient.email}
                    onChange={(e) => handleEditDialogChange('email', e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                  <input
                    type="tel"
                    value={editingClient.phone}
                    onChange={(e) => handleEditDialogChange('phone', e.target.value)}
                    placeholder="(###)-###-####"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Type to search or enter state..."
                      value={editDialogStateSearch || editingClient.state}
                      onChange={(e) => {
                        const value = e.target.value.toUpperCase();
                        setEditDialogStateSearch(value);
                        // Also update the client state immediately for manual entry
                        handleEditDialogChange('state', value);
                      }}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm"
                    />
                    {editDialogStateSearch && editDialogStateSearch.length > 0 && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto">
                        {US_STATES.filter(state => 
                          state.includes(editDialogStateSearch)
                        ).map((state) => (
                          <button
                            key={state}
                            onClick={() => {
                              handleEditDialogChange('state', state);
                              setEditDialogStateSearch('');
                            }}
                            className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm cursor-pointer"
                          >
                            {state}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {editingClient.state && !editDialogStateSearch && (
                    <div className="mt-2 text-sm text-gray-600">
                      Selected: <span className="font-medium">{editingClient.state}</span>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Purchase Price</label>
                  <input
                    type="text"
                    value={editingClient.purchase_price}
                    onChange={(e) => handleEditDialogChange('purchase_price', e.target.value)}
                    placeholder="$0"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Loan Amount</label>
                  <input
                    type="text"
                    value={editingClient.loan_amount || ''}
                    onChange={(e) => handleEditDialogChange('loan_amount', e.target.value)}
                    placeholder="$0"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Loan Type</label>
                  <select
                    value={editingClient.loan_type}
                    onChange={(e) => handleEditDialogChange('loan_type', e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm cursor-pointer"
                  >
                    {loanTypes.map((type) => (
                      <option key={type.id} value={type.loan_type}>{type.loan_type}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Lead Source</label>
                  <select
                    value={editDialogLeadSource}
                    onChange={(e) => {
                      if (e.target.value === 'other') {
                        setShowLeadSourceRealtorModal(true);
                        setEditingClientIdForLeadSource(editingClient.id);
                      } else {
                        setEditDialogLeadSource(e.target.value);
                        
                        // Check if it's FreeRateUpdate or a realtor
                        if (e.target.value === 'FreeRateUpdate') {
                          handleEditDialogChange('lead_source', 'FreeRateUpdate');
                          handleEditDialogChange('realtor_id', '');
                        } else {
                          // It's a realtor from the list
                          const selectedRealtor = realtors.find(r => r.name === e.target.value);
                          if (selectedRealtor) {
                            handleEditDialogChange('lead_source', 'Realtor');
                            handleEditDialogChange('realtor_id', selectedRealtor.id);
                          } else {
                            handleEditDialogChange('lead_source', e.target.value);
                            handleEditDialogChange('realtor_id', '');
                          }
                        }
                      }
                    }}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm cursor-pointer"
                  >
                    {realtors.map((realtor) => (
                      <option key={realtor.id} value={realtor.name}>{realtor.name}</option>
                    ))}
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Lender Name</label>
                  <select
                    value={editDialogLender}
                    onChange={(e) => {
                      if (e.target.value === 'other') {
                        setShowEditLenderModal(true);
                      } else {
                        setEditDialogLender(e.target.value);
                        setEditDialogCustomLender('');
                      }
                    }}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm cursor-pointer"
                  >
                    {lenderOptions.map((lender) => (
                      <option key={lender} value={lender}>{lender}</option>
                    ))}
                    <option value="other">Other</option>
                  </select>
                  {editDialogLender === 'other' && editDialogCustomLender && (
                    <div className="mt-2 text-sm text-gray-600">
                      Custom Lender: <span className="font-medium">{editDialogCustomLender}</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={handleSaveEditDialog}
                  className="flex-1 bg-teal text-white px-6 py-2.5 rounded-lg font-medium hover:bg-teal/90 transition-colors whitespace-nowrap cursor-pointer text-sm"
                >
                  <i className="ri-check-line mr-2"></i>
                  Save Changes
                </button>
                <button
                  onClick={handleCancelEditDialog}
                  className="flex-1 bg-gray-200 text-gray-700 px-6 py-2.5 rounded-lg font-medium hover:bg-gray-300 transition-colors whitespace-nowrap cursor-pointer text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Lead Source Realtor Modal */}
      {showLeadSourceRealtorModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl max-w-sm w-full"
          >
            <div className="p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 flex items-center justify-center bg-teal/10 rounded-full">
                  <i className="ri-user-add-line text-xl text-teal"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Enter Lead Source Name</h3>
                </div>
              </div>
              <p className="text-sm text-gray-700 mb-3">
                Please enter the lead source name:
              </p>
              <div className="mb-3">
                <input
                  type="text"
                  placeholder="Lead source name"
                  value={customLeadSourceRealtorName}
                  onChange={(e) => setCustomLeadSourceRealtorName(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm"
                  autoFocus
                />
              </div>
              <div className="mb-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={addToRealtorsTable}
                    onChange={(e) => setAddToRealtorsTable(e.target.checked)}
                    className="w-4 h-4 text-teal border-gray-300 rounded focus:ring-teal cursor-pointer"
                  />
                  <span className="text-sm text-gray-700">Add to Realtors Table</span>
                </label>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={async () => {
                    if (!customLeadSourceRealtorName.trim()) {
                      alert('Please enter a lead source name');
                      return;
                    }

                    // If checkbox is checked, add to realtors table
                    if (addToRealtorsTable) {
                      const { data: { session } } = await supabase.auth.getSession();
                      if (session) {
                        // Get max display order
                        const { data: maxOrderData } = await supabase
                          .from('realtors')
                          .select('display_order')
                          .eq('user_id', session.user.id)
                          .order('display_order', { ascending: false })
                          .limit(1);

                        const maxOrder = maxOrderData && maxOrderData.length > 0 ? maxOrderData[0].display_order : 0;

                        const { data: newRealtor, error } = await supabase
                          .from('realtors')
                          .insert([{
                            name: customLeadSourceRealtorName.trim(),
                            email: '',
                            phone: '',
                            state: '',
                            display_order: maxOrder + 1,
                            user_id: session.user.id
                          }])
                          .select()
                          .single();

                        if (error) {
                          console.error('Error adding realtor:', error);
                          alert('Failed to add to Realtors table');
                        } else if (newRealtor) {
                          // Reload realtors
                          loadRealtors();
                          
                          // Set the new realtor as the lead source
                          if (showEditDialog && editingClient) {
                            setEditDialogLeadSource(customLeadSourceRealtorName.trim());
                            handleEditDialogChange('lead_source', customLeadSourceRealtorName.trim());
                            handleEditDialogChange('realtor_id', newRealtor.id);
                          } else {
                            handleChange(editingClientIdForLeadSource, 'lead_source', customLeadSourceRealtorName.trim());
                            handleChange(editingClientIdForLeadSource, 'realtor_id', newRealtor.id);
                          }
                        }
                      }
                    } else {
                      // Just set the lead source without adding to realtors
                      if (showEditDialog && editingClient) {
                        setEditDialogLeadSource(customLeadSourceRealtorName.trim());
                        handleEditDialogChange('lead_source', customLeadSourceRealtorName.trim());
                        handleEditDialogChange('realtor_id', '');
                      } else {
                        handleChange(editingClientIdForLeadSource, 'lead_source', customLeadSourceRealtorName.trim());
                        handleChange(editingClientIdForLeadSource, 'realtor_id', '');
                      }
                    }

                    setShowLeadSourceRealtorModal(false);
                    setCustomLeadSourceRealtorName('');
                    setEditingClientIdForLeadSource('');
                    setAddToRealtorsTable(false);
                  }}
                  className="flex-1 bg-teal text-white px-5 py-2 rounded-lg font-medium hover:bg-teal/90 transition-colors whitespace-nowrap cursor-pointer text-sm"
                >
                  <i className="ri-check-line mr-2"></i>
                  Confirm
                </button>
                <button
                  onClick={() => {
                    setShowLeadSourceRealtorModal(false);
                    setCustomLeadSourceRealtorName('');
                    setEditingClientIdForLeadSource('');
                    setAddToRealtorsTable(false);
                  }}
                  className="flex-1 bg-gray-200 text-gray-700 px-5 py-2 rounded-lg font-medium hover:bg-gray-300 transition-colors whitespace-nowrap cursor-pointer text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Edit Lender Modal */}
      {showEditLenderModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl max-w-sm w-full"
          >
            <div className="p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 flex items-center justify-center bg-teal/10 rounded-full">
                  <i className="ri-bank-line text-xl text-teal"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Enter Lender Name</h3>
                </div>
              </div>
              <p className="text-sm text-gray-700 mb-3">
                Please enter the lender name:
              </p>
              <div className="mb-3">
                <input
                  type="text"
                  placeholder="Lender name"
                  value={customEditLenderName}
                  onChange={(e) => setCustomEditLenderName(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm"
                  autoFocus
                />
              </div>
              <div className="mb-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={addEditLenderToTable}
                    onChange={(e) => setAddEditLenderToTable(e.target.checked)}
                    className="w-4 h-4 text-teal border-gray-300 rounded focus:ring-teal cursor-pointer"
                  />
                  <span className="text-sm text-gray-700">Add to Lender Names Table</span>
                </label>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={async () => {
                    if (!customEditLenderName.trim()) {
                      alert('Please enter a lender name');
                      return;
                    }

                    // If checkbox is checked, add to lender_names table
                    if (addEditLenderToTable) {
                      const { data: { session } } = await supabase.auth.getSession();
                      if (session) {
                        // Get max display order
                        const { data: maxOrderData } = await supabase
                          .from('lender_names')
                          .select('display_order')
                          .order('display_order', { ascending: false })
                          .limit(1);

                        const maxOrder = maxOrderData && maxOrderData.length > 0 ? maxOrderData[0].display_order : 0;

                        const { error } = await supabase
                          .from('lender_names')
                          .insert([{
                            lender_name: customEditLenderName.trim(),
                            display_order: maxOrder + 1
                          }]);

                        if (error) {
                          console.error('Error adding lender name:', error);
                          alert('Failed to add to Lender Names table');
                        } else {
                          // Reload lender names
                          loadLenderNames();
                        }
                      }
                    }

                    // Set the custom lender name
                    setEditDialogLender('Other');
                    setEditDialogCustomLender(customEditLenderName.trim());

                    setShowEditLenderModal(false);
                    setCustomEditLenderName('');
                    setAddEditLenderToTable(false);
                  }}
                  className="flex-1 bg-teal text-white px-5 py-2 rounded-lg font-medium hover:bg-teal/90 transition-colors whitespace-nowrap cursor-pointer text-sm"
                >
                  <i className="ri-check-line mr-2"></i>
                  Confirm
                </button>
                <button
                  onClick={() => {
                    setShowEditLenderModal(false);
                    setCustomEditLenderName('');
                    setAddEditLenderToTable(false);
                  }}
                  className="flex-1 bg-gray-200 text-gray-700 px-5 py-2 rounded-lg font-medium hover:bg-gray-300 transition-colors whitespace-nowrap cursor-pointer text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Client Info Dialog */}
      {showClientInfoDialog && clientInfoDialogClient && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className={`${isDarkMode ? 'bg-[#212936]' : 'bg-white'} rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto`}
          >
            <div className={`p-6 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center justify-between">
                <h3 className={`text-xl font-bold ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>Client Information</h3>
                <div className="flex items-center gap-2">
                  {/* Generate Pre-Approval Button */}
                  <button
                    onClick={() => generatePreApprovalPDF(clientInfoDialogClient, compRates, userProfile)}
                    className={`px-4 py-2 ${isDarkMode ? 'bg-purple-700 hover:bg-purple-800' : 'bg-purple-600 hover:bg-purple-700'} text-white rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer text-sm flex items-center gap-2`}
                    title="Generate Pre-Approval PDF"
                  >
                    <i className="ri-file-pdf-line"></i>
                    Generate Pre-Approval
                  </button>
                  
                  {/* Follow-Up Date Section */}
                  <div className="flex items-center gap-2 mr-4">
                    <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Follow-Up:</span>
                    {clientInfoDialogClient.follow_up_date ? (
                      <div className="flex items-center gap-2">
                        {editingFollowUpId === clientInfoDialogClient.follow_up_date ? (
                          <>
                            <input
                              type="date"
                              value={editingFollowUpDate}
                              onChange={(e) => setEditingFollowUpDate(e.target.value)}
                              className={`px-3 py-1.5 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-gray-200' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm`}
                            />
                            <button
                              onClick={async () => {
                                await handleSaveFollowUpDate(clientInfoDialogClient.id);
                                // Update the dialog client
                                setClientInfoDialogClient({ ...clientInfoDialogClient, follow_up_date: editingFollowUpDate });
                              }}
                              className="w-7 h-7 flex items-center justify-center bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition-colors cursor-pointer"
                              title="Save"
                            >
                              <i className="ri-check-line text-sm"></i>
                            </button>
                            <button
                              onClick={handleCancelFollowUpEdit}
                              className={`w-7 h-7 flex items-center justify-center ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'} rounded-lg hover:bg-gray-200 transition-colors cursor-pointer`}
                              title="Cancel"
                            >
                              <i className="ri-close-line text-sm"></i>
                            </button>
                          </>
                        ) : (
                          <>
                            <span className={`text-sm font-medium ${
                              (() => {
                                const today = new Date();
                                today.setHours(0, 0, 0, 0);
                                const followUpDate = new Date(clientInfoDialogClient.follow_up_date + 'T00:00:00');
                                return followUpDate <= today ? 'text-red-600' : (isDarkMode ? 'text-gray-300' : 'text-gray-900');
                              })()
                            }`}>
                              {(() => {
                                const date = new Date(clientInfoDialogClient.follow_up_date + 'T00:00:00');
                                const month = String(date.getMonth() + 1).padStart(2, '0');
                                const day = String(date.getDate()).padStart(2, '0');
                                const year = date.getFullYear();
                                return `${month}/${day}/${year}`;
                              })()}
                            </span>
                            <button
                              onClick={() => handleEditFollowUpDate(clientInfoDialogClient.id, clientInfoDialogClient.follow_up_date || '')}
                              className={`w-7 h-7 flex items-center justify-center ${isDarkMode ? 'text-gray-400 hover:text-blue-400' : 'text-gray-400 hover:text-blue-600'} transition-colors cursor-pointer`}
                              title="Edit follow-up date"
                            >
                              <i className="ri-edit-line text-sm"></i>
                            </button>
                            <button
                              onClick={async () => {
                                if (confirm('Are you sure you want to clear this follow-up date?')) {
                                  await handleClearFollowUpDate(clientInfoDialogClient.id);
                                  // Update the dialog client
                                  setClientInfoDialogClient({ ...clientInfoDialogClient, follow_up_date: '' });
                                }
                              }}
                              className={`w-7 h-7 flex items-center justify-center ${isDarkMode ? 'text-gray-400 hover:text-red-400' : 'text-gray-400 hover:text-red-600'} transition-colors cursor-pointer`}
                              title="Clear follow-up date"
                            >
                              <i className="ri-close-circle-line text-sm"></i>
                            </button>
                          </>
                        )}
                      </div>
                    ) : (
                      <button
                        onClick={() => {
                          setSelectedClientForCalendar(clientInfoDialogClient);
                          setCalendarAction('reminder');
                          setShowCalendarModal(true);
                          setShowClientInfoDialog(false);
                        }}
                        className={`text-sm ${isDarkMode ? 'text-gray-400 hover:text-teal' : 'text-gray-400 hover:text-teal'} transition-colors cursor-pointer italic`}
                        title="Add follow-up date"
                      >
                        None
                      </button>
                    )}
                  </div>
                  
                  <button
                    onClick={async () => {
                      if (!clientInfoDialogClient) return;
                      
                      // If email or phone is being edited, save those first
                      if (editingClientInfoEmail || editingClientInfoPhone) {
                        await handleSaveClientInfo();
                      }
                      
                      // Save the client state
                      await handleSaveClientState(clientInfoDialogClient.id);
                    }}
                    className={`px-4 py-2 ${isDarkMode ? 'bg-teal/90 hover:bg-teal' : 'bg-teal hover:bg-teal/90'} text-white rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer text-sm flex items-center gap-2`}
                    title="Save Changes"
                  >
                    <i className="ri-save-line"></i>
                    Save
                  </button>
                  <button
                    onClick={async () => {
                      if (!clientInfoDialogClient) return;
                      
                      // If email or phone is being edited, save those first
                      if (editingClientInfoEmail || editingClientInfoPhone) {
                        await handleSaveClientInfo();
                      }
                      
                      // Save the client state
                      await handleSaveClientState(clientInfoDialogClient.id);
                      
                      // Close the dialog
                      setShowClientInfoDialog(false);
                    }}
                    className={`px-4 py-2 ${isDarkMode ? 'bg-green-700 hover:bg-green-800' : 'bg-green-600 hover:bg-green-700'} text-white rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer text-sm flex items-center gap-2`}
                    title="Save and Close"
                  >
                    <i className="ri-save-line"></i>
                    Save & Close
                  </button>
                  <button
                    onClick={() => setShowClientInfoDialog(false)}
                    className={`w-8 h-8 flex items-center justify-center ${isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'} transition-colors cursor-pointer`}
                  >
                    <i className="ri-close-line text-2xl"></i>
                  </button>
                </div>
              </div>
            </div>
            <div className="p-6">
              <div className="mb-4">
                <div className="flex items-center justify-between mb-4 gap-3">
                  <h4 className={`text-lg font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>{clientInfoDialogClient.name}</h4>
                  <div className="flex items-center gap-2">
                    <select
                      value={clientInfoDialogClient.client_state}
                      onChange={async (e) => {
                        const newState = e.target.value;
                        await handleChange(clientInfoDialogClient.id, 'client_state', newState);
                        // Update the dialog client state
                        setClientInfoDialogClient({ ...clientInfoDialogClient, client_state: newState });
                      }}
                      className={`px-3 py-2 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-gray-200' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm cursor-pointer`}
                    >
                      {clientStateOptions.map((option) => (
                        <option key={option.state_name} value={option.state_name}>{option.state_name}</option>
                      ))}
                    </select>
                    <button
                      onClick={() => {
                        setSelectedClientForDiscovery(clientInfoDialogClient);
                        setDiscoveryCallData({
                          current_housing: '',
                          purchase_timeline: '',
                          preapproval_status: '',
                          budget_down_payment: '',
                          credit_score_range: '',
                          employment_status: '',
                          additional_notes: '',
                          call_date: new Date().toISOString().split('T')[0],
                          shopping_reason: '',
                          assets: '',
                          liabilities: ''
                        });
                        setMortgagees([
                          { name: '', employments: [{ employment_status: '', current_income: '', employer_job_description: '' }], credit_score_range: '' }
                        ]);
                        setShowDiscoveryCallDialog(true);
                      }}
                      className={`${isDarkMode ? 'bg-green-700 hover:bg-green-800' : 'bg-green-600 hover:bg-green-700'} text-white px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer text-sm flex items-center gap-2`}
                    >
                      <i className="ri-phone-line"></i>
                      Log Discovery Call
                    </button>
                  </div>
                </div>
              </div>

              {/* Email Field */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <label className={`block text-sm font-bold underline ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Email</label>
                  <div className="flex items-center gap-2">
                    {!editingClientInfoEmail && (
                      <>
                        <button
                          onClick={() => {
                            setShowClientInfoDialog(false);
                            setEmailTemplateFromClientInfo(true);
                            openEmailTemplateModal(clientInfoDialogClient);
                          }}
                          className="text-xs text-teal hover:text-teal/80 font-medium cursor-pointer flex items-center gap-1"
                        >
                          <i className="ri-mail-send-line"></i>
                          Send Template
                        </button>
                        <button
                          onClick={() => setEditingClientInfoEmail(true)}
                          className="text-xs text-teal hover:text-teal/80 font-medium cursor-pointer"
                        >
                          <i className="ri-edit-line mr-1"></i>
                          Edit
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {editingClientInfoEmail ? (
                  <input
                    type="email"
                    value={clientInfoEmail}
                    onChange={(e) => setClientInfoEmail(e.target.value)}
                    className={`w-full px-4 py-2.5 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-gray-200' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm`}
                  />
                ) : (
                  <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>{clientInfoEmail}</p>
                )}
              </div>

              {/* Phone Field */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <label className={`block text-sm font-bold underline ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Phone</label>
                  <div className="flex items-center gap-2">
                    {!editingClientInfoPhone && (
                      <>
                        <button
                          onClick={() => {
                            setShowClientInfoDialog(false);
                            setTextTemplateFromClientInfo(true);
                            openTextTemplateModal(clientInfoDialogClient);
                          }}
                          className="text-xs text-purple-600 hover:text-purple-700 font-medium cursor-pointer flex items-center gap-1"
                        >
                          <i className="ri-message-3-line"></i>
                          Send Template
                        </button>
                        <button
                          onClick={() => {
                            // Strip formatting so the user sees raw digits — easy to select-all and retype
                            setClientInfoPhone(clientInfoPhone.replace(/\D/g, ''));
                            setEditingClientInfoPhone(true);
                          }}
                          className="text-xs text-teal hover:text-teal/80 font-medium cursor-pointer"
                        >
                          <i className="ri-edit-line mr-1"></i>
                          Edit
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {editingClientInfoPhone ? (
                  <input
                    type="tel"
                    value={clientInfoPhone}
                    onChange={(e) => handleClientInfoPhoneChange(e.target.value)}
                    onFocus={(e) => e.target.select()}
                    autoFocus
                    placeholder="Enter 10-digit phone number"
                    className={`w-full px-4 py-2.5 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-gray-200' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm`}
                  />
                ) : (
                  <a 
                    href={`tel:${clientInfoPhone.replace(/\D/g, '')}`}
                    className="text-sm text-teal hover:text-teal/80 font-medium cursor-pointer underline"
                  >
                    {formatPhoneNumber(clientInfoPhone)}
                  </a>
                )}
              </div>

              {/* Notes Field */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <label className={`block text-sm font-bold underline ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Notes</label>
                </div>
                
                {/* Show old notes from clients.notes field if they exist */}
                {clientInfoDialogClient.notes && clientInfoDialogClient.notes.trim() !== '' && (
                  <div className={`mb-4 p-4 ${isDarkMode ? 'bg-yellow-900/30 border-yellow-700' : 'bg-yellow-50 border-yellow-200'} border rounded-lg`}>
                    <div className="flex items-start gap-2 mb-2">
                      <i className={`ri-information-line ${isDarkMode ? 'text-yellow-500' : 'text-yellow-600'} text-lg mt-0.5`}></i>
                      <div className="flex-1">
                        <p className={`text-sm font-semibold ${isDarkMode ? 'text-yellow-400' : 'text-yellow-900'} mb-1`}>Legacy Note Found</p>
                        <p className={`text-xs ${isDarkMode ? 'text-yellow-300' : 'text-yellow-700'} mb-2`}>This note was saved in the old format. You can migrate it to the new notes system below.</p>
                        <div className={`${isDarkMode ? 'bg-[#394150] border-yellow-600' : 'bg-white border-yellow-300'} p-3 rounded border mb-2`}>
                          <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-900'} whitespace-pre-wrap`}>{clientInfoDialogClient.notes}</p>
                        </div>
                        <button
                          onClick={async () => {
                            if (!clientInfoDialogClient.notes) return;
                            
                            const { data: { session } } = await supabase.auth.getSession();
                            if (!session) return;

                            // Add the old note to the new notes system
                            const { data, error } = await supabase
                              .from('client_notes')
                              .insert({
                                client_id: clientInfoDialogClient.id,
                                note: clientInfoDialogClient.notes.trim(),
                                user_id: session.user.id
                              })
                              .select()
                              .single();

                            if (error) {
                              console.error('Error migrating note:', error);
                              alert('Failed to migrate note');
                              return;
                            }

                            if (data) {
                              // Clear the old notes field
                              const { error: updateError } = await supabase
                                .from('clients')
                                .update({ notes: null })
                                .eq('id', clientInfoDialogClient.id);

                              if (updateError) {
                                console.error('Error clearing old note:', updateError);
                              }

                              // Update local state
                              setClientNotes([data, ...clientNotes]);
                              setClientInfoDialogClient({ ...clientInfoDialogClient, notes: '' });
                              setClients(clients.map(c => 
                                c.id === clientInfoDialogClient.id ? { ...c, notes: '' } : c
                              ));
                              
                              alert('Note migrated successfully!');
                            }
                          }}
                          className="bg-yellow-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-yellow-700 transition-colors whitespace-nowrap cursor-pointer text-sm"
                        >
                          <i className="ri-arrow-right-line mr-2"></i>
                          Migrate to New Notes System
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Add New Note with RichTextEditor */}
                <div className="mb-3">
                  <RichTextEditor
                    value={newNoteText}
                    onChange={(value) => setNewNoteText(value)}
                    placeholder="Add a new note with formatting..."
                    className="w-full"
                  />
                  <button
                    onClick={handleAddNote}
                    disabled={!newNoteText.trim()}
                    className="mt-2 bg-teal text-white px-4 py-2 rounded-lg font-medium hover:bg-teal/90 transition-colors whitespace-nowrap cursor-pointer text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <i className="ri-add-line mr-2"></i>
                    Add Note
                  </button>
                </div>

                {/* Notes List */}
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {clientNotes.length > 0 ? (
                    clientNotes.map((note) => (
                      <div
                        key={note.id}
                        className={`border ${isDarkMode ? 'border-gray-700 bg-[#394150]' : 'border-gray-200 bg-white'} rounded-lg p-3`}
                      >
                        {editingNoteId === note.id ? (
                          <div>
                            <RichTextEditor
                              value={editingNoteText}
                              onChange={(value) => setEditingNoteText(value)}
                              placeholder="Edit note..."
                              className="w-full"
                            />
                            <div className="flex gap-2 mt-2">
                              <button
                                onClick={() => handleSaveNote(note.id)}
                                className="bg-green-600 text-white px-3 py-1.5 rounded-lg font-medium hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer text-xs"
                              >
                                <i className="ri-check-line mr-1"></i>
                                Save
                              </button>
                              <button
                                onClick={handleCancelEditNote}
                                className={`${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'} px-3 py-1.5 rounded-lg font-medium hover:bg-gray-300 transition-colors whitespace-nowrap cursor-pointer text-xs`}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div>
                            <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mb-2`}>
                              {formatDateTime(note.created_at)}
                              {note.updated_at && note.updated_at !== note.created_at && (
                                <span className="ml-2">(Updated: {formatDateTime(note.updated_at)})</span>
                              )}
                            </div>
                            <div 
                              className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-900'} mb-2`}
                              dangerouslySetInnerHTML={{ __html: note.note }}
                            />
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => handleEditNote(note)}
                                className="text-blue-600 hover:text-blue-700 transition-colors cursor-pointer text-xs"
                              >
                                <i className="ri-edit-line mr-1"></i>
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteNote(note.id)}
                                className="text-red-600 hover:text-red-700 transition-colors cursor-pointer text-xs"
                              >
                                <i className="ri-delete-bin-line mr-1"></i>
                                Delete
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className={`text-center py-4 ${isDarkMode ? 'bg-[#394150]' : 'bg-white'} rounded-lg border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No notes added yet</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              {(editingClientInfoEmail || editingClientInfoPhone) && (
                <div className="flex gap-3 mb-6">
                  <button
                    onClick={handleSaveClientInfo}
                    className="flex-1 bg-teal text-white px-6 py-2.5 rounded-lg font-medium hover:bg-teal/90 transition-colors whitespace-nowrap cursor-pointer text-sm"
                  >
                    <i className="ri-check-line mr-2"></i>
                    Save Changes
                  </button>
                  <button
                    onClick={() => {
                      setEditingClientInfoEmail(false);
                      setEditingClientInfoPhone(false);
                      setClientInfoEmail(clientInfoDialogClient.email || '');
                      setClientInfoPhone(clientInfoDialogClient.phone || '');
                    }}
                    className={`flex-1 ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'} px-6 py-2.5 rounded-lg font-medium hover:bg-gray-300 transition-colors whitespace-nowrap cursor-pointer text-sm`}
                  >
                    Cancel
                  </button>
                </div>
              )}

              {/* Sent Templates Section */}
              <div className={`pt-6 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <h5 className={`text-sm font-bold underline ${isDarkMode ? 'text-gray-200' : 'text-gray-900'} mb-3`}>Sent Templates History</h5>
                {sentTemplates.length > 0 ? (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {sentTemplates.map((template) => (
                      <div
                        key={template.id}
                        className={`flex items-center justify-between p-3 ${isDarkMode ? 'bg-[#394150]' : 'bg-gray-50'} rounded-lg`}
                      >
                        <div className="flex items-center gap-3 flex-1">
                          <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${
                            template.template_type === 'email' 
                              ? 'bg-teal/10 text-teal' 
                              : 'bg-purple-100 text-purple-600'
                          }`}>
                            <i className={`${
                              template.template_type === 'email' 
                                ? 'ri-mail-line' 
                                : 'ri-message-3-line'
                            } text-sm`}></i>
                          </div>
                          <div className="flex-1">
                            <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>{template.template_name}</p>
                            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {formatDateTime(template.sent_at)}
                            </p>
                          </div>
                          <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                            template.template_type === 'email'
                              ? 'bg-teal/10 text-teal'
                              : 'bg-purple-100 text-purple-600'
                          }`}>
                            {template.template_type === 'email' ? 'Email' : 'Text'}
                          </span>
                        </div>
                        <button
                          onClick={() => handleDeleteSentTemplate(template.id)}
                          className="ml-3 w-7 h-7 flex items-center justify-center bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors cursor-pointer"
                          title="Delete"
                        >
                          <i className="ri-delete-bin-line text-xs"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className={`text-center py-6 ${isDarkMode ? 'bg-[#394150]' : 'bg-gray-50'} rounded-lg`}>
                    <i className={`ri-inbox-line text-3xl ${isDarkMode ? 'text-gray-600' : 'text-gray-300'} mb-2`}></i>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No templates sent yet</p>
                  </div>
                )}
              </div>

              {/* Discovery Calls Section */}
              <div className={`pt-6 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="flex items-center justify-between mb-3">
                  <h5 className={`text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>Discovery Calls</h5>
                  {clientDiscoveryCalls.length > 0 && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleCopyDiscoveryCalls(clientInfoDialogClient, clientDiscoveryCalls)}
                        className={`${isDarkMode ? 'bg-purple-700 hover:bg-purple-800' : 'bg-purple-600 hover:bg-purple-700'} text-white px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer text-sm flex items-center gap-2`}
                      >
                        <i className="ri-file-copy-line"></i>
                        Copy as Text
                      </button>
                      <button
                        onClick={() => handleExportDiscoveryCalls(clientInfoDialogClient, clientDiscoveryCalls)}
                        className={`${isDarkMode ? 'bg-green-700 hover:bg-green-800' : 'bg-green-600 hover:bg-green-700'} text-white px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer text-sm flex items-center gap-2`}
                      >
                        <i className="ri-file-excel-2-line"></i>
                        Export to Excel
                      </button>
                    </div>
                  )}
                </div>
                {clientDiscoveryCalls.length > 0 ? (
                  <div className="space-y-3 max-h-60 overflow-y-auto">
                    {clientDiscoveryCalls.map((call) => (
                      <div
                        key={call.id}
                        className={`border ${isDarkMode ? 'border-gray-700 bg-[#394150]' : 'border-gray-200 bg-gray-50'} rounded-lg p-3`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className={`text-xs font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                            Call Date: {formatDate(call.call_date)}
                          </span>
                          <div className="flex items-center gap-2">
                            <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              Logged: {formatDateTime(call.created_at)}
                            </span>
                            <button
                              onClick={() => handleEditDiscoveryCall(clientInfoDialogClient, call)}
                              className="px-2 py-1 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors cursor-pointer text-xs font-medium"
                              title="View/Edit Discovery Call"
                            >
                              <i className="ri-edit-line mr-1"></i>
                              View/Edit
                            </button>
                            <button
                              onClick={() => handleDeleteDiscoveryCall(call.id)}
                              className="px-2 py-1 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors cursor-pointer text-xs font-medium"
                              title="Delete Discovery Call"
                            >
                              <i className="ri-delete-bin-line mr-1"></i>
                              Delete
                            </button>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div>
                            <span className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Housing:</span>
                            <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} ml-1`}>{call.current_housing || '-'}</span>
                          </div>
                          <div>
                            <span className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Timeline:</span>
                            <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} ml-1`}>{call.purchase_timeline || '-'}</span>
                          </div>
                          <div>
                            <span className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Pre-approval:</span>
                            <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} ml-1`}>{call.preapproval_status || '-'}</span>
                          </div>
                          <div>
                            <span className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Budget:</span>
                            <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} ml-1`}>{call.budget_down_payment || '-'}</span>
                          </div>
                          <div>
                            <span className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Credit:</span>
                            <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} ml-1`}>{call.credit_score_range || '-'}</span>
                          </div>
                          <div>
                            <span className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Employment:</span>
                            <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} ml-1`}>{call.employment_status || '-'}</span>
                          </div>
                          {call.shopping_reason && (
                            <div className="col-span-2">
                              <span className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Shopping Reason:</span>
                              <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} ml-1`}>{call.shopping_reason}</span>
                            </div>
                          )}
                          {call.assets && (
                            <div className="col-span-2">
                              <span className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Assets:</span>
                              <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} ml-1`}>{call.assets}</span>
                            </div>
                          )}
                          {call.liabilities && (
                            <div className="col-span-2">
                              <span className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Liabilities:</span>
                              <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} ml-1`}>{call.liabilities}</span>
                            </div>
                          )}
                        </div>
                        {call.additional_notes && (
                          <div className={`mt-2 pt-2 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                            <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Notes:</span>
                            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mt-1 whitespace-pre-wrap`}>{call.additional_notes}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className={`text-center py-6 ${isDarkMode ? 'bg-[#394150]' : 'bg-gray-50'} rounded-lg`}>
                    <i className={`ri-phone-line text-3xl ${isDarkMode ? 'text-gray-600' : 'text-gray-300'} mb-2`}></i>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No discovery calls logged yet</p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Discovery Call Dialog */}
      {showDiscoveryCallDialog && selectedClientForDiscovery && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className={`${isDarkMode ? 'bg-[#212936]' : 'bg-white'} rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto`}
          >
            <div className={`p-6 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className={`text-xl font-bold ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                    {editingDiscoveryCallId ? 'Edit Discovery Call' : 'Discovery Call'}
                  </h3>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mt-1`}>{selectedClientForDiscovery.name}</p>
                </div>
                <button
                  onClick={handleCancelDiscoveryCall}
                  className={`w-8 h-8 flex items-center justify-center ${isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'} transition-colors cursor-pointer`}
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {discoveryCallFields
                  .filter(field => field.field_name !== 'mortgagee_section')
                  .map((field) => {
                    // Check if this is the field right before mortgagees section
                    // We want to insert mortgagees after "current_housing" field
                    const isFieldBeforeMortgagees = field.field_name === 'current_housing';
                    
                    return (
                      <>
                        <div key={field.id} className={field.field_type === 'textarea' ? 'md:col-span-2' : ''}>
                          <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                            {field.field_label}
                            {field.is_required && <span className="text-red-500 ml-1">*</span>}
                          </label>
                          {field.field_type === 'select' ? (
                            <select
                              value={discoveryCallData[field.field_name as keyof typeof discoveryCallData] || ''}
                              onChange={(e) => setDiscoveryCallData({ ...discoveryCallData, [field.field_name]: e.target.value })}
                              className={`w-full px-4 py-2.5 border ${isDarkMode ? 'bg-[#394150] border-gray-600 text-gray-200' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm cursor-pointer`}
                              required={field.is_required}
                            >
                              <option value="">Select...</option>
                              {field.dropdown_options?.map((option) => (
                                <option key={option} value={option}>{option}</option>
                              ))}
                            </select>
                          ) : field.field_type === 'textarea' ? (
                            <textarea
                              value={discoveryCallData[field.field_name as keyof typeof discoveryCallData] || ''}
                              onChange={(e) => setDiscoveryCallData({ ...discoveryCallData, [field.field_name]: e.target.value })}
                              placeholder={`Enter ${field.field_label.toLowerCase()}...`}
                              rows={4}
                              className={`w-full px-4 py-2.5 border ${isDarkMode ? 'bg-[#394150] border-gray-600 text-gray-200' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm resize-none`}
                              required={field.is_required}
                            />
                          ) : field.field_type === 'date' ? (
                            <input
                              type="date"
                              value={discoveryCallData[field.field_name as keyof typeof discoveryCallData] || ''}
                              onChange={(e) => setDiscoveryCallData({ ...discoveryCallData, [field.field_name]: e.target.value })}
                              className={`w-full px-4 py-2.5 border ${isDarkMode ? 'bg-[#394150] border-gray-600 text-gray-200' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm`}
                              required={field.is_required}
                            />
                          ) : field.field_name === 'budget_down_payment' ? (
                            <input
                              type="text"
                              value={discoveryCallData[field.field_name as keyof typeof discoveryCallData] || ''}
                              onChange={(e) => {
                                const rawValue = parseCurrencyInput(e.target.value);
                                setDiscoveryCallData({ ...discoveryCallData, [field.field_name]: rawValue });
                              }}
                              onBlur={(e) => {
                                const formatted = formatCurrencyInput(e.target.value);
                                setDiscoveryCallData({ ...discoveryCallData, [field.field_name]: formatted });
                              }}
                              placeholder="e.g., $50,000"
                              className={`w-full px-4 py-2.5 border ${isDarkMode ? 'bg-[#394150] border-gray-600 text-gray-200' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm`}
                              required={field.is_required}
                            />
                          ) : (
                            <input
                              type="text"
                              value={discoveryCallData[field.field_name as keyof typeof discoveryCallData] || ''}
                              onChange={(e) => setDiscoveryCallData({ ...discoveryCallData, [field.field_name]: e.target.value })}
                              placeholder={`e.g., ${field.field_label}`}
                              className={`w-full px-4 py-2.5 border ${isDarkMode ? 'bg-[#394150] border-gray-600 text-gray-200' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm`}
                              required={field.is_required}
                            />
                          )}
                        </div>
                        
                        {/* Insert Mortgagees Section right after current_housing field */}
                        {isFieldBeforeMortgagees && (
                          <div className="md:col-span-2">
                            <div className={`mt-6 pt-6 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                              <div className="flex items-center justify-between mb-4">
                                <h4 className={`text-lg font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>Mortgagee Information</h4>
                                <button
                                  onClick={addMortgagee}
                                  className={`${isDarkMode ? 'bg-teal/20 hover:bg-teal/30 text-teal' : 'bg-teal/10 hover:bg-teal/20 text-teal'} px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer text-sm flex items-center gap-2`}
                                >
                                  <i className="ri-add-line"></i>
                                  Add Mortgagee
                                </button>
                              </div>

                              {/* Who Will be on Mortgage field */}
                              <div className="mb-4">
                                <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                                  Who Will be on Mortgage?
                                </label>
                                <input
                                  type="text"
                                  value={whoWillBeOnMortgage}
                                  onChange={(e) => setWhoWillBeOnMortgage(e.target.value)}
                                  placeholder="Enter names of people who will be on the mortgage"
                                  className={`w-full px-4 py-2.5 border ${isDarkMode ? 'bg-[#394150] border-gray-600 text-gray-200' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm`}
                                />
                              </div>

                              {mortgagees.map((mortgagee, mortgageeIndex) => (
                                <div key={mortgageeIndex} className={`mb-6 p-4 ${isDarkMode ? 'bg-[#394150] border-gray-600' : 'bg-gray-50 border-gray-200'} border rounded-lg`}>
                                  <div className="flex items-center justify-between mb-4">
                                    <h5 className={`text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                                      Mortgagee {mortgageeIndex + 1}
                                    </h5>
                                    {mortgagees.length > 1 && (
                                      <button
                                        onClick={() => removeMortgagee(mortgageeIndex)}
                                        className="text-red-600 hover:text-red-700 transition-colors cursor-pointer text-sm flex items-center gap-1"
                                      >
                                        <i className="ri-delete-bin-line"></i>
                                        Remove
                                      </button>
                                    )}
                                  </div>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                    <div className="md:col-span-2">
                                      <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                                        Mortgagee Name
                                      </label>
                                      <input
                                        type="text"
                                        value={mortgagee.name}
                                        onChange={(e) => updateMortgagee(mortgageeIndex, 'name', e.target.value)}
                                        placeholder="Enter name"
                                        className={`w-full px-4 py-2.5 border ${isDarkMode ? 'bg-[#212936] border-gray-600 text-gray-200' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm`}
                                      />
                                    </div>
                                    <div className="md:col-span-2">
                                      <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                                        Credit Score Range
                                      </label>
                                      <select
                                        value={mortgagee.credit_score_range}
                                        onChange={(e) => updateMortgagee(mortgageeIndex, 'credit_score_range', e.target.value)}
                                        className={`w-full px-4 py-2.5 border ${isDarkMode ? 'bg-[#212936] border-gray-600 text-gray-200' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm cursor-pointer`}
                                      >
                                        <option value="">Select...</option>
                                        <option value="Excellent (740+)">Excellent (740+)</option>
                                        <option value="Good (670-739)">Good (670-739)</option>
                                        <option value="Fair (580-669)">Fair (580-669)</option>
                                        <option value="Poor (&lt;580)">Poor (&lt;580)</option>
                                        <option value="Unknown">Unknown</option>
                                      </select>
                                    </div>
                                  </div>

                                  {/* Employment/Income Section */}
                                  <div className={`border-t ${isDarkMode ? 'border-gray-600' : 'border-gray-300'} pt-4`}>
                                    <div className="flex items-center justify-between mb-3">
                                      <h6 className={`text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                        Employment & Income
                                      </h6>
                                      <button
                                        onClick={() => addEmployment(mortgageeIndex)}
                                        className={`${isDarkMode ? 'bg-blue-900/30 hover:bg-blue-900/50 text-blue-400' : 'bg-blue-50 hover:bg-blue-100 text-blue-600'} px-3 py-1.5 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer text-xs flex items-center gap-1`}
                                      >
                                        <i className="ri-add-line"></i>
                                        Add Job
                                      </button>
                                    </div>

                                    {mortgagee.employments.map((employment, employmentIndex) => (
                                      <div key={employmentIndex} className={`mb-3 p-3 ${isDarkMode ? 'bg-[#212936] border-gray-700' : 'bg-white border-gray-200'} border rounded-lg`}>
                                        <div className="flex items-center justify-between mb-2">
                                          <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                            Job {employmentIndex + 1}
                                          </span>
                                          {mortgagee.employments.length > 1 && (
                                            <button
                                              onClick={() => removeEmployment(mortgageeIndex, employmentIndex)}
                                              className="text-red-600 hover:text-red-700 transition-colors cursor-pointer text-xs flex items-center gap-1"
                                            >
                                              <i className="ri-close-line"></i>
                                              Remove
                                            </button>
                                          )}
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                          <div className="md:col-span-2">
                                            <label className={`block text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mb-1`}>
                                              Employer/Job Description
                                            </label>
                                            <input
                                              type="text"
                                              value={employment.employer_job_description}
                                              onChange={(e) => updateEmployment(mortgageeIndex, employmentIndex, 'employer_job_description', e.target.value)}
                                              placeholder="e.g., ABC Company - Software Engineer"
                                              className={`w-full px-3 py-2 border ${isDarkMode ? 'bg-[#394150] border-gray-600 text-gray-200' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-xs`}
                                            />
                                          </div>
                                          <div>
                                            <label className={`block text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mb-1`}>
                                              Employment Status
                                            </label>
                                            <select
                                              value={employment.employment_status}
                                              onChange={(e) => updateEmployment(mortgageeIndex, employmentIndex, 'employment_status', e.target.value)}
                                              className={`w-full px-3 py-2 border ${isDarkMode ? 'bg-[#394150] border-gray-600 text-gray-200' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-xs cursor-pointer`}
                                            >
                                              <option value="">Select...</option>
                                              <option value="Full-time W2">Full-time W2</option>
                                              <option value="Self-employed">Self-employed</option>
                                              <option value="Part-time">Part-time</option>
                                              <option value="Contract">Contract</option>
                                              <option value="Retired">Retired</option>
                                              <option value="Other">Other</option>
                                            </select>
                                          </div>
                                          <div>
                                            <label className={`block text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mb-1`}>
                                              Current Income
                                            </label>
                                            <input
                                              type="text"
                                              value={employment.current_income}
                                              onChange={(e) => {
                                                const rawValue = parseCurrencyInput(e.target.value);
                                                updateEmployment(mortgageeIndex, employmentIndex, 'current_income', rawValue);
                                              }}
                                              onBlur={(e) => {
                                                const formatted = formatCurrencyInput(e.target.value);
                                                updateEmployment(mortgageeIndex, employmentIndex, 'current_income', formatted);
                                              }}
                                              placeholder="e.g., $75,000"
                                              className={`w-full px-3 py-2 border ${isDarkMode ? 'bg-[#394150] border-gray-600 text-gray-200' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-xs`}
                                            />
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })}
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleSaveDiscoveryCall}
                  className={`${isDarkMode ? 'bg-green-700 hover:bg-green-800' : 'bg-green-600 hover:bg-green-700'} text-white px-6 py-2.5 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer text-sm`}
                >
                  <i className="ri-check-line mr-2"></i>
                  {editingDiscoveryCallId ? 'Update Discovery Call' : 'Save Discovery Call'}
                </button>
                <button
                  onClick={handleCancelDiscoveryCall}
                  className={`${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'} px-6 py-2.5 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer text-sm`}
                >
                  Cancel
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Calendar Modal */}
      {showCalendarModal && selectedClientForCalendar && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-5 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-gray-900">
                  Calendar Options - {selectedClientForCalendar.name}
                </h3>
                <button
                  onClick={closeCalendarModal}
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
            </div>

            <div className="p-5">
              <div className="space-y-3">
                <button
                  onClick={() => setCalendarAction('meeting')}
                  className="w-full flex items-center gap-3 p-4 border-2 border-gray-200 rounded-lg hover:border-indigo-500 hover:bg-indigo-50 transition-all cursor-pointer"
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-indigo-100 rounded-lg">
                    <i className="ri-calendar-event-line text-xl text-indigo-600"></i>
                  </div>
                  <div className="text-left">
                    <h4 className="font-semibold text-gray-900 text-sm">Create Meeting</h4>
                    <p className="text-xs text-gray-600">Schedule a meeting with this client</p>
                  </div>
                </button>

                <button
                  onClick={() => setCalendarAction('reminder')}
                  className="w-full flex items-center gap-3 p-4 border-2 border-gray-200 rounded-lg hover:border-teal hover:bg-teal/5 transition-all cursor-pointer"
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-teal/10 rounded-lg">
                    <i className="ri-alarm-line text-xl text-teal"></i>
                  </div>
                  <div className="text-left">
                    <h4 className="font-semibold text-gray-900 text-sm">Set Follow-Up Reminder</h4>
                    <p className="text-xs text-gray-600">Create a reminder for future follow-up</p>
                  </div>
                </button>
              </div>

              {calendarAction === 'meeting' && (
                <div className="space-y-4 mt-4 pt-4 border-t border-gray-200">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Meeting Title (Optional)
                    </label>
                    <input
                      type="text"
                      value={meetingTitle}
                      onChange={(e) => setMeetingTitle(e.target.value)}
                      placeholder={`Meeting with ${selectedClientForCalendar.name}`}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Meeting Date <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        value={meetingDate}
                        onChange={(e) => setMeetingDate(e.target.value)}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Meeting Time <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="time"
                        value={meetingTime}
                        onChange={(e) => setMeetingTime(e.target.value)}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Duration (minutes)
                    </label>
                    <select
                      value={meetingDuration}
                      onChange={(e) => setMeetingDuration(e.target.value)}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm cursor-pointer"
                    >
                      <option value="15">15 minutes</option>
                      <option value="30">30 minutes</option>
                      <option value="45">45 minutes</option>
                      <option value="60">1 hour</option>
                      <option value="90">1.5 hours</option>
                      <option value="120">2 hours</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Meeting Notes (Optional)
                    </label>
                    <textarea
                      value={meetingNotes}
                      onChange={(e) => setMeetingNotes(e.target.value)}
                      placeholder="Add any notes or agenda items..."
                      rows={3}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm resize-none"
                    />
                  </div>

                  <div className="flex flex-col gap-2 pt-2">
                    <button
                      onClick={handleCreateGoogleCalendarMeeting}
                      className="w-full bg-blue-600 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer text-sm flex items-center justify-center gap-2"
                    >
                      <i className="ri-google-line text-lg"></i>
                      Add to Google Calendar
                    </button>
                    <button
                      onClick={handleCreateMeeting}
                      className="w-full bg-indigo-600 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-indigo-700 transition-colors whitespace-nowrap cursor-pointer text-sm flex items-center justify-center gap-2"
                    >
                      <i className="ri-calendar-event-line"></i>
                      Download for Outlook/iCloud
                    </button>
                    <button
                      onClick={() => setCalendarAction(null)}
                      className="w-full bg-gray-200 text-gray-700 px-6 py-2.5 rounded-lg font-medium hover:bg-gray-300 transition-colors whitespace-nowrap cursor-pointer text-sm"
                    >
                      Back
                    </button>
                  </div>
                </div>
              )}

              {calendarAction === 'reminder' && (
                <div className="space-y-4 mt-4 pt-4 border-t border-gray-200">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Reminder Date <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        value={reminderDate}
                        onChange={(e) => setReminderDate(e.target.value)}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Time (Optional)
                      </label>
                      <input
                        type="time"
                        value={reminderTime}
                        onChange={(e) => setReminderTime(e.target.value)}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Reminder Notes
                    </label>
                    <textarea
                      value={reminderNotes}
                      onChange={(e) => setReminderNotes(e.target.value)}
                      placeholder="Add notes for this follow-up..."
                      rows={3}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm resize-none"
                    />
                  </div>

                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={handleCreateReminder}
                      className="flex-1 bg-teal text-white px-6 py-2.5 rounded-lg font-medium hover:bg-teal/90 transition-colors whitespace-nowrap cursor-pointer text-sm"
                    >
                      <i className="ri-alarm-line mr-2"></i>
                      Set Reminder
                    </button>
                    <button
                      onClick={() => setCalendarAction(null)}
                      className="flex-1 bg-gray-200 text-gray-700 px-6 py-2.5 rounded-lg font-medium hover:bg-gray-300 transition-colors whitespace-nowrap cursor-pointer text-sm"
                    >
                      Back
                    </button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}

      {/* Add Client Dialog */}
      {showAddClientDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className={`${isDarkMode ? 'bg-[#212936]' : 'bg-white'} rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto`}
          >
            <div className={`p-6 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center justify-between">
                <h3 className={`text-xl font-bold ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>Add New Client</h3>
                <button
                  onClick={() => {
                    setShowAddClientDialog(false);
                    setLocalClientName('');
                    setLocalFirstName('');
                    setLocalLastName('');
                    setLocalEmail('');
                    setLocalPhone('');
                    setLocalState('');
                    setLocalPurchasePrice('');
                    setLocalLoanAmount('');
                    setLocalLoanType('');
                    setLocalRealtorId('');
                    setLocalLenderName('');
                    setLocalRealtorName('');
                    setShowStateDropdown(false);
                  }}
                  className={`w-8 h-8 flex items-center justify-center ${isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'} transition-colors cursor-pointer`}
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <input
                  type="text"
                  placeholder="Client Name (Required)"
                  value={localClientName}
                  onChange={(e) => setLocalClientName(e.target.value)}
                  autoFocus
                  className={`px-4 py-2.5 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-gray-200 placeholder-gray-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm`}
                />
                <select
                  value={localRealtorId}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === 'other') {
                      setShowRealtorModal(true);
                    } else {
                      setLocalRealtorId(value);
                    }
                  }}
                  className={`px-4 py-2.5 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-gray-200' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm cursor-pointer ${!localRealtorId ? (isDarkMode ? 'text-gray-500' : 'text-gray-400') : ''}`}
                >
                  <option value="">Lead Source (Required)</option>
                  {realtors.map((realtor) => (
                    <option key={realtor.id} value={realtor.id}>{realtor.name}</option>
                  ))}
                  <option value="other">Other</option>
                </select>
                <input
                  type="email"
                  placeholder="Email Address (Required)"
                  value={localEmail}
                  onChange={(e) => setLocalEmail(e.target.value)}
                  className={`px-4 py-2.5 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-gray-200 placeholder-gray-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm`}
                />
                <input
                  type="tel"
                  placeholder="Phone Number (Optional)"
                  value={localPhone}
                  onChange={(e) => setLocalPhone(e.target.value)}
                  className={`px-4 py-2.5 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-gray-200 placeholder-gray-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm`}
                />
                <div className="relative">
                  <input
                    type="text"
                    placeholder="State (Optional)"
                    value={localState}
                    onChange={(e) => {
                      const value = e.target.value.toUpperCase();
                      setLocalState(value);
                      setShowStateDropdown(value.length === 1);
                    }}
                    onFocus={() => {
                      if (localState.length === 1) {
                        setShowStateDropdown(true);
                      }
                    }}
                    onBlur={() => {
                      setTimeout(() => setShowStateDropdown(false), 200);
                    }}
                    maxLength={2}
                    className={`w-full px-4 py-2.5 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-gray-200 placeholder-gray-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm uppercase`}
                  />
                  {showStateDropdown && localState.length === 1 && (
                    <div className={`absolute top-full left-0 right-0 mt-1 ${isDarkMode ? 'bg-[#394150] border-gray-600' : 'bg-white border-gray-300'} border rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto`}>
                      {US_STATES.filter(state => 
                        state.startsWith(localState)
                      ).slice(0, 10).map((state) => (
                        <button
                          key={state}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setLocalState(state);
                            setShowStateDropdown(false);
                          }}
                          className={`w-full text-left px-4 py-2 ${isDarkMode ? 'hover:bg-gray-700 text-gray-200' : 'hover:bg-gray-50'} text-sm cursor-pointer`}
                        >
                          {state}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <input
                  type="text"
                  placeholder="Purchase Price (Optional)"
                  value={localPurchasePrice}
                  onChange={(e) => setLocalPurchasePrice(e.target.value)}
                  className={`px-4 py-2.5 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-gray-200 placeholder-gray-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm`}
                />
                <input
                  type="text"
                  placeholder="Loan Amount (Optional)"
                  value={localLoanAmount}
                  onChange={(e) => setLocalLoanAmount(e.target.value)}
                  className={`px-4 py-2.5 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-gray-200 placeholder-gray-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm`}
                />
                <select
                  value={localLoanType}
                  onChange={(e) => setLocalLoanType(e.target.value)}
                  className={`px-4 py-2.5 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-gray-200' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm cursor-pointer ${!localLoanType ? (isDarkMode ? 'text-gray-500' : 'text-gray-400') : ''}`}
                >
                  <option value="">Loan Type (Optional)</option>
                  {loanTypes.map((type) => (
                    <option key={type.id} value={type.loan_type}>{type.loan_type}</option>
                  ))}
                </select>
                <select
                  value={localLenderName}
                  onChange={(e) => setLocalLenderName(e.target.value)}
                  className={`px-4 py-2.5 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-gray-200' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm cursor-pointer ${!localLenderName ? (isDarkMode ? 'text-gray-500' : 'text-gray-400') : ''}`}
                >
                  <option value="">Lender Name (Optional)</option>
                  {lenderOptions.map((lender) => (
                    <option key={lender} value={lender}>{lender}</option>
                  ))}
                </select>
                <input
                  type="text"
                  placeholder="Realtor Name (Optional)"
                  value={localRealtorName}
                  onChange={(e) => setLocalRealtorName(e.target.value)}
                  className={`px-4 py-2.5 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-gray-200 placeholder-gray-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm`}
                />
              </div>
              <div className={`mb-4 p-3 ${isDarkMode ? 'bg-blue-900/30 border-blue-700' : 'bg-blue-50 border-blue-200'} border rounded-lg`}>
                <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <strong className={isDarkMode ? 'text-gray-200' : 'text-gray-900'}>Note:</strong> Fields marked with (Required) are mandatory. Phone number is optional. If state is not provided, broker will default to NS.
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleAddClient}
                  className={`flex-1 ${isDarkMode ? 'bg-teal/90 hover:bg-teal' : 'bg-teal hover:bg-teal/90'} text-white px-6 py-2.5 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer text-sm`}
                >
                  <i className="ri-check-line mr-2"></i>
                  Save Client
                </button>
                <button
                  onClick={handleAddClientWithDiscovery}
                  className={`flex-1 ${isDarkMode ? 'bg-green-700 hover:bg-green-800' : 'bg-green-600 hover:bg-green-700'} text-white px-6 py-2.5 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer text-sm`}
                >
                  <i className="ri-phone-line mr-2"></i>
                  Save & Add Discovery Call
                </button>
                <button
                  onClick={() => {
                    setShowAddClientDialog(false);
                    setLocalClientName('');
                    setLocalFirstName('');
                    setLocalLastName('');
                    setLocalEmail('');
                    setLocalPhone('');
                    setLocalState('');
                    setLocalPurchasePrice('');
                    setLocalLoanAmount('');
                    setLocalLoanType('');
                    setLocalRealtorId('');
                    setLocalLenderName('');
                    setLocalRealtorName('');
                    setShowStateDropdown(false);
                  }}
                  className={`${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'} px-6 py-2.5 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer text-sm`}
                >
                  Cancel
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}